import { paperKey, paperRequest, paperDownload, isDefinitivePaperRejection, paperSummaryText, csvCell, pollingDeadline, paperQuestionMarkText, paperQuestionStatus } from './utils/paperClient';
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import {
  ArrowRight,
  ChartColumn,
  CircleCheck,
  CircleAlert,
  CircleHelp,
  Clock3,
  CloudUpload,
  ClipboardCheck,
  ClipboardList,
  Eye,
  FileText,
  Gauge,
  Info,
  Layers,
  PenLine,
  ScanText,
  Search,
  Settings2,
  Target,
  Upload,
  Users,
  FileCheck,
  X,
  Zap
} from "lucide-react";
import { collection, query, where, orderBy, getDocs, doc, getDoc, onSnapshot } from "firebase/firestore";
import { auth, db } from "./firebaseConfig";
import { getStorage, ref, uploadBytes } from "firebase/storage";
import { getAuth } from "firebase/auth";
import { AlignmentType, BorderStyle, Document, Footer, Packer, Paragraph, Table, TableCell, TableRow, TextRun, HeadingLevel, ShadingType, VerticalAlign, WidthType } from "docx";
import { saveAs } from "file-saver";
import "./PaperSubmission.css";
import {createPaperRequestScope} from "./utils/paperRequestScope";
import { accountDisplayName, DashboardHeader, DashboardSidebar, PanelHeading } from "./Dashboard";
import { createPaperUploadSession } from "./utils/paperUploadSession"; // QR Upload helper
import { QRCodeSVG } from "qrcode.react";
import { API_BASE_URL } from "./utils/apiBase";


const MARKING_MODES = {
  quick: {
    label: "Quick Mark",
    copy: "Fastest and lowest credit cost. Uses the assessment structure selected earlier, but gives shorter feedback and simpler outputs.",
    expectedOutput: "Simple marks, status, short reasons, totals, and CSV."
  },
  standard: {
    label: "Standard Marking",
    copy: "More careful marking with clearer question-by-question feedback. Best for tests and exam papers.",
    expectedOutput: "Question-by-question marks, feedback, question summary, and PDF reports."
  },
  fullAnalysis: {
    label: "Full Analysis",
    copy: "Most detailed option. Includes deeper feedback, evidence, analysis, and fuller reports.",
    expectedOutput: "Detailed question-by-question marking, fuller feedback, analysis, and PDF reports."
  }
};

const WIZARD_STEPS = [
  { number: 1, shortLabel: "Assessment", label: "Set Up Assessment", icon: ClipboardCheck },
  { number: 2, shortLabel: "Students", label: "Review Students", icon: Users },
  { number: 3, shortLabel: "Upload", label: "Upload Papers", icon: Upload },
  { number: 4, shortLabel: "Depth", label: "Choose Marking Depth", icon: Target },
  { number: 5, shortLabel: "Review", label: "Review and Mark", icon: FileCheck }
];

const MARKING_MODE_ICONS = {
  quick: Zap,
  standard: ClipboardCheck,
  fullAnalysis: ChartColumn
};

const normalizeUserRole = (role) => String(role || "").trim().toLowerCase();

// Helper: Get paper batch limits based on role, teacherStatus, and fullAnalysis toggle
const getPaperSubmissionLimits = (role, teacherStatus, fullAnalysisEnabled) => {
  const normalizedRole = normalizeUserRole(role);
  if (normalizedRole === "admin") {
    return {
      maxStudents: 25,
      maxPages: 250,
      label: "You can select up to 25 students per batch. Maximum 250 total pages per batch."
    };
  }
  if (normalizedRole === "teacher" && teacherStatus === "active") {
    return {
      maxStudents: fullAnalysisEnabled ? 10 : 15,
      maxPages: 100,
      label: `Active teacher accounts can select up to ${fullAnalysisEnabled ? 10 : 15} students${fullAnalysisEnabled ? " with full analysis" : " without full analysis"}. Maximum 100 total pages per batch.`
    };
  }
  if (normalizedRole === "teacher" && teacherStatus === "trial") {
    return {
      maxStudents: fullAnalysisEnabled ? 3 : 5,
      maxPages: 30,
      label: `Trial teacher accounts can select up to ${fullAnalysisEnabled ? 3 : 5} students${fullAnalysisEnabled ? " with full analysis" : " without full analysis"}. Maximum 30 total pages per batch.`
    };
  }
  // Fallback: most restrictive
  return {
    maxStudents: fullAnalysisEnabled ? 3 : 5,
    maxPages: 30,
    label: `Your account can select up to ${fullAnalysisEnabled ? 3 : 5} students${fullAnalysisEnabled ? " with full analysis" : " without full analysis"}. Maximum 30 total pages per batch.`
  };
};

// Helper: Natural sort for filenames with numeric ordering
// This function extracts numbers from filenames and sorts them numerically
// Example: page-2.png comes before page-10.png (not alphabetically)
const naturalSort = (files) => {
  const extractNumbers = (filename) => {
    // Extract all numbers from the filename
    const numbers = [];
    let lastIndex = 0;
    const regex = /\d+/g;
    let match;
    while ((match = regex.exec(filename)) !== null) {
      numbers.push({
        value: parseInt(match[0], 10),
        index: match.index,
      });
      lastIndex = match.index + match[0].length;
    }
    return numbers;
  };

  return [...files].sort((a, b) => {
    const aFileName = a.name.toLowerCase();
    const bFileName = b.name.toLowerCase();

    // Extract numeric sequences from both filenames
    const aNumbers = extractNumbers(aFileName);
    const bNumbers = extractNumbers(bFileName);

    // If both have numbers, compare them sequentially
    if (aNumbers.length > 0 && bNumbers.length > 0) {
      for (let i = 0; i < Math.min(aNumbers.length, bNumbers.length); i++) {
        if (aNumbers[i].value !== bNumbers[i].value) {
          return aNumbers[i].value - bNumbers[i].value;
        }
      }
      // If all compared numbers are equal, the one with fewer numbers comes first
      if (aNumbers.length !== bNumbers.length) {
        return aNumbers.length - bNumbers.length;
      }
    }

    // If no numbers or numbers are equal, fall back to alphabetical comparison
    return aFileName.localeCompare(bFileName);
  });
};

// Helper: Client-side image compression using canvas
const compressImage = async (file, maxDimension = 2000, quality = 0.85) => {
  try {
    // Skip compression if file is already small and likely properly sized
    if (file.size < 800000) { // under 800KB
      return file;
    }

    return new Promise((resolve) => {
      const img = new Image();
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      img.onload = () => {
        URL.revokeObjectURL(img.src);
        // Calculate new dimensions
        let { width, height } = img;
        if (width <= maxDimension && height <= maxDimension) {
          resolve(file); // Already small enough
          return;
        }

        const ratio = Math.min(maxDimension / width, maxDimension / height);
        width *= ratio;
        height *= ratio;

        // Set canvas size and draw scaled image
        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);

        // Convert to blob
        canvas.toBlob((blob) => {
          // If compression failed or made it larger, use original
          if (!blob || blob.size >= file.size) {
            resolve(file);
          } else {
            // Create new File object with same name and type
            const compressedFile = new File([blob], file.name, {
              type: file.type,
              lastModified: Date.now()
            });
            resolve(compressedFile);
          }
        }, file.type, quality);
      };

      img.onerror = () => { URL.revokeObjectURL(img.src); resolve(file); }; // Fallback to original on error
      img.src = URL.createObjectURL(file);
    });
  } catch (error) {
    void 0;
    return file; // Fallback to original
  }
};

// Helper: Run jobs with concurrency limit
const runWithConcurrency = async (jobs, concurrency, worker) => {
  const results = new Array(jobs.length); let next = 0;
  await Promise.all(Array.from({ length: Math.min(concurrency, jobs.length) }, async () => {
    while (next < jobs.length) { const index = next++; results[index] = await worker(jobs[index]); }
  }));
  return results;
};

const PaperSubmission = ({ user }) => {
  const navigate = useNavigate();

  // ===== APP SHELL (sidebar/top bar) =====
  // Mirrors My Tests (ViewQuizzes) and is independent of the paper workflow state.
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem("darkMode") === "true");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [copiedTeacherCode, setCopiedTeacherCode] = useState(false);

  useEffect(() => {
    document.body.classList.toggle("dark-mode", darkMode);
    document.documentElement.setAttribute("data-dark-mode", darkMode ? "true" : "false");
    localStorage.setItem("darkMode", JSON.stringify(darkMode));
  }, [darkMode]);

  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === "darkMode") setDarkMode(event.newValue === "true");
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  const handleShellLogout = async () => {
    try {
      await auth.signOut();
      navigate("/");
    } catch (error) {
      console.error("Error signing out", error);
    }
  };

  const handleCopyTeacherCode = async () => {
    if (!user?.teacherCode) return;
    try {
      await navigator.clipboard.writeText(user.teacherCode);
      setCopiedTeacherCode(true);
      window.setTimeout(() => setCopiedTeacherCode(false), 1800);
    } catch (error) {
      console.error("Could not copy teacher code", error);
    }
  };

  const shellRole = user?.role === "admin" ? "admin" : "teacher";
  const shellUsername = accountDisplayName(user, "StudyDen teacher");

  /**
   * Pre-flight access check: verify teacher can use AI marking
   * before starting any paper batch submission workflow.
   * 
   * Checks:
   * - teacherStatus === "expired" 
   * - teacherStatus === "trial" && trialEndsAt has passed
   * - creditsRemaining <= 0
   * 
   * Returns { allowed: boolean, message?: string, code?: string }
   */
  const checkTeacherAiAccessFrontend = useCallback(async () => {
    const paperScope = capturePaperRequest('checkTeacherAiAccessFrontend', {ownerOnly:true});
    if (!user || !user.uid) {
      return {
        allowed: false,
        message: "You must be logged in to submit paper batches.",
        code: "NOT_LOGGED_IN"
      };
    }

    // Get fresh user data from Firestore to check current status
    try {
      const auth = getAuth();
      const currentUser = auth.currentUser;
      if (!currentUser) {
        return {
          allowed: false,
          message: "You must be logged in to submit paper batches.",
          code: "NOT_LOGGED_IN"
        };
      }

      const userDocRef = doc(db, "users", currentUser.uid);
      const userDocSnap = await getDoc(userDocRef);
      if (!paperActive(paperScope)) return;

      if (!userDocSnap.exists()) {
        return {
          allowed: false,
          message: "User profile not found.",
          code: "USER_NOT_FOUND"
        };
      }

      const userData = userDocSnap.data();
      const userRole = normalizeUserRole(userData.role);
      if (userRole === "admin") {
        return { allowed: true };
      }

      const teacherStatus = userData.teacherStatus || "unknown";
      const trialEndsAt = userData.trialEndsAt;
      const creditsRemaining = userData.creditsRemaining || 0;

      // Check 1: If already marked expired
      if (teacherStatus === "expired") {
        return {
          allowed: false,
          message: "Your 4-week free trial has ended. Please contact contact@aiautomarker.com to continue using StudyDen.",
          code: "TRIAL_EXPIRED"
        };
      }

      // Check 2: If on trial, check if trial has ended
      if (teacherStatus === "trial" && trialEndsAt) {
        let trialEndDate;
        try {
          if (typeof trialEndsAt.toDate === "function") {
            // Firestore Timestamp
            trialEndDate = trialEndsAt.toDate();
          } else if (trialEndsAt instanceof Date) {
            trialEndDate = trialEndsAt;
          } else if (typeof trialEndsAt === "string") {
            // ISO string
            trialEndDate = new Date(trialEndsAt);
          } else {
            // Could not parse - block access
            return {
              allowed: false,
              message: "Unable to verify trial status. Please contact Ryan.",
              code: "ACCOUNT_INACTIVE"
            };
          }
        } catch (parseErr) {
      if (!paperActive(paperScope)) return;
          void 0;
          return {
            allowed: false,
            message: "Unable to verify trial status. Please contact Ryan.",
            code: "ACCOUNT_INACTIVE"
          };
        }

        // Check if trial has expired
        const now = new Date();
        if (now > trialEndDate) {
          return {
            allowed: false,
            message: "Your 4-week free trial has ended. Please contact contact@aiautomarker.com to continue using StudyDen.",
            code: "TRIAL_EXPIRED"
          };
        }
      }

      // Check 3: If teacher status is neither trial nor active, block
      if (teacherStatus !== "trial" && teacherStatus !== "active") {
        return {
          allowed: false,
          message: "Your teacher account is not currently active.",
          code: "ACCOUNT_INACTIVE"
        };
      }

      // Check 4: If no credits remaining
      if (creditsRemaining <= 0) {
        return {
          allowed: false,
          message: "You have no credits remaining. Please contact Ryan to add more credits.",
          code: "NO_CREDITS"
        };
      }

      // All checks passed
      return { allowed: true };
    } catch (error) {
      if (!paperActive(paperScope)) return;
      void 0;
      return {
        allowed: false,
        message: "Error verifying account status. Please try again.",
        code: "CHECK_FAILED"
      };
    }
  }, [user]);

  /**
   * Determine if batch submission should be disabled based on user status.
   * This is a synchronous check using already-loaded user data if available.
   */
  const isPaperSubmissionDisabled = useMemo(() => {
    if (user && normalizeUserRole(user.role) === "admin") {
      return false;
    }

    // If user data is available from props or state, check it
    if (user && user.teacherStatus === "expired") {
      return true;
    }
    if (user && user.teacherStatus === "trial" && user.trialEndsAt) {
      try {
        let trialEndDate;
        if (typeof user.trialEndsAt.toDate === "function") {
          trialEndDate = user.trialEndsAt.toDate();
        } else if (user.trialEndsAt instanceof Date) {
          trialEndDate = user.trialEndsAt;
        } else if (typeof user.trialEndsAt === "string") {
          trialEndDate = new Date(user.trialEndsAt);
        } else {
          return false;
        }
        const now = new Date();
        if (now > trialEndDate) {
          return true;
        }
      } catch (err) {
        void 0;
      }
    }
    if (user && (user.creditsRemaining === 0 || user.creditsRemaining < 0)) {
      return true;
    }
    return false;
  }, [user]);

  // ===== QUIZ SELECTION =====
  const [setupMode, setSetupMode] = useState("existingQuiz");
  const [quizzes, setQuizzes] = useState([]);
  const [selectedQuiz, setSelectedQuiz] = useState(null);
  const [loadingQuizzes, setLoadingQuizzes] = useState(false);

  // ===== STUDENT SELECTION =====
  const [students, setStudents] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [manualStudentName, setManualStudentName] = useState("");
  const [loadingStudents, setLoadingStudents] = useState(false);

  // ===== BATCH STATE =====
  const [currentStep, setCurrentStep] = useState(1); // 1-5 steps
  const [studentImages, setStudentImages] = useState({}); // { studentId: { name, images: [], uploadingCount } }
  const [currentUploadingStudent, setCurrentUploadingStudent] = useState(null);
  const [markingMode, setMarkingMode] = useState("standard");
  const [markingModeNotice, setMarkingModeNotice] = useState("");
  const fullAnalysisEnabled = markingMode === "fullAnalysis";
  const setFullAnalysisEnabled = (enabled) => setMarkingMode(enabled ? "fullAnalysis" : "standard");
  const [userRole, setUserRole] = useState(null);
  const [teacherStatus, setTeacherStatus] = useState(null);
  const [studentLimitError, setStudentLimitError] = useState(null);
  
  // ===== PROCESSING STATE =====
  const [isProcessing, setIsProcessing] = useState(false);
  // Covers the whole start request (access check, uploads, acceptance) so one click sends one request.
  const [isStartingBatch, setIsStartingBatch] = useState(false);
  const startBatchInFlightRef = useRef(false);
  // Optional "go to step" action for a blocked submission; shown only while its message is current.
  const [batchErrorAction, setBatchErrorAction] = useState(null);
  const [batchId, setBatchId] = useState(null);
  const [processingProgress, setProcessingProgress] = useState({
    completed: 0,
    failed: 0,
    processing: 0,
    pending: 0,
  });
  const [submissionStatuses, setSubmissionStatuses] = useState({}); // { studentId: { status, error } }
  const [statusMessage, setStatusMessage] = useState("");

   // ===== RESULTS STATE =====
   const [batchResults, setBatchResults] = useState(null);
   const [fullBatchResults, setFullBatchResults] = useState(null);
   const [expandedStudent, setExpandedStudent] = useState(null);
   const [selectedBreakdownStudent, setSelectedBreakdownStudent] = useState(null);

  // ===== ERROR STATE =====
  const [batchError, setBatchError] = useState(null);

  // ===== QR UPLOAD STATE =====
  const [qrModalSessionId, setQrModalSessionId] = useState(null);
  const [qrModalStudentName, setQrModalStudentName] = useState("");
  const [qrModalLoading, setQrModalLoading] = useState(false);

  // ===== QR SYNC STATE (Phase 4) =====
  // Tracks which QR session is currently being listened to and which student it belongs to.
  const [activeQrSession, setActiveQrSession] = useState(null);
  const [qrCapability, setQrCapability] = useState('');
  const pdfDeadlineRef = useRef(null);
  const restoredOwnerRef = useRef(null); // { sessionId, studentId }
  // Holds the Firestore onSnapshot unsubscribe function so it can be cleaned up.
  const qrListenerUnsubRef = useRef(null);

  // ── Batch polling refs ──
  // pollTimeoutRef  : holds the active recursive-setTimeout handle for batch polling.
  //                   Stored in a ref so it survives re-renders and can be cleared on unmount.
  // pollStartTimeRef: records when polling started, used to implement slowdown/timeout logic.
  // isMountedRef    : lets async poll callbacks bail out safely after the component unmounts.
  const pollTimeoutRef = useRef(null);
  const pollStartTimeRef = useRef(null);
  const pollEpochRef = useRef(0);
  const isMountedRef = useRef(true);
  const ownerRef = useRef(user?.uid);
  ownerRef.current = user?.uid;
  const requestScopeRef = useRef(null);
  if (!requestScopeRef.current) requestScopeRef.current = createPaperRequestScope(() => {
    let pendingRequestId = null;
    try { pendingRequestId = JSON.parse(localStorage.getItem(paperKey(ownerRef.current, 'pending')) || 'null')?.requestId; } catch { /* malformed restoration is reported by its caller */ }
    return {owner:ownerRef.current,authOwner:getAuth().currentUser?.uid,mounted:isMountedRef.current,pendingRequestId};
  });
  requestScopeRef.current.updateOwner(user?.uid);
  const capturePaperRequest = (kind, identity) => requestScopeRef.current.capture(kind, identity);
  const paperActive = scope => requestScopeRef.current.active(scope);
  const invalidatePaperWork = () => { requestScopeRef.current.invalidate(); pollEpochRef.current++; };
  const selectPaperBatch = id => { requestScopeRef.current.selectBatch(id); setBatchId(id); };

  // ── PDF conversion state ──
  // Tracks which student IDs currently have a PDF being converted server-side.
  const [pdfConverting, setPdfConverting] = useState({}); // { [studentId]: true }
  const [useDetectedNamesFromPdf, setUseDetectedNamesFromPdf] = useState(false);
  const [batchQuickMarkMode, setBatchQuickMarkMode] = useState("single");
  const [batchSplitMethod, setBatchSplitMethod] = useState("coverNamePages");
  const [showAdvancedBatchSplitOptions, setShowAdvancedBatchSplitOptions] = useState(false);
  const [batchPagesPerStudent, setBatchPagesPerStudent] = useState("");
  const [batchPdfName, setBatchPdfName] = useState("");
  const [batchPdfPages, setBatchPdfPages] = useState([]);
  const [batchScriptGroups, setBatchScriptGroups] = useState([]);
  const [batchDetecting, setBatchDetecting] = useState(false);
  const [batchDetectionError, setBatchDetectionError] = useState("");
  const [questionExtracting, setQuestionExtracting] = useState(false);
  const [questionExtractionError, setQuestionExtractionError] = useState("");
  const [extractedQuiz, setExtractedQuiz] = useState(null);
  const [extractedQuestions, setExtractedQuestions] = useState([]);
  const [extractionDiagnostics, setExtractionDiagnostics] = useState(null);
  const [questionExtractionSource, setQuestionExtractionSource] = useState(null);
  const [holisticTaskSuggestion, setHolisticTaskSuggestion] = useState(null);
  const [extractedQuestionsConfirmed, setExtractedQuestionsConfirmed] = useState(false);

  // Clean up any active QR listener AND pending batch poll when the component unmounts.
  // isMountedRef.current is set to false so any in-flight async poll callbacks stop
  // updating state after unmount.
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      invalidatePaperWork();
      if (qrListenerUnsubRef.current) {
        qrListenerUnsubRef.current();
        qrListenerUnsubRef.current = null;
      }
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
        pollTimeoutRef.current = null;
      }
    };
  }, []);

  // Apply gradient background to body on mount
  useEffect(() => {
    document.body.classList.add("paper-submission-open");
    return () => {
      document.body.classList.remove("paper-submission-open");
    };
  }, []);

  // ===== PDF POLLING AFTER BATCH COMPLETION =====
  // Polls /api/batch-results every 2 seconds once the batch is done, to pick up
  // the PDF URL once background PDF generation finishes.
  //
  // Dependency notes:
  //   fullBatchResults is intentionally kept in the dep array so the effect
  //   re-runs (and restarts the interval) when the PDF poll updates it.
  //   A fresh `pdfPollStartTime` is captured each invocation; the 60-second
  //   timeout therefore resets on each restart, which is acceptable for a
  //   background task.  The cleanup function correctly clears the interval
  //   of that specific invocation via closure.
  useEffect(() => {
    // Only poll when the batch is finished and we have per-submission data
    if (!batchId || !fullBatchResults || isProcessing) return;

    // Only poll if at least one PDF is still generating
    const hasPendingPdfs = fullBatchResults.some(
      sub => ["completed","needs_review"].includes(sub.status) && ["generating","pending"].includes(sub.pdfStatus)
    );
    if (!hasPendingPdfs) return;

    void 0;
    const PDF_POLL_TIMEOUT_MS = 60000; // 60 seconds per invocation
    pdfDeadlineRef.current = pollingDeadline(pdfDeadlineRef.current, batchId, Date.now());
    const pdfPollStartTime = pdfDeadlineRef.current.expiresAt - PDF_POLL_TIMEOUT_MS;
    const ownerUid = getAuth().currentUser?.uid;
    let cancelled = false;
    let requestInFlight = false;
    let pdfPollInterval = null;
    const pdfScope = capturePaperRequest('pdf-results', {batchId});

    pdfPollInterval = setInterval(async () => {
      if (requestInFlight) return;
      requestInFlight = true;
      try {
        if (Date.now() - pdfPollStartTime > PDF_POLL_TIMEOUT_MS) {
          void 0;
          clearInterval(pdfPollInterval);
          pdfPollInterval = null;
          return;
        }

        const auth = getAuth();
        const currentUser = auth.currentUser;
        if (!currentUser || currentUser.uid !== ownerUid || cancelled) {
          clearInterval(pdfPollInterval);
          pdfPollInterval = null;
          return;
        }

        const idToken = await currentUser.getIdToken();
        if (!paperActive(pdfScope) || cancelled) return;
        const response = await fetch(`${API_BASE_URL}/api/batch-results/${batchId}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${idToken}`,
          },
          cache: "no-store", // Always fetch fresh — never serve a cached response
        });

        if (!paperActive(pdfScope) || cancelled) return;
        if (!response.ok) {
          void 0;
          clearInterval(pdfPollInterval);
          pdfPollInterval = null;
          return;
        }

        const data = await response.json();
        if (cancelled || !paperActive(pdfScope)) return;
        // Update the results grid with new pdfStatus / pdfReportRef values
        setFullBatchResults(data.submissions || []);

        const stillGenerating = (data.submissions || []).some(
          sub => ["completed","needs_review"].includes(sub.status) && ["generating","pending"].includes(sub.pdfStatus)
        );
        if (!stillGenerating) {
          void 0;
          clearInterval(pdfPollInterval);
          pdfPollInterval = null;
        }
      } catch (error) {
        void 0;
        clearInterval(pdfPollInterval);
        pdfPollInterval = null;
      } finally { requestInFlight = false; }
    }, 2000);

    // Cleanup: runs when deps change (clears old interval before a new one starts)
    // or when the component unmounts.
    return () => {
      cancelled = true;
      if (pdfPollInterval) {
        void 0;
        clearInterval(pdfPollInterval);
        pdfPollInterval = null;
      }
    };
  }, [batchId, fullBatchResults, isProcessing]); // Re-run when these change


  useEffect(() => {
    let cancelled = false;
    const uid = user?.uid;
    if (restoredOwnerRef.current !== uid) {
      pollEpochRef.current++;
      if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
      pdfDeadlineRef.current = null;
      restoredOwnerRef.current = uid;
      selectPaperBatch(null); setBatchResults(null); setFullBatchResults(null); setSelectedBreakdownStudent(null); setIsProcessing(false);
      setBatchError(null);setStatusMessage('');setSubmissionStatuses({});setProcessingProgress({completed:0,failed:0,processing:0,pending:0});
      setCurrentStep(1);setSelectedQuiz(null);setSelectedStudents([]);setStudentImages({});setQuizzes([]);setStudents([]);
      setQrCapability('');setQrModalSessionId(null);setQrModalLoading(false);setActiveQrSession(null);
      if(qrListenerUnsubRef.current){qrListenerUnsubRef.current();qrListenerUnsubRef.current=null;}
      setUserRole(null);setTeacherStatus(null);setBatchPdfPages([]);setBatchScriptGroups([]);resetExtractedQuizReview();
      setPdfConverting({});setBatchDetecting(false);setBatchDetectionError('');setQuestionExtracting(false);
    }
    if (!uid) return;
    const restore = async () => {
    const paperScope = capturePaperRequest('restore', {});
      try {
        let saved = localStorage.getItem(paperKey(uid, 'batch'));
        const pending = localStorage.getItem(paperKey(uid, 'pending'));
        if (pending) {
          paperScope.requestId=JSON.parse(pending).requestId;
          const accepted = await paperRequest('/api/submit-paper-batch', JSON.parse(pending));
      if (!paperActive(paperScope)) return;
          saved = accepted.batchId;
          localStorage.setItem(paperKey(uid, 'batch'), saved);
          localStorage.removeItem(paperKey(uid, 'pending'));
          paperScope.requestId=null;
        }
        if (!saved) return;
        const status = await paperRequest('/api/batch-status/' + saved);
      if (!paperActive(paperScope)) return;
        if (cancelled || getAuth().currentUser?.uid !== uid) return;
        selectPaperBatch(saved); setCurrentStep(5);
        if (status.status === 'processing') { setIsProcessing(true); pollBatchStatus(saved); }
        else { setBatchResults(status); await fetchFullBatchResults(saved);
      if (!paperActive(paperScope)) return; }
      } catch (error) {
      if (!paperActive(paperScope)) return;
        // The server will reject this payload again; keeping it would replay it on every start.
        if (isDefinitivePaperRejection(error)) localStorage.removeItem(paperKey(uid, 'pending'));
        if (!cancelled) setBatchError(error.message); }
    };
    restore();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid]);

  const retrySavedPaper = async (submissionId, reportOnly = false) => {
    invalidatePaperWork();
    const paperScope = capturePaperRequest('retrySavedPaper', {});
    try {
      await paperRequest('/api/paper-batches/' + batchId + '/retry', { submissionId, reportOnly });
      if (!paperActive(paperScope)) return;
      if (reportOnly) pdfDeadlineRef.current = null;
      setIsProcessing(true); pollBatchStatus(batchId);
    } catch (error) {
      if (!paperActive(paperScope)) return; setBatchError(error.message); }
  };

  // Fetch quizzes on mount
  useEffect(() => {
    const fetchQuizzes = async () => {
    const paperScope = capturePaperRequest('fetchQuizzes', {ownerOnly:true});
      if (!user) return;
      try {
        setLoadingQuizzes(true);
        const quizzesRef = collection(db, "quizzes");
        const q = query(
          quizzesRef,
          where("creatorUid", "==", user.uid),
          orderBy("createdAt", "desc")
        );
        const querySnapshot = await getDocs(q);
      if (!paperActive(paperScope)) return;
        const quizzesList = [];
        querySnapshot.forEach((docSnap) => {
          const data = docSnap.data();
          quizzesList.push({
            value: docSnap.id,
            label: data.quizTitle || data.quizCode || docSnap.id,
            subject: data.subject,
            createdAt: data.createdAt,
          });
        });
        setQuizzes(quizzesList);
      } catch (error) {
      if (!paperActive(paperScope)) return;
        void 0;
      } finally {
      if (!paperActive(paperScope)) return;
        setLoadingQuizzes(false);
      }
    };
    fetchQuizzes();
  }, [user]);

   // Fetch students on mount - load students linked to the current teacher via:
   //   Source A: classMemberships where teacherUid == user.uid and active == true
   //   Source B: users where teacherCodes array-contains the teacher's own teacherCode
   useEffect(() => {
     const fetchStudents = async () => {
    const paperScope = capturePaperRequest('fetchStudents', {ownerOnly:true});
       if (!user) return;
       try {
         setLoadingStudents(true);
         void 0;

         // Use a Map to deduplicate students — key is studentUid/uid first, email second
         const uniqueStudentsMap = new Map();

         // ── SOURCE A: classMemberships ──────────────────────────────────────────────
         const membershipsRef = collection(db, "classMemberships");
         const membershipsQuery = query(
           membershipsRef,
           where("teacherUid", "==", user.uid),
           where("active", "==", true)
         );
         const membershipsSnapshot = await getDocs(membershipsQuery);
      if (!paperActive(paperScope)) return;
         let classMembershipCount = 0;

         for (const membershipDoc of membershipsSnapshot.docs) {
           const membership = membershipDoc.data();
           const studentUid = membership.studentUid;
           const studentEmail = membership.studentEmail;
           const studentName = membership.studentName;
           const className = membership.className;

           // Use studentUid as the primary key, or studentEmail as fallback
           const key = studentUid || studentEmail;
           if (!key) continue;

           if (!uniqueStudentsMap.has(key)) {
             uniqueStudentsMap.set(key, {
               value: studentUid || studentEmail,
               label: studentName ? `${studentName}` : (studentEmail || "Unknown"),
               email: studentEmail,
               className: className,
             });
             classMembershipCount++;
           }
         }
         void 0;

         // ── SOURCE B: users.teacherCodes ────────────────────────────────────────────
         // Step 1: Load the teacher's own user document to get their teacherCode
         let teacherCode = null;
         try {
           const teacherDocRef = doc(db, "users", user.uid);
           const teacherDocSnap = await getDoc(teacherDocRef);
      if (!paperActive(paperScope)) return;
          if (teacherDocSnap.exists()) {
              const tData = teacherDocSnap.data();
              teacherCode = tData.teacherCode || null;
              setUserRole(tData.role || null);
              setTeacherStatus(tData.teacherStatus || null);
            }
         } catch (err) {
      if (!paperActive(paperScope)) return;
           void 0;
         }
         void 0;

         // Step 2: Query users whose teacherCodes array contains this teacher's code
         let teacherCodeCount = 0;
         if (teacherCode) {
           const usersRef = collection(db, "users");
           const teacherCodesQuery = query(
             usersRef,
             where("teacherCodes", "array-contains", teacherCode)
           );
           const teacherCodesSnapshot = await getDocs(teacherCodesQuery);
      if (!paperActive(paperScope)) return;

           for (const userDoc of teacherCodesSnapshot.docs) {
             const data = userDoc.data();
             const uid = userDoc.id;
             const email = data.email || "";
             const name = data.displayName || data.username || email || "Unknown";

             // Use uid as the deduplication key; email as fallback if uid missing
             const key = uid || email;
             if (!key) continue;

             if (!uniqueStudentsMap.has(key)) {
               uniqueStudentsMap.set(key, {
                 value: uid || email,
                 label: name,
                 email: email,
                 className: null,
               });
               teacherCodeCount++;
             }
           }
         }
         void 0;

         // Source C placeholder log (direct teacherUid/teacherUids not used in this project)
         void 0;

         const studentsList = Array.from(uniqueStudentsMap.values());
         studentsList.sort((a, b) => a.label.localeCompare(b.label));
         void 0;

         if (studentsList.length === 0) {
           void 0;
         }

         setStudents(studentsList);
       } catch (error) {
      if (!paperActive(paperScope)) return;
         void 0;
       } finally {
      if (!paperActive(paperScope)) return;
         setLoadingStudents(false);
       }
     };
     fetchStudents();
   }, [user]);

  // ===== STEP 1: QUIZ SELECTION =====
  const isSingleTaskMode = setupMode === "singleTask";
  const isScanExtractedQuizMode = setupMode === "scanExtractedQuiz" || isSingleTaskMode;
  const routeAccentClass =
    setupMode === "singleTask"
      ? "assessment-route--single"
      : setupMode === "scanExtractedQuiz"
        ? "assessment-route--scanned"
        : "assessment-route--existing";

  const getAssessmentSourceLabel = () => {
    if (setupMode === "existingQuiz") return "Use my existing test";
    if (setupMode === "singleTask") return "Mark one writing task";
    return "Build a test from this paper";
  };

  const hasStructuredExtractedQuestionSet = isScanExtractedQuizMode && extractedQuestions.length > 1;
  const isSingleWritingTaskAssessment = isScanExtractedQuizMode &&
    !hasStructuredExtractedQuestionSet &&
    (isSingleTaskMode || Boolean(holisticTaskSuggestion) || extractedQuestions.length === 1);
  const singleWritingTaskModeNotice = "This assessment is a single writing task, so Quick Mark has been selected.";
  const singleWritingTaskUnavailableMessage =
    "This batch is set up as one writing task. Use Quick Mark, or add structured questions if you want Standard Marking or Full Analysis.";
  const isMarkingModeAvailable = (modeKey) => (
    modeKey === "quick" || !isSingleWritingTaskAssessment || hasStructuredExtractedQuestionSet
  );

  useEffect(() => {
    if (isSingleWritingTaskAssessment && markingMode !== "quick") {
      setMarkingMode("quick");
      setMarkingModeNotice(singleWritingTaskModeNotice);
      return;
    }
    if (!isSingleWritingTaskAssessment && markingModeNotice === singleWritingTaskModeNotice) {
      setMarkingModeNotice("");
    }
  }, [isSingleWritingTaskAssessment, markingMode, markingModeNotice]);

  const getFriendlyScanQuizTitle = () => {
    const date = new Date();
    const day = String(date.getDate()).padStart(2, "0");
    const month = date.toLocaleString("en-GB", { month: "short" });
    const year = date.getFullYear();
    return `Scanned Assessment - ${day} ${month} ${year}`;
  };

  const getEffectiveQuizTitle = () => (
    isScanExtractedQuizMode
      ? (!extractedQuiz?.quizTitle || /^scanQuiz_/i.test(extractedQuiz.quizTitle)
          ? getFriendlyScanQuizTitle()
          : extractedQuiz.quizTitle)
      : (selectedQuiz?.label || "")
  );

  const getExtractedTotalMarks = () => (
    extractedQuestions.reduce((sum, question) => sum + (parseInt(question.maxMark, 10) || 0), 0)
  );

  const resetExtractedQuizReview = () => {
    setQuestionExtractionError("");
    setExtractedQuiz(null);
    setExtractedQuestions([]);
    setExtractionDiagnostics(null);
    setQuestionExtractionSource(null);
    setHolisticTaskSuggestion(null);
    setExtractedQuestionsConfirmed(false);
  };

  const handleSetupModeChange = (mode) => {
    invalidatePaperWork();
    setSetupMode(mode);
    if (mode === "scanExtractedQuiz" || mode === "singleTask") {
      setSelectedQuiz(null);
      setUseDetectedNamesFromPdf(true);
      setBatchQuickMarkMode("batch");
      setBatchSplitMethod("coverNamePages");
      setMarkingMode(mode === "singleTask" ? "quick" : "standard");
      setBatchError(null);
      setBatchDetectionError("");
      setShowAdvancedBatchSplitOptions(false);
    } else {
      setMarkingMode("standard");
      resetExtractedQuizReview();
    }
  };

  const handleQuizSelect = (quiz) => {
    invalidatePaperWork();
    setSelectedQuiz(quiz);
  };

  const proceedToStudentSelection = () => {
    if (!isScanExtractedQuizMode && !selectedQuiz) {
      alert("Please select a test first.");
      return;
    }
    if (isScanExtractedQuizMode) {
      setUseDetectedNamesFromPdf(true);
      setBatchQuickMarkMode("batch");
      setBatchSplitMethod("coverNamePages");
      setBatchError(null);
      setBatchDetectionError("");
      setShowAdvancedBatchSplitOptions(false);
    }
    setCurrentStep(2);
  };

  // ===== STEP 2: STUDENT SELECTION =====
  const handleAddStudent = (student) => {
    if (!student || !student.value) {
      return; // Ignore null selections
    }
    const { maxStudents } = getPaperSubmissionLimits(userRole, teacherStatus, fullAnalysisEnabled);
    if (selectedStudents.length >= maxStudents) {
      setStudentLimitError(
        `Your account can process a maximum of ${maxStudents} student${maxStudents !== 1 ? "s" : ""} per batch${fullAnalysisEnabled ? " when full analysis is enabled" : " without full analysis"}.`
      );
      return;
    }
    if (selectedStudents.find((s) => s.value === student.value)) {
      alert("Student already selected.");
      return;
    }
    setStudentLimitError(null);
    const newStudent = { ...student };
    setSelectedStudents([...selectedStudents, newStudent]);
    setStudentImages((prev) => ({
      ...prev,
      [student.value]: { name: student.label, images: [], mobileImages: [], uploadingCount: 0 },
    }));
  };

  const handleAddManualStudent = () => {
    if (!manualStudentName.trim()) {
      alert("Please enter a student name.");
      return;
    }
    const { maxStudents } = getPaperSubmissionLimits(userRole, teacherStatus, fullAnalysisEnabled);
    if (selectedStudents.length >= maxStudents) {
      setStudentLimitError(
        `Your account can process a maximum of ${maxStudents} student${maxStudents !== 1 ? "s" : ""} per batch${fullAnalysisEnabled ? " when full analysis is enabled" : " without full analysis"}.`
      );
      return;
    }
    setStudentLimitError(null);
    const tempId = `manual_${Date.now()}`;
    const newStudent = { value: tempId, label: manualStudentName.trim() };
    setSelectedStudents([...selectedStudents, newStudent]);
    setStudentImages((prev) => ({
      ...prev,
      [tempId]: { name: manualStudentName.trim(), images: [], mobileImages: [], uploadingCount: 0 },
    }));
    setManualStudentName("");
  };

  const handleRemoveStudent = (studentId) => {
    setSelectedStudents((prev) => prev.filter((s) => s.value !== studentId));
    setStudentImages((prev) => {
      const updated = { ...prev };
      delete updated[studentId];
      return updated;
    });
    setSubmissionStatuses((prev) => {
      const updated = { ...prev };
      delete updated[studentId];
      return updated;
    });
  };

  const proceedToImageUpload = () => {
    if (selectedStudents.length === 0 && !useDetectedNamesFromPdf) {
      alert("Please select at least one student, or choose detected names from a combined PDF.");
      return;
    }
    const { maxStudents } = getPaperSubmissionLimits(userRole, teacherStatus, fullAnalysisEnabled);
    if (selectedStudents.length > maxStudents) {
      setStudentLimitError(
        `Your account can process a maximum of ${maxStudents} student${maxStudents !== 1 ? "s" : ""} per batch${fullAnalysisEnabled ? " when full analysis is enabled" : " without full analysis"}.`
      );
      return;
    }
    if (useDetectedNamesFromPdf) {
      setBatchQuickMarkMode("batch");
      setBatchSplitMethod("coverNamePages");
    }
    setStudentLimitError(null);
    setCurrentStep(3);
    if (selectedStudents.length > 0) {
      setCurrentUploadingStudent(selectedStudents[0].value);
    }
  };

  // ===== STEP 3: IMAGE UPLOAD PER STUDENT =====
  const handleImageUpload = (e, studentId) => {
    const files = Array.from(e.target.files);
    const imageFiles = files.filter((file) =>
      ["image/jpeg", "image/png"].includes(file.type)
    );

    if (imageFiles.length !== files.length) {
      alert("Only .jpg, .jpeg, and .png files are allowed.");
    }

    setStudentImages((prev) => ({
      ...prev,
      [studentId]: {
        ...prev[studentId],
        // Use natural sort to order files by filename numbers
        images: [...(prev[studentId].images || []), ...naturalSort(imageFiles)],
      },
    }));

    e.target.value = "";
  };

  // Helper: Sort current student's images by filename
  const handleSortImagesByFilename = (studentId) => {
    setStudentImages((prev) => ({
      ...prev,
      [studentId]: {
        ...prev[studentId],
        images: naturalSort(prev[studentId].images),
      },
    }));
  };

  const handleRemoveImage = (studentId, imageIndex) => {
    setStudentImages((prev) => ({
      ...prev,
      [studentId]: {
        ...prev[studentId],
        images: prev[studentId].images.filter((_, i) => i !== imageIndex),
      },
    }));
  };

  const readPdfFileAsBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = String(reader.result || "").split(",")[1];
      resolve(base64);
    };
    reader.onerror = () => reject(new Error("Could not read the PDF file."));
    reader.readAsDataURL(file);
  });

  const dataUrlToFile = (dataUrl, fileName = "page.png") => {
    const [header, b64] = dataUrl.split(",");
    const mime = header.match(/:(.*?);/)?.[1] || "image/png";
    const binaryStr = atob(b64);
    const bytes = new Uint8Array(binaryStr.length);
    for (let i = 0; i < binaryStr.length; i++) {
      bytes[i] = binaryStr.charCodeAt(i);
    }
    return new File([bytes], fileName, { type: mime });
  };

  const getBatchGroupStudentName = (group) => (
    group?.assignedStudentName ||
    group?.manualName ||
    group?.detectedName ||
    ""
  ).trim();

  const getBatchGroupStudentId = (group) => (group?.assignedStudentId || "").trim();

  const normalizeBatchScriptGroups = (groups = []) => (
    groups.map((group) => {
      const detectedName = (group.detectedName || "").trim();
      const currentName = getBatchGroupStudentName(group);
      const start = Number.isInteger(group.pageStart) ? group.pageStart : 0;
      const end = Number.isInteger(group.pageEnd) ? group.pageEnd : start;
      const pageIndexes = Array.isArray(group.pageIndexes) && group.pageIndexes.length > 0
        ? group.pageIndexes
        : Array.from({ length: Math.max(0, end - start + 1) }, (_, index) => start + index);
      return {
        ...group,
        detectedName,
        assignedStudentId: getBatchGroupStudentId(group),
        assignedStudentName: currentName || detectedName,
        pageStart: start,
        pageEnd: end,
        pageIndexes,
        sourcePageIndexes: pageIndexes,
        pageByteSizes: pageIndexes.map((pageIndex) => Number.isFinite(batchPdfPages[pageIndex]?.byteSize) ? batchPdfPages[pageIndex].byteSize : null)
      };
    })
  );

  const isLikelyBlankPdfPage = (pageIndex) => {
    const byteSize = batchPdfPages[pageIndex]?.byteSize;
    return Number.isFinite(byteSize) && byteSize > 0 && byteSize <= 25000;
  };

  const getLikelyBlankPagesForGroup = (group) => (
    (group?.pageIndexes || []).filter(pageIndex => isLikelyBlankPdfPage(pageIndex))
  );

  const getVisibleScriptWarnings = (group) => (
    (group?.warnings || []).filter((warning) => {
      if (/unusually few pages/i.test(String(warning || "")) && (group?.pageIndexes || []).length >= 2) {
        return false;
      }
      return true;
    })
  );

  const getUniqueWarnings = (warnings = []) => {
    const seen = new Set();
    return warnings
      .map((warning) => String(warning || "").trim())
      .filter(Boolean)
      .filter((warning) => {
        const key = warning
          .toLowerCase()
          .replace(/\bpdf page \d+\b/g, "pdf page")
          .replace(/\bpage \d+\b/g, "page")
          .replace(/\s+/g, " ");
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  };

  const buildPageIndexesFromRange = (startPage, endPage) => {
    const totalPages = batchPdfPages.length;
    const start = Math.max(1, Math.min(totalPages || 1, Number.parseInt(startPage, 10) || 1));
    const end = Math.max(start, Math.min(totalPages || start, Number.parseInt(endPage, 10) || start));
    return Array.from({ length: end - start + 1 }, (_, index) => start - 1 + index);
  };

  const updateBatchScriptRange = (groupId, field, value) => {
    setBatchScriptGroups((prev) => prev.map((group) => {
      if (group.groupId !== groupId) return group;
      const currentStartPage = (Number.isInteger(group.pageStart) ? group.pageStart : 0) + 1;
      const currentEndPage = (Number.isInteger(group.pageEnd) ? group.pageEnd : currentStartPage - 1) + 1;
      const nextStartPage = field === "start" ? value : currentStartPage;
      const nextEndPage = field === "end" ? value : currentEndPage;
      const pageIndexes = buildPageIndexesFromRange(nextStartPage, nextEndPage);
      const pageStart = pageIndexes[0] ?? 0;
      const pageEnd = pageIndexes[pageIndexes.length - 1] ?? pageStart;
      return {
        ...group,
        pageStart,
        pageEnd,
        pageIndexes,
        sourcePageIndexes: pageIndexes,
        pageByteSizes: pageIndexes.map((pageIndex) => Number.isFinite(batchPdfPages[pageIndex]?.byteSize) ? batchPdfPages[pageIndex].byteSize : null)
      };
    }));
    if (isScanExtractedQuizMode) setExtractedQuestionsConfirmed(false);
  };

  const removeLikelyBlankPagesFromScript = (groupId) => {
    setBatchScriptGroups((prev) => prev.map((group) => {
      if (group.groupId !== groupId) return group;
      const keptPageIndexes = (group.pageIndexes || []).filter(pageIndex => !isLikelyBlankPdfPage(pageIndex));
      if (keptPageIndexes.length === 0) return group;
      return {
        ...group,
        pageStart: keptPageIndexes[0],
        pageEnd: keptPageIndexes[keptPageIndexes.length - 1],
        pageIndexes: keptPageIndexes,
        sourcePageIndexes: keptPageIndexes,
        pageByteSizes: keptPageIndexes.map((pageIndex) => Number.isFinite(batchPdfPages[pageIndex]?.byteSize) ? batchPdfPages[pageIndex].byteSize : null)
      };
    }));
    if (isScanExtractedQuizMode) setExtractedQuestionsConfirmed(false);
  };

  const getBatchRangeDiagnostics = () => {
    if (!batchPdfPages.length || !batchScriptGroups.length) return { overlapWarnings: [], unassignedPages: [] };
    const pageOwners = new Map();
    batchScriptGroups.forEach((group) => {
      (group.pageIndexes || []).forEach((pageIndex) => {
        if (!pageOwners.has(pageIndex)) pageOwners.set(pageIndex, []);
        pageOwners.get(pageIndex).push(getBatchGroupStudentName(group) || group.groupId);
      });
    });
    const overlapWarnings = [...pageOwners.entries()]
      .filter(([, owners]) => owners.length > 1)
      .map(([pageIndex, owners]) => `Page ${pageIndex + 1} is assigned to ${owners.join(" and ")}.`);
    const unassignedPages = Array.from({ length: batchPdfPages.length }, (_, index) => index + 1)
      .filter((pageNumber) => !pageOwners.has(pageNumber - 1));
    return { overlapWarnings, unassignedPages };
  };

  const logPaperEndpointDebug = (endpoint, details = {}) => {
    void 0;
  };

  const getRepresentativeScriptExtractionPages = (pdfPages, groups) => {
    const firstGroup = [...(groups || [])]
      .sort((a, b) => (a.pageStart ?? 0) - (b.pageStart ?? 0))[0];
    if (!firstGroup) {
      return {
        pages: [],
        sourcePageIndexes: [],
        representativeScriptStartPageIndex: 0,
        coverPageIncluded: false,
        coverPageGroupIndex: null
      };
    }

    const groupPageIndexes = Array.isArray(firstGroup.pageIndexes) && firstGroup.pageIndexes.length > 0
      ? firstGroup.pageIndexes
      : Array.from(
          { length: Math.max(0, (firstGroup.pageEnd ?? firstGroup.pageStart ?? 0) - (firstGroup.pageStart ?? 0) + 1) },
          (_, index) => (firstGroup.pageStart ?? 0) + index
        );
    const firstGroupPageIndex = firstGroup.pageStart ?? groupPageIndexes[0];
    const coverPageGroupIndex = groupPageIndexes.findIndex(pageIndex => pageIndex === firstGroupPageIndex);
    const sourcePageIndexes = groupPageIndexes.filter(
      pageIndex => pdfPages[pageIndex]?.dataUrl
    );

    return {
      pages: sourcePageIndexes.map(pageIndex => pdfPages[pageIndex]),
      sourcePageIndexes,
      representativeScriptStartPageIndex: firstGroup.pageStart ?? 0,
      coverPageIncluded: coverPageGroupIndex >= 0,
      coverPageGroupIndex: coverPageGroupIndex >= 0 && sourcePageIndexes.includes(groupPageIndexes[coverPageGroupIndex])
        ? sourcePageIndexes.findIndex(pageIndex => pageIndex === groupPageIndexes[coverPageGroupIndex])
        : null
    };
  };

  const extractQuestionsFromDetectedScript = async (pdfPages, groups, idToken, pdfName = "", modeOverride = "quick") => {
    const paperScope = capturePaperRequest('extractQuestionsFromDetectedScript', {});
    if (!isScanExtractedQuizMode) return;
    const quickExtractionAllowed = modeOverride === "quick";
    const {
      pages,
      sourcePageIndexes,
      representativeScriptStartPageIndex,
      coverPageIncluded,
      coverPageGroupIndex
    } = getRepresentativeScriptExtractionPages(pdfPages, groups);

    logPaperEndpointDebug("/api/extract-quickmark-quiz-from-script", {
      markingMode: modeOverride,
      quickExtractionAllowed,
      pagesSent: pages.length,
      sourcePageIndexes,
      representativeScriptStartPageIndex,
      coverPageIncluded,
      coverPageGroupIndex
    });

    if (!quickExtractionAllowed) {
      void 0;
      return;
    }

    if (!pages.length) {
      setQuestionExtractionError("StudyDen could not find question pages in the first detected script.");
      return;
    }

    setQuestionExtracting(true);
    setQuestionExtractionError("");
    setExtractedQuestionsConfirmed(false);

    try {
      const response = await fetch(`${API_BASE_URL}/api/extract-quickmark-quiz-from-script`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          pages,
          sourcePageIndexes,
          representativeScriptStartPageIndex,
          coverPageIncluded,
          coverPageGroupIndex,
          markingMode: modeOverride,
          assessmentSource: setupMode,
          setupMode,
          uploadedPdfName: pdfName || batchPdfName
        }),
      });
      if (!paperActive(paperScope)) return;
      const data = await response.json();
      if (!paperActive(paperScope)) return;
      if (!data.ok) throw new Error(data.error || "Could not extract questions from this scan.");
      const responseQuestions = data.extractedQuiz?.questions || [];
      const responseLabels = responseQuestions
        .map((question, index) => String(question.questionLabel || `Q${index + 1}`).trim())
        .filter(Boolean);
      const responseTotalMarks = responseQuestions.reduce(
        (sum, question) => sum + (parseInt(question.maxMark, 10) || 0),
        0
      );
      void 0;

      setExtractedQuiz(data.extractedQuiz || null);
      setExtractedQuestions(responseQuestions);
      setExtractionDiagnostics(data.diagnostics || null);
      setQuestionExtractionSource(data.questionExtractionSource || null);
      setHolisticTaskSuggestion(data.holisticTaskSuggestion || null);
      if ((data.extractedQuiz?.questions || []).length === 0 && data.holisticTaskSuggestion) {
        setQuestionExtractionError("");
      }
    } catch (err) {
      if (!paperActive(paperScope)) return;
      void 0;
      setQuestionExtractionError(err.message || "Could not extract questions from this scan.");
    } finally {
      if (!paperActive(paperScope)) return;
      setQuestionExtracting(false);
    }
  };

  const runQuickExtractionForCurrentBatch = async (modeOverride = "quick") => {
    const paperScope = capturePaperRequest('runQuickExtractionForCurrentBatch', {});
    if (!isScanExtractedQuizMode || modeOverride !== "quick" || !batchPdfPages.length || !batchScriptGroups.length || questionExtracting) {
      return;
    }
    if (extractedQuestions.length > 0 || holisticTaskSuggestion) {
      return;
    }
    try {
      const auth = getAuth();
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error("You must be logged in.");
      const idToken = await currentUser.getIdToken();
      if (!paperActive(paperScope)) return;
      await extractQuestionsFromDetectedScript(batchPdfPages, batchScriptGroups, idToken, batchPdfName, "quick");
      if (!paperActive(paperScope)) return;
    } catch (err) {
      if (!paperActive(paperScope)) return;
      setQuestionExtractionError(err.message || "Could not extract questions from this scan.");
    }
  };

  const handleMarkingModeChange = (modeKey) => {
    if (!isMarkingModeAvailable(modeKey)) {
      setMarkingMode("quick");
      setMarkingModeNotice(singleWritingTaskModeNotice);
      return;
    }
    setMarkingModeNotice("");
    setMarkingMode(modeKey);
    if (modeKey === "quick") {
      runQuickExtractionForCurrentBatch(modeKey);
    }
  };

  const updateExtractedQuestion = (index, changes) => {
    setExtractedQuestions((prev) => prev.map((question, questionIndex) => (
      questionIndex === index ? { ...question, ...changes } : question
    )));
    setExtractedQuestionsConfirmed(false);
  };

  const removeExtractedQuestion = (index) => {
    setExtractedQuestions((prev) => prev.filter((_, questionIndex) => questionIndex !== index));
    setExtractedQuestionsConfirmed(false);
  };

  const addExtractedQuestion = () => {
    setExtractedQuestions((prev) => ([
      ...prev,
      {
        questionLabel: `Q${prev.length + 1}`,
        questionText: "",
        maxMark: 1,
        confidence: 0.5,
        markSchemeAnswer: "No mark scheme was provided for this scanned paper. Infer marks from the question text, visible student response, max mark value, and general subject knowledge."
      }
    ]));
    setExtractedQuestionsConfirmed(false);
  };

  const buildHolisticQuestionText = (suggestion = {}) => {
    if (suggestion.likelyStudentResponseAsPrompt) {
      return "Spanish writing task - please enter the printed prompt";
    }
    const prompts = Array.isArray(suggestion.numberedPrompts)
      ? suggestion.numberedPrompts.map(prompt => String(prompt || "").trim()).filter(Boolean)
      : [];
    const mainInstruction = String(suggestion.mainInstruction || "").trim();
    const wordCount = String(suggestion.wordCount || "").trim();
    const questionText = String(suggestion.questionText || "").trim();
    if (questionText && prompts.every(prompt => questionText.includes(prompt))) return questionText;
    const instructionParts = [mainInstruction || questionText || "Write a 130-150 word comparison of the two city pictures."];
    if (wordCount && !instructionParts[0].includes(wordCount)) instructionParts.push(`Word count: ${wordCount}`);
    const instruction = instructionParts.join("\n");
    return [
      instruction,
      prompts.length ? `Include answers to the following questions:\n\n${prompts.map((prompt, index) => `${index + 1}. ${prompt}`).join("\n")}` : ""
    ].filter(Boolean).join("\n\n");
  };

  const buildHolisticRubric = (suggestion = {}, fallbackRubric) => {
    const rubric = String(suggestion.markSchemeAnswer || "").trim();
    const prompts = Array.isArray(suggestion.numberedPrompts)
      ? suggestion.numberedPrompts.map(prompt => String(prompt || "").trim()).filter(Boolean)
      : [];
    const successCriteria = Array.isArray(suggestion.successCriteria)
      ? suggestion.successCriteria.map(item => String(item || "").trim()).filter(Boolean)
      : [];
    if (rubric && prompts.every(prompt => rubric.includes(prompt))) return rubric;
    if (!prompts.length && !successCriteria.length) return rubric || fallbackRubric;
    return [
      "Award marks for:",
      prompts.length ? `- answering all ${prompts.length} task prompts` : "- covering all parts of the task",
      ...prompts.map(prompt => `- ${prompt}`),
      ...successCriteria.map(item => `- ${item}`),
      suggestion.wordBankVisible ? "- using relevant vocabulary from the word bank" : "",
      "- using appropriate tenses",
      "- communicating clearly and accurately"
    ].filter(Boolean).join("\n");
  };

  const createHolisticWritingTask = () => {
    const fallbackRubric = [
      "Award marks for:",
      "- covering all parts of the task",
      "- comparing the two pictures/cities",
      "- mentioning past and present",
      "- describing advantages and disadvantages of change",
      "- identifying environmental problems",
      "- suggesting improvements",
      "- using comparison phrases",
      "- using relevant vocabulary from the word bank",
      "- using appropriate past, present, and conditional/future language",
      "- communicating clearly, accurately, and in detail"
    ].join("\n");
    const suggestion = holisticTaskSuggestion || {};
    const quizTitle = suggestion.quizTitle || suggestion.assessmentTitle || "Spanish Writing Task";
    const question = {
      questionLabel: suggestion.questionLabel || "Writing Task",
      questionText: buildHolisticQuestionText(suggestion) || "Spanish writing task - please enter the printed prompt",
      maxMark: suggestion.maxMark || 20,
      confidence: suggestion.promptConfidence || suggestion.confidence || 0.4,
      markSchemeAnswer: buildHolisticRubric(suggestion, fallbackRubric),
      visualOrder: 1,
      questionRelativePageIndex: null,
      sourceRelativePageIndex: null,
      extractionNotes: "Holistic writing-task fallback created by teacher."
    };
    setExtractedQuiz((prev) => ({
      ...(prev || {}),
      temporaryQuizId: prev?.temporaryQuizId || `scanQuiz_${Date.now()}`,
      temporary: true,
      source: "scan_extracted",
      quizTitle,
      totalMarks: Number(question.maxMark) || 20,
      questions: [question]
    }));
    setExtractedQuestions([question]);
    setQuestionExtractionError("");
    setExtractedQuestionsConfirmed(false);
  };

  const confirmExtractedQuestions = () => {
    if (extractedQuestions.length === 0 || getExtractedTotalMarks() <= 0) {
      setQuestionExtractionError("Please keep at least one extracted question with marks before continuing.");
      return;
    }
    const missingQuestion = extractedQuestions.some(question => (
      !String(question.questionLabel || "").trim() ||
      !String(question.questionText || "").trim() ||
      !(parseInt(question.maxMark, 10) > 0)
    ));
    if (missingQuestion) {
      setQuestionExtractionError("Please add a label, question text, and positive mark value for every question.");
      return;
    }
    setQuestionExtractionError("");
    setExtractedQuestionsConfirmed(true);
  };

  const getDetectedQuestionPatternCount = () => {
    const visibleCount = Number(extractionDiagnostics?.visibleQuestionLabelCount);
    if (Number.isFinite(visibleCount) && visibleCount > 0) return visibleCount;
    const diagnosticCount = Number(extractionDiagnostics?.detectedQuestionPatternCount);
    if (Number.isFinite(diagnosticCount) && diagnosticCount > 0) return diagnosticCount;
    const labelPattern = /(?:^|\b)(?:q\s*)?\d+\s*(?:\([a-zivx]+\)|[a-z]\b|\.\d+)?/i;
    return extractedQuestions.filter((question) => (
      labelPattern.test(String(question.questionLabel || "")) ||
      /\[\s*\d+\s*(?:marks?)?\s*\]|\(\s*\d+\s*marks?\s*\)/i.test(String(question.questionText || ""))
    )).length;
  };

  const buildTemporaryQuizPayload = () => {
    if (!isScanExtractedQuizMode) return null;
    const questions = extractedQuestions.map((question, index) => ({
      ...question,
      questionLabel: String(question.questionLabel || `Q${index + 1}`).trim(),
      questionText: String(question.questionText || "").trim(),
      maxMark: parseInt(question.maxMark, 10) || 1,
      markSchemeAnswer: question.markSchemeAnswer || "No mark scheme was provided for this scanned paper. Infer marks from the question text, visible student response, max mark value, and general subject knowledge."
    }));
    return {
      ...(extractedQuiz || {}),
      temporaryQuizId: extractedQuiz?.temporaryQuizId || `scanQuiz_${Date.now()}`,
      temporary: true,
      source: "scan_extracted",
      quizTitle: getEffectiveQuizTitle(),
      totalMarks: questions.reduce((sum, question) => sum + (Number(question.maxMark) || 0), 0),
      questions
    };
  };

  const getProcessingStudents = () => {
    if (batchQuickMarkMode !== "batch") return selectedStudents;
    return batchScriptGroups.map((group, index) => ({
      value: group.groupId,
      label: getBatchGroupStudentName(group) || `Script ${index + 1}`
    }));
  };

  const activateDetectedNamesFromPdf = (enabled = true) => {
    setUseDetectedNamesFromPdf(enabled);
    if (enabled) {
      setBatchQuickMarkMode("batch");
      setBatchSplitMethod("coverNamePages");
      setBatchError(null);
      setBatchDetectionError("");
      setShowAdvancedBatchSplitOptions(false);
    }
  };

  const isDetectedNamesOnlyFlow = () => useDetectedNamesFromPdf && selectedStudents.length === 0;

  const detectedNamesOnlyFlowError =
    "Detected-name PDF mode needs a combined PDF with multiple scripts. Upload a combined PDF, or go back and select students for single-student uploads.";

  const handleBatchPdfUpload = async (e) => {
    const paperScope = capturePaperRequest('handleBatchPdfUpload', {});
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.type !== "application/pdf") {
      alert("Please select a PDF file (.pdf).");
      return;
    }
    if (!selectedQuiz && !isScanExtractedQuizMode) {
      alert("Please select a test before using a combined batch PDF.");
      return;
    }

    setBatchQuickMarkMode("batch");
    setBatchDetecting(true);
    setBatchDetectionError("");
    setBatchPdfName(file.name);
    setBatchPdfPages([]);
    setBatchScriptGroups([]);
    resetExtractedQuizReview();

    try {
      const pdfBase64 = await readPdfFileAsBase64(file);
      if (!paperActive(paperScope)) return;
      const auth = getAuth();
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error("You must be logged in to upload a PDF.");
      const idToken = await currentUser.getIdToken();
      if (!paperActive(paperScope)) return;

      setStatusMessage("Detecting scripts...");
      logPaperEndpointDebug("/api/convert-pdf-to-images", {
        pagesSent: 0,
        representativeScriptStartPageIndex: null,
        coverPageIncluded: null,
        coverPageGroupIndex: null
      });
      const convertResponse = await fetch(`${API_BASE_URL}/api/convert-pdf-to-images`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({ pdfBase64, allowBatchQuickMark: true }),
      });
      if (!paperActive(paperScope)) return;
      const convertData = await convertResponse.json();
      if (!paperActive(paperScope)) return;
      if (!convertData.ok) throw new Error(convertData.error || "PDF conversion failed on the server.");
      if (!Array.isArray(convertData.pages) || convertData.pages.length === 0) {
        throw new Error("PDF pages are still preparing. Please try again in a moment.");
      }

      setBatchPdfPages(convertData.pages || []);

      logPaperEndpointDebug("/api/detect-batch-quickmark-groups", {
        pagesSent: (convertData.pages || []).length,
        representativeScriptStartPageIndex: null,
        coverPageIncluded: null,
        coverPageGroupIndex: null
      });
      const detectResponse = await fetch(`${API_BASE_URL}/api/detect-batch-quickmark-groups`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          pages: convertData.pages || [],
          splitMethod: batchSplitMethod,
          pagesPerStudent: Number(batchPagesPerStudent) || null,
          selectedStudents
        }),
      });
      if (!paperActive(paperScope)) return;
      const detectData = await detectResponse.json();
      if (!paperActive(paperScope)) return;
      if (!detectData.ok) throw new Error(detectData.error || "Could not detect script groups.");

      const normalizedGroups = normalizeBatchScriptGroups(detectData.groups || []);
      setBatchScriptGroups(normalizedGroups);
      if (isScanExtractedQuizMode) {
        setStatusMessage("Extracting question structure...");
        await extractQuestionsFromDetectedScript(convertData.pages || [], normalizedGroups, idToken, file.name, "quick");
      if (!paperActive(paperScope)) return;
      }
      setStatusMessage("");
    } catch (err) {
      if (!paperActive(paperScope)) return;
      void 0;
      setBatchDetectionError(err.message || "Could not detect script groups.");
      setStatusMessage("");
    } finally {
      if (!paperActive(paperScope)) return;
      setBatchDetecting(false);
    }
  };

  const rerunBatchSplitDetection = async () => {
    const paperScope = capturePaperRequest('rerunBatchSplitDetection', {});
    if (!batchPdfPages.length) return;
    setBatchDetecting(true);
    setBatchDetectionError("");
    try {
      const auth = getAuth();
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error("You must be logged in.");
      const idToken = await currentUser.getIdToken();
      if (!paperActive(paperScope)) return;
      logPaperEndpointDebug("/api/detect-batch-quickmark-groups", {
        pagesSent: batchPdfPages.length,
        representativeScriptStartPageIndex: null,
        coverPageIncluded: null,
        coverPageGroupIndex: null
      });
      const response = await fetch(`${API_BASE_URL}/api/detect-batch-quickmark-groups`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          pages: batchPdfPages,
          splitMethod: batchSplitMethod,
          pagesPerStudent: Number(batchPagesPerStudent) || null,
          selectedStudents
        }),
      });
      if (!paperActive(paperScope)) return;
      const data = await response.json();
      if (!paperActive(paperScope)) return;
      if (!data.ok) throw new Error(data.error || "Could not detect script groups.");
      const normalizedGroups = normalizeBatchScriptGroups(data.groups || []);
      setBatchScriptGroups(normalizedGroups);
      if (isScanExtractedQuizMode) {
        resetExtractedQuizReview();
        await extractQuestionsFromDetectedScript(batchPdfPages, normalizedGroups, idToken, batchPdfName, "quick");
      if (!paperActive(paperScope)) return;
      }
    } catch (err) {
      if (!paperActive(paperScope)) return;
      setBatchDetectionError(err.message || "Could not detect script groups.");
    } finally {
      if (!paperActive(paperScope)) return;
      setBatchDetecting(false);
    }
  };

  const updateBatchScriptGroup = (groupId, changes) => {
    setBatchScriptGroups((prev) => prev.map(group => (
      group.groupId === groupId ? { ...group, ...changes } : group
    )));
  };

  const removeBatchScriptGroup = (groupId) => {
    setBatchScriptGroups((prev) => prev.filter(group => group.groupId !== groupId));
  };

  const addBatchScriptManually = () => {
    if (!batchPdfPages.length) return;
    const studentName = window.prompt("Student name for the new script:");
    if (!studentName || !studentName.trim()) return;
    const startInput = window.prompt("Start page number:");
    const endInput = window.prompt("End page number:");
    const pageIndexes = buildPageIndexesFromRange(startInput, endInput);
    if (!pageIndexes.length) return;
    const pageStart = pageIndexes[0];
    const pageEnd = pageIndexes[pageIndexes.length - 1];
    const name = studentName.trim();
    setBatchScriptGroups((prev) => ([
      ...prev,
      {
        groupId: `manual_group_${Date.now()}`,
        detectedName: name,
        assignedStudentId: "",
        assignedStudentName: name,
        pageStart,
        pageEnd,
        pageIndexes,
        sourcePageIndexes: pageIndexes,
        pageByteSizes: pageIndexes.map((pageIndex) => Number.isFinite(batchPdfPages[pageIndex]?.byteSize) ? batchPdfPages[pageIndex].byteSize : null),
        confidence: "manual",
        rawSummary: "Manually added script range.",
        warnings: []
      }
    ]));
    if (isScanExtractedQuizMode) setExtractedQuestionsConfirmed(false);
  };

  const moveImageUp = (studentId, imageIndex) => {
    if (imageIndex === 0) return;
    setStudentImages((prev) => ({
      ...prev,
      [studentId]: {
        ...prev[studentId],
        images: [
          ...prev[studentId].images.slice(0, imageIndex - 1),
          prev[studentId].images[imageIndex],
          prev[studentId].images[imageIndex - 1],
          ...prev[studentId].images.slice(imageIndex + 1),
        ],
      },
    }));
  };

  const moveImageDown = (studentId, imageIndex) => {
    if (imageIndex === studentImages[studentId].images.length - 1) return;
    setStudentImages((prev) => ({
      ...prev,
      [studentId]: {
        ...prev[studentId],
        images: [
          ...prev[studentId].images.slice(0, imageIndex),
          prev[studentId].images[imageIndex + 1],
          prev[studentId].images[imageIndex],
          ...prev[studentId].images.slice(imageIndex + 2),
        ],
      },
    }));
  };

  // ─────────────────────────────────────────────────────────────────────────
  // PDF UPLOAD HANDLER
  // Reads the selected PDF as base64, sends it to /api/convert-pdf-to-images,
  // receives one PNG per page, converts each to a File object, and appends
  // them to the student's images array.  The rest of the pipeline
  // (compression → Firebase Storage → OCR → marking) is completely unchanged.
  // ─────────────────────────────────────────────────────────────────────────
  const handlePdfUpload = async (e, studentId) => {
    const paperScope = capturePaperRequest('handlePdfUpload', {});
    const file = e.target.files?.[0];
    e.target.value = ""; // always reset so the same file can be re-selected

    if (!file) return;
    if (file.type !== "application/pdf") {
      alert("Please select a PDF file (.pdf).");
      return;
    }

    // Check combined page limit before starting conversion
    const existingPages =
      (studentImages[studentId]?.images?.length || 0) +
      (studentImages[studentId]?.mobileImages?.length || 0);
    if (existingPages >= 25) {
      alert("Maximum 25 pages already reached for this student.");
      return;
    }

    setPdfConverting((prev) => ({ ...prev, [studentId]: true }));

    try {
      // ── Step 1: Read the PDF file as base64 ──
      const pdfBase64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          // result is "data:application/pdf;base64,<base64>"
          const base64 = reader.result.split(",")[1];
          resolve(base64);
        };
        reader.onerror = () => reject(new Error("Could not read the PDF file."));
        reader.readAsDataURL(file);
      });
      if (!paperActive(paperScope)) return;

      // ── Step 2: Authenticate and POST to server ──
      const auth = getAuth();
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error("You must be logged in to upload a PDF.");
      const idToken = await currentUser.getIdToken();
      if (!paperActive(paperScope)) return;

      const response = await fetch(`${API_BASE_URL}/api/convert-pdf-to-images`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({ pdfBase64 }),
      });
      if (!paperActive(paperScope)) return;

      const data = await response.json();
      if (!paperActive(paperScope)) return;
      if (!data.ok) throw new Error(data.error || "PDF conversion failed on the server.");

      // ── Step 3: Enforce page limit on returned pages ──
      const remainingSlots = 25 - existingPages;
      const pagesToAdd = data.pages.slice(0, remainingSlots);

      if (data.pages.length > remainingSlots) {
        alert(
          `The PDF has ${data.pages.length} pages, but only ${remainingSlots} slot(s) ` +
            `remain for this student. Only the first ${remainingSlots} page(s) were added.`
        );
      }

      // ── Step 4: Convert base64 data URLs → File objects ──
      const pageFiles = pagesToAdd.map((page, idx) => {
        const [header, b64] = page.dataUrl.split(",");
        const mime = header.match(/:(.*?);/)[1]; // e.g. "image/png"
        const binaryStr = atob(b64);
        const bytes = new Uint8Array(binaryStr.length);
        for (let i = 0; i < binaryStr.length; i++) {
          bytes[i] = binaryStr.charCodeAt(i);
        }
        return new File([bytes], page.name || `PDF_Page_${idx + 1}.png`, { type: mime });
      });

      // ── Step 5: Append to studentImages exactly like regular images ──
      setStudentImages((prev) => ({
        ...prev,
        [studentId]: {
          ...prev[studentId],
          images: [...(prev[studentId].images || []), ...pageFiles],
        },
      }));

      void 0;
    } catch (err) {
      if (!paperActive(paperScope)) return;
      void 0;
      alert(`Failed to process PDF: ${err.message}`);
    } finally {
      if (!paperActive(paperScope)) return;
      setPdfConverting((prev) => ({ ...prev, [studentId]: false }));
    }
  };

  const goToNextStudent = () => {
    const currentIndex = selectedStudents.findIndex(
      (s) => s.value === currentUploadingStudent
    );
    if (currentIndex < selectedStudents.length - 1) {
      setCurrentUploadingStudent(selectedStudents[currentIndex + 1].value);
    }
  };

  const goToPreviousStudent = () => {
    const currentIndex = selectedStudents.findIndex(
      (s) => s.value === currentUploadingStudent
    );
    if (currentIndex > 0) {
      setCurrentUploadingStudent(selectedStudents[currentIndex - 1].value);
    }
  };

  const proceedToMarkingMode = () => {
    if (isDetectedNamesOnlyFlow() && batchQuickMarkMode !== "batch") {
      alert(detectedNamesOnlyFlowError);
      return;
    }
    if (batchQuickMarkMode === "batch") {
      if (batchScriptGroups.length < 2) {
        alert("Please upload a combined PDF and detect at least two script groups.");
        return;
      }
      if (batchScriptGroups.some(group => !getBatchGroupStudentName(group))) {
        alert("Every detected script needs a usable student name before marking.");
        return;
      }
      if (isScanExtractedQuizMode && (!extractedQuestionsConfirmed || extractedQuestions.length === 0 || getExtractedTotalMarks() <= 0)) {
        alert("Please review and confirm the extracted question structure before continuing.");
        return;
      }
      setCurrentStep(4);
      return;
    }

    // Validate: every student must have at least 1 image (local or mobile — Phase 4)
    const allHaveImages = selectedStudents.every((s) => {
      const entry = studentImages[s.value];
      const localCount = entry?.images?.length || 0;
      const mobileCount = entry?.mobileImages?.length || 0;
      return localCount + mobileCount > 0;
    });
    if (!allHaveImages) {
      alert("Every student must have at least one uploaded image.");
      return;
    }
    setCurrentStep(4);
  };

  const proceedToReview = () => {
    if (!isMarkingModeAvailable(markingMode)) {
      setMarkingMode("quick");
      setMarkingModeNotice(singleWritingTaskModeNotice);
      alert(singleWritingTaskUnavailableMessage);
      return;
    }
    if (markingMode === "quick" && !selectedQuiz && !isScanExtractedQuizMode) {
      alert("Please select a test before using Quick Mark.");
      return;
    }
    if (isDetectedNamesOnlyFlow() && batchQuickMarkMode !== "batch") {
      alert(detectedNamesOnlyFlowError);
      return;
    }
    if (markingMode === "quick" && batchQuickMarkMode === "batch") {
      if (!batchPdfPages.length || batchScriptGroups.length < 2) {
        alert("Please upload a combined PDF and detect at least two script groups.");
        return;
      }
      const missingName = batchScriptGroups.some(group => !getBatchGroupStudentName(group));
      if (missingName) {
        alert("Every detected script needs a usable student name before marking.");
        return;
      }
      if (isScanExtractedQuizMode && (!extractedQuestionsConfirmed || extractedQuestions.length === 0 || getExtractedTotalMarks() <= 0)) {
        alert("Please review and confirm the extracted question structure before marking.");
        return;
      }
    }
    if (isScanExtractedQuizMode && (!extractedQuestionsConfirmed || extractedQuestions.length === 0 || getExtractedTotalMarks() <= 0)) {
      alert("Please review and confirm the extracted assessment structure before marking.");
      return;
    }
    setBatchError(null);
    setCurrentStep(5);
  };

  // ===== STEP 5: BATCH SUMMARY & CREDIT CALCULATION =====
  const calculateCreditCost = () => {
    const studentCount = batchQuickMarkMode === "batch" ? batchScriptGroups.length : selectedStudents.length;
    if (!studentCount) return { baseStudentCost: 0, pageCost: 0, fullAnalysisCost: 0, quickMarkDiscount: 0, totalCost: 0 };
    const totalPages = getTotalPageCount();
    const standardBaseCost = studentCount;                                // 1 credit per student script
    const baseStudentCost = markingMode === "quick" ? Math.ceil(studentCount / 2) : standardBaseCost;
    const pageCost = Math.ceil(totalPages / 10);                         // 1 credit per 10 pages, rounded up
    const fullAnalysisCost = fullAnalysisEnabled ? studentCount * 2 : 0; // 2 credits per student if enabled
    return {
      baseStudentCost,
      pageCost,
      fullAnalysisCost,
      quickMarkDiscount: markingMode === "quick" ? standardBaseCost - baseStudentCost : 0,
      totalCost: baseStudentCost + pageCost + fullAnalysisCost
    };
  };

  const getTotalPageCount = () => {
    if (batchQuickMarkMode === "batch") {
      return batchPdfPages.length;
    }
    // Phase 4: includes both local files and mobile-uploaded images in the total
    return selectedStudents.reduce((total, student) => {
      const entry = studentImages[student.value];
      const localCount = entry?.images?.length || 0;
      const mobileCount = entry?.mobileImages?.length || 0;
      return total + localCount + mobileCount;
    }, 0);
  };

  const blockSubmission = (message, step) => {
    setBatchError(message);
    setBatchErrorAction(step ? { message, step } : null);
  };

  const proceedToProcessing = () => {
    if (startBatchInFlightRef.current) return;
    const { maxStudents, maxPages } = getPaperSubmissionLimits(userRole, teacherStatus, fullAnalysisEnabled);
    const totalPages = getTotalPageCount();
    const effectiveStudentCount = batchQuickMarkMode === "batch" ? batchScriptGroups.length : selectedStudents.length;

    if (!isMarkingModeAvailable(markingMode)) {
      setMarkingMode("quick");
      setMarkingModeNotice(singleWritingTaskModeNotice);
      setBatchError(singleWritingTaskUnavailableMessage);
      return;
    }

    if (markingMode === "quick" && !selectedQuiz && !isScanExtractedQuizMode) {
      setBatchError("Please select a test before using Quick Mark.");
      return;
    }
    if (isScanExtractedQuizMode && (!extractedQuestionsConfirmed || extractedQuestions.length === 0 || getExtractedTotalMarks() <= 0)) {
      blockSubmission("Review and confirm the assessment structure in Step 4 before starting marking.", 4);
      return;
    }

    if (isDetectedNamesOnlyFlow() && batchQuickMarkMode !== "batch") {
      blockSubmission(detectedNamesOnlyFlowError, 3);
      return;
    }

    // Frontend safety: re-validate student count (full analysis toggle may have changed)
    if (effectiveStudentCount > maxStudents) {
      setBatchError(
        `Batch limit exceeded. Your account can process a maximum of ${maxStudents} student${maxStudents !== 1 ? "s" : ""} per batch${fullAnalysisEnabled ? " when full analysis is enabled" : " without full analysis"}.`
      );
      return;
    }

    // Frontend safety: validate total page count
    if (totalPages > maxPages) {
      setBatchError(
        `Your batch contains ${totalPages} pages/images. Your account limit is ${maxPages} pages/images per batch.`
      );
      return;
    }

    const creditCost = calculateCreditCost();
    setCurrentStep(5);
    handleStartBatch(creditCost.totalCost);
  };

  // ===== STEP 5: PROCESSING & RESULTS =====
  const handleStartBatch = async (estimatedCost) => {
    if (startBatchInFlightRef.current) return;
    startBatchInFlightRef.current = true;
    setIsStartingBatch(true);
    setStatusMessage("⏳ Starting marking...");
    try {
      await startBatch(estimatedCost);
    } finally {
      startBatchInFlightRef.current = false;
      if (isMountedRef.current) setIsStartingBatch(false);
    }
  };

  const startBatch = async (estimatedCost) => {
    const paperScope = capturePaperRequest('handleStartBatch', {});
    if ((!selectedQuiz && !isScanExtractedQuizMode) || (batchQuickMarkMode !== "batch" && selectedStudents.length === 0)) {
      alert("Invalid batch configuration.");
      return;
    }
    if (!isMarkingModeAvailable(markingMode)) {
      setMarkingMode("quick");
      setMarkingModeNotice(singleWritingTaskModeNotice);
      alert(singleWritingTaskUnavailableMessage);
      return;
    }
    if (isScanExtractedQuizMode && (!extractedQuestionsConfirmed || extractedQuestions.length === 0 || getExtractedTotalMarks() <= 0)) {
      alert("Please review and confirm the extracted assessment structure before starting marking.");
      return;
    }

    try {
      const requestOwner = getAuth().currentUser;
      const pending = requestOwner && localStorage.getItem(paperKey(requestOwner.uid, 'pending'));
      if (pending) {
        paperScope.requestId=JSON.parse(pending).requestId;
        try {
          const accepted = await paperRequest('/api/submit-paper-batch', JSON.parse(pending));
      if (!paperActive(paperScope)) return;
          localStorage.setItem(paperKey(requestOwner.uid, 'batch'), accepted.batchId);
          localStorage.removeItem(paperKey(requestOwner.uid, 'pending'));
          selectPaperBatch(accepted.batchId); setIsProcessing(true); pollBatchStatus(accepted.batchId); return;
        } catch (error) {
          if (!paperActive(paperScope)) return;
          // A lost or transient acceptance stays saved so its idempotent replay cannot double-charge.
          if (!isDefinitivePaperRejection(error)) throw error;
          // The saved request was rejected (and not charged); discard it and submit the current batch.
          localStorage.removeItem(paperKey(requestOwner.uid, 'pending'));
          paperScope.requestId = null;
        }
      }
      // ========== PRE-FLIGHT ACCESS CHECK ==========
      // Check teacher status BEFORE starting any batch processing
      // This prevents the processing UI from showing if access is denied
      void 0;
      const accessCheckResult = await checkTeacherAiAccessFrontend();
      if (!paperActive(paperScope)) return;
      
      if (!accessCheckResult.allowed) {
        // Access denied - show the error message and STOP
        void 0;
        alert(accessCheckResult.message);
        setBatchError(accessCheckResult.message);
        return; // Exit without starting processing
      }
      
      void 0;

      // Only NOW set processing state after access check passes
      setIsProcessing(true);
      setBatchError(null);
      setStatusMessage("⏳ Compressing and uploading images...");

      // Get Firebase ID token and auth setup
      const auth = getAuth();
      const currentUser = auth.currentUser;
      
      if (!currentUser) {
        throw new Error("You must be logged in to create a batch.");
      }

      // Step 1: Build upload jobs for all images across all students/scripts
      const storage = getStorage();
      const batchFolder = `paper-submissions/${currentUser.uid}/${crypto.randomUUID()}`;
      const uploadJobs = [];

      if (batchQuickMarkMode === "batch") {
        for (const group of batchScriptGroups) {
          const studentName = getBatchGroupStudentName(group);
          if (!studentName) throw new Error("Every detected script needs a student name.");
          const student = {
            value: group.groupId,
            studentId: getBatchGroupStudentId(group),
            label: studentName,
            detectedName: group.detectedName,
            batchGroup: group
          };
          for (let i = 0; i < group.pageIndexes.length; i++) {
            const sourcePageIndex = group.pageIndexes[i];
            const page = batchPdfPages[sourcePageIndex];
            if (!page?.dataUrl) throw new Error(`Missing page image for PDF page ${sourcePageIndex + 1}.`);
            uploadJobs.push({
              student,
              pageIndex: i,
              sourcePageIndex,
              sourceByteSize: Number.isFinite(page.byteSize) ? page.byteSize : null,
              file: dataUrlToFile(page.dataUrl, page.name || `PDF_Page_${sourcePageIndex + 1}.png`),
              storagePath: `${batchFolder}/${group.groupId}/page_${i + 1}`,
            });
          }
        }
      } else {
        for (const student of selectedStudents) {
          const studentId = student.value;
          const images = studentImages[studentId].images;

          for (let i = 0; i < images.length; i++) {
            uploadJobs.push({
              student: student,
              pageIndex: i,
              file: images[i],
              storagePath: `${batchFolder}/${studentId}/page_${i + 1}`,
            });
          }
        }
      }

      let completedUploads = 0;
      const totalUploads = uploadJobs.length;

      // Upload worker function with compression and progress tracking
      const uploadWorker = async (job) => {
        try {
          // Compress image before upload
          const compressedFile = await compressImage(job.file);
          if (!paperActive(paperScope)) return null;
          
          // Upload to Firebase Storage
          const imageRef = ref(storage, job.storagePath);
          await uploadBytes(imageRef, compressedFile);
          if (!paperActive(paperScope)) return null;
          const downloadURL = job.storagePath;

          // Update progress
          completedUploads++;
          setStatusMessage(`⏳ Uploading images... (${completedUploads} / ${totalUploads})`);

          return {
            student: job.student,
            pageIndex: job.pageIndex,
            sourcePageIndex: Number.isInteger(job.sourcePageIndex) ? job.sourcePageIndex : null,
            sourceByteSize: Number.isFinite(job.sourceByteSize) ? job.sourceByteSize : null,
            downloadURL: downloadURL,
          };
        } catch (error) {
          throw new Error(`Failed to upload ${job.student.label} page ${job.pageIndex + 1}: ${error.message}`);
        }
      };

      // Step 2: Execute uploads with concurrency limit
      const uploadResults = await runWithConcurrency(uploadJobs, 6, uploadWorker);
      if (!paperActive(paperScope)) return;

      // Step 3: Regroup results by student, preserving page order.
      // Local files are sorted by pageIndex (the order they were shown in the UI).
      // Mobile images are appended after local files, in their displayed order.
      const batchStudentsData = batchQuickMarkMode === "batch"
        ? batchScriptGroups.map(group => {
            const batchGroupId = group.groupId;
            const studentId = getBatchGroupStudentId(group);
            const studentName = getBatchGroupStudentName(group);
            const studentUploads = uploadResults
              .filter(result => result.student.value === batchGroupId || result.student.batchGroup?.groupId === group.groupId)
              .sort((a, b) => a.pageIndex - b.pageIndex)
              .map(result => result.downloadURL);
            const pageByteSizes = uploadResults
              .filter(result => result.student.value === batchGroupId || result.student.batchGroup?.groupId === group.groupId)
              .sort((a, b) => a.pageIndex - b.pageIndex)
              .map(result => Number.isFinite(result.sourceByteSize) ? result.sourceByteSize : null);
            const coverPageGroupIndex = Array.isArray(group.pageIndexes)
              ? group.pageIndexes.findIndex(pageIndex => pageIndex === group.pageStart)
              : -1;
            const coverPageIncluded = coverPageGroupIndex >= 0;
            const firstQuestionGroupIndex = coverPageIncluded ? coverPageGroupIndex + 1 : 0;
            return {
              studentId,
              batchGroupId,
              studentName,
              detectedName: group.detectedName,
              batchQuickMark: true,
              pageStart: group.pageStart,
              pageEnd: group.pageEnd,
              pageIndexes: group.pageIndexes,
              coverPageIncluded,
              coverPageGroupIndex: coverPageIncluded ? coverPageGroupIndex : null,
              firstQuestionGroupIndex,
              imagesAreQuestionOnly: false,
              pageCount: studentUploads.length,
              pageByteSizes,
              uploadedImageRefs: studentUploads,
            };
          })
        : selectedStudents.map(student => {
            const studentUploads = uploadResults
              .filter(result => result.student.value === student.value)
              .sort((a, b) => a.pageIndex - b.pageIndex)
              .map(result => result.downloadURL);

            // Phase 5: append phone-uploaded URLs (already in Storage, no re-upload needed)
            const mobileUrls = (studentImages[student.value].mobileImages || []).map(m => m.url);
            const allImageUrls = [...studentUploads, ...mobileUrls];

            return {
              studentId: student.value,
              studentName: student.label,
              pageCount: allImageUrls.length,
              uploadedImageRefs: allImageUrls,
            };
          });

      setStatusMessage("⏳ Creating batch in system...");
      
      const idToken = await currentUser.getIdToken();
      if (!paperActive(paperScope)) return;
      const temporaryQuizPayload = buildTemporaryQuizPayload();
      const effectiveQuizId = isScanExtractedQuizMode ? temporaryQuizPayload.temporaryQuizId : selectedQuiz.value;
      const effectiveQuizTitle = isScanExtractedQuizMode ? temporaryQuizPayload.quizTitle : selectedQuiz.label;

      // Step 4: Send batch to backend for creation and processing
      logPaperEndpointDebug("/api/submit-paper-batch", {
        pagesSent: getTotalPageCount(),
        representativeScriptStartPageIndex: null,
        coverPageIncluded: null,
        coverPageGroupIndex: null
      });
      const pendingPayload = { requestId: crypto.randomUUID(),
          quizId: effectiveQuizId,
          quizTitle: effectiveQuizTitle,
          students: batchStudentsData,
          markingMode: markingMode,
          fullAnalysisEnabled: fullAnalysisEnabled,
          estimatedCreditCost: estimatedCost,
          batchQuickMark: batchQuickMarkMode === "batch",
          splitMethod: batchQuickMarkMode === "batch" ? batchSplitMethod : null,
          uploadedPdfName: batchQuickMarkMode === "batch" ? batchPdfName : null,
          scriptGroups: batchQuickMarkMode === "batch" ? batchScriptGroups : null,
          setupMode: isScanExtractedQuizMode ? "scanExtractedQuiz" : setupMode,
          assessmentSource: setupMode,
          assessmentReviewConfirmed: isScanExtractedQuizMode ? extractedQuestionsConfirmed : undefined,
          temporaryQuiz: temporaryQuizPayload,
          extractedQuestions: temporaryQuizPayload?.questions || null,
          extractionDiagnostics,
          questionExtractionSource,
      };
      localStorage.setItem(paperKey(currentUser.uid, 'pending'), JSON.stringify(pendingPayload));
      paperScope.requestId=pendingPayload.requestId;
      const response = await fetch(`${API_BASE_URL}/api/submit-paper-batch`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${idToken}`
        },
        body: JSON.stringify(pendingPayload),
      });
      if (!paperActive(paperScope)) return;

      const data = await response.json();
      if (!paperActive(paperScope)) return;

      if (!response.ok) {
        const errorMsg = data.error || "Failed to create batch";
        if (isDefinitivePaperRejection({ status: response.status })) localStorage.removeItem(paperKey(currentUser.uid, 'pending'));
        setBatchError(errorMsg);
        setBatchErrorAction(data.code === "PAPER_SCAN_REVIEW_REQUIRED"
          ? { message: errorMsg, step: batchQuickMarkMode === "batch" ? 3 : 4 }
          : null);
        setStatusMessage(`❌ Error: ${errorMsg}`);
        setIsProcessing(false);
        return;
      }

      localStorage.setItem(paperKey(currentUser.uid, 'batch'), data.batchId);
      localStorage.removeItem(paperKey(currentUser.uid, 'pending'));
      if (!isMountedRef.current || getAuth().currentUser?.uid !== currentUser.uid) return;
      selectPaperBatch(data.batchId);
      setStatusMessage("⏳ Processing batch...");

      // Step 5: Poll for batch status
      pollBatchStatus(data.batchId);
    } catch (error) {
      if (!paperActive(paperScope)) return;
      void 0;
      const errorMsg = error.message || "Failed to process batch";
      setBatchError(errorMsg);
      setStatusMessage(`❌ Error: ${errorMsg}`);
      setIsProcessing(false);
    }
  };

  // Fetches the full per-submission results once the batch is complete.
  // Uses cache: "no-store" so we always get fresh data from the server.
  const fetchFullBatchResults = async (batchId) => {
    const paperScope = capturePaperRequest('fetchFullBatchResults', {batchId});
    try {
      const auth = getAuth();
      const currentUser = auth.currentUser;

      if (!currentUser) {
        throw new Error("Authentication expired");
      }

      const idToken = await currentUser.getIdToken();
      if (!paperActive(paperScope)) return;

      const response = await fetch(`${API_BASE_URL}/api/batch-results/${batchId}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${idToken}`,
        },
        cache: "no-store", // Always fetch fresh — never serve a cached response
      });
      if (!paperActive(paperScope)) return;

      if (!response.ok) {
        const data = await response.json();
      if (!paperActive(paperScope)) return;
        throw new Error(data.error || "Failed to fetch batch results");
      }

      const data = await response.json();
      if (!paperActive(paperScope)) return;
      setFullBatchResults(data.submissions || []);
      if (data.submissions?.[0]?.markingMode) setMarkingMode(data.submissions[0].markingMode);
      void 0;
    } catch (error) {
      if (!paperActive(paperScope)) return;
      void 0;
    }
  };

  const safeExportText = (value) => {
    if (value === null || value === undefined) return "";
    return String(value).replace(/\s+/g, " ").trim();
  };

  

  const safeFilenamePart = (value, fallback = "results") => (
    safeExportText(value || fallback)
      .replace(/[^a-zA-Z0-9_\- ]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .substring(0, 60) || fallback
  );

  const getExportDateStamp = () => new Date().toISOString().slice(0, 10);

  const getSubmissionSummary = (submission) => {
    const summary = submission?.markingSummary || {};
    if (summary.provisional || ['failed','needs_review'].includes(submission?.status)) return {
      totalAwarded: 'Pending (' + (summary.knownAwarded || 0) + ' known marks)',
      totalPossible: Number(summary.totalPossible) || 0, percentage: null, provisional: true, knownAwarded: Number(summary.knownAwarded) || 0, unresolvedCount: summary.unresolvedCount || 0
    };
    const totalAwarded = Number(summary.totalAwarded ?? submission?.totalAwarded ?? 0);
    const totalPossible = Number(summary.totalPossible ?? submission?.totalPossible ?? 0);
    const rawPercentage = summary.percentage ?? submission?.percentage;
    const percentage = Number.isFinite(Number(rawPercentage))
      ? Number(rawPercentage)
      : totalPossible > 0
        ? Math.round((totalAwarded / totalPossible) * 100)
        : 0;

    return {
      totalAwarded: Number.isFinite(totalAwarded) ? totalAwarded : 0,
      totalPossible: Number.isFinite(totalPossible) ? totalPossible : 0,
      percentage: Number.isFinite(percentage) ? Math.round(percentage) : 0
    };
  };

  const formatExportPercentage = (value) => {
    if (value == null) return "Pending review";
    const num = Number(value);
    return Number.isFinite(num) ? `${Math.round(num)}%` : "";
  };

  const getQuickQuestionResults = (submission) => (
    submission.questionResults ||
    submission.perQuestionResults ||
    submission.markedResults ||
    []
  );

  const getPreparedQuickMarkSubmissions = (ranked = false) => {
    const submissions = Array.isArray(fullBatchResults) ? [...fullBatchResults] : [];
    const hasBatchQuickMark = submissions.some((submission) => submission.batchQuickMark);

    return submissions
      .map((submission, index) => ({
        ...submission,
        _exportIndex: index,
        _summary: getSubmissionSummary(submission)
      }))
      .sort((a, b) => {
        if (a._summary.percentage == null && b._summary.percentage != null) return 1;
        if (b._summary.percentage == null && a._summary.percentage != null) return -1;
        if (ranked) {
          const pctDiff = (b._summary.percentage || 0) - (a._summary.percentage || 0);
          if (pctDiff !== 0) return pctDiff;
          const scoreDiff = (b._summary.totalAwarded || 0) - (a._summary.totalAwarded || 0);
          if (scoreDiff !== 0) return scoreDiff;
          return safeExportText(a.studentName).localeCompare(safeExportText(b.studentName));
        }

        if (hasBatchQuickMark) {
          const aPage = Number.isInteger(a.pageStart) ? a.pageStart : Number.MAX_SAFE_INTEGER;
          const bPage = Number.isInteger(b.pageStart) ? b.pageStart : Number.MAX_SAFE_INTEGER;
          if (aPage !== bPage) return aPage - bPage;
          return a._exportIndex - b._exportIndex;
        }

        const pctDiff = (b._summary.percentage || 0) - (a._summary.percentage || 0);
        if (pctDiff !== 0) return pctDiff;
        return a._exportIndex - b._exportIndex;
      });
  };

  const getQuickMarkBandSummary = (percentage) => {
    if (percentage == null) return "Provisional: unresolved work needs review.";
    if (percentage >= 80) return "Strong overall performance.";
    if (percentage >= 60) return "Secure performance with some areas to review.";
    if (percentage >= 40) return "Developing performance; review key misconceptions.";
    return "Needs support; review key gaps.";
  };

  const createDocParagraph = (text, options = {}) => {
    const { textRun, children, ...paragraphOptions } = options;
    return new Paragraph({
      ...paragraphOptions,
      children: children || [new TextRun({ text: safeExportText(text), ...(textRun || {}) })]
    });
  };

  const DOC_COLORS = {
    navy: "172A3A",
    muted: "64748B",
    teal: "1F6F9F",
    tealSoft: "EAF8FB",
    tealBorder: "BDE7EF",
    greySoft: "F8FAFC",
    greyBorder: "D9E4EA",
    amberSoft: "FFFBEB",
    amberBorder: "FDE68A",
    amberText: "92400E",
    white: "FFFFFF"
  };

  const DOC_BORDER = { style: BorderStyle.SINGLE, size: 1, color: DOC_COLORS.greyBorder };
  const DOC_NO_BORDER = { style: BorderStyle.NONE, size: 0, color: DOC_COLORS.white };

  const cleanTeacherFacingText = (value) => (
    safeExportText(value)
      .replace(/\[Moderated:[^\]]*\]/gi, "")
      .replace(/\bOCR fallback\b/gi, "Reading confidence: lower")
      .replace(/\bfallback mode\b/gi, "review mode")
      .replace(/\bfallback\b/gi, "review")
      .replace(/\bextracted text\b/gi, "answer preview")
      .replace(/\s+/g, " ")
      .trim()
  );

  const truncateForWordPreview = (value, maxLength = 520) => {
    const cleaned = cleanTeacherFacingText(value);
    if (cleaned.length <= maxLength) return cleaned;
    return `${cleaned.slice(0, maxLength).trim()}... Full response available in the student breakdown/PDF.`;
  };

  const createDocCell = (text, options = {}) => new TableCell({
    width: options.width ? { size: options.width, type: WidthType.PERCENTAGE } : undefined,
    shading: options.shading ? { type: ShadingType.CLEAR, fill: options.shading } : undefined,
    margins: {
      top: options.tight ? 80 : 120,
      bottom: options.tight ? 80 : 120,
      left: 120,
      right: 120
    },
    verticalAlign: VerticalAlign.CENTER,
    borders: options.borders,
    children: [
      new Paragraph({
        alignment: options.alignment,
        spacing: { before: 0, after: 0 },
        children: [
          new TextRun({
            text: cleanTeacherFacingText(text),
            bold: options.bold || false,
            color: options.color || DOC_COLORS.navy,
            size: options.size || 20
          })
        ]
      })
    ]
  });

  const createDocTable = (headers, rows, options = {}) => new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    columnWidths: options.columnWidths,
    margins: { top: 120, bottom: 120, left: 120, right: 120 },
    borders: {
      top: DOC_BORDER,
      bottom: DOC_BORDER,
      left: DOC_BORDER,
      right: DOC_BORDER,
      insideHorizontal: DOC_BORDER,
      insideVertical: DOC_BORDER
    },
    rows: [
      new TableRow({
        tableHeader: true,
        children: headers.map((header, index) => createDocCell(header, {
          bold: true,
          shading: options.headerFill || DOC_COLORS.tealSoft,
          color: options.headerText || DOC_COLORS.navy,
          alignment: options.alignments?.[index] || AlignmentType.LEFT,
          width: options.widths?.[index],
          size: 19
        }))
      }),
      ...rows.map((row, rowIndex) => new TableRow({
        cantSplit: true,
        children: row.map((cell, index) => createDocCell(cell, {
          shading: options.alternateRows && rowIndex % 2 === 1 ? DOC_COLORS.greySoft : undefined,
          alignment: options.alignments?.[index] || AlignmentType.LEFT,
          width: options.widths?.[index],
          size: options.bodySize || 19
        }))
      }))
    ]
  });

  const createDocHeading = (text, level = HeadingLevel.HEADING_1) => new Paragraph({
    text,
    heading: level,
    spacing: { before: 260, after: 120 }
  });

  const createCalloutTable = (text, options = {}) => new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    borders: {
      top: { style: BorderStyle.SINGLE, size: 1, color: options.border || DOC_COLORS.amberBorder },
      bottom: { style: BorderStyle.SINGLE, size: 1, color: options.border || DOC_COLORS.amberBorder },
      left: { style: BorderStyle.SINGLE, size: 8, color: options.accent || DOC_COLORS.amberText },
      right: { style: BorderStyle.SINGLE, size: 1, color: options.border || DOC_COLORS.amberBorder },
      insideHorizontal: DOC_NO_BORDER,
      insideVertical: DOC_NO_BORDER
    },
    rows: [
      new TableRow({
        cantSplit: true,
        children: [
          createDocCell(text, {
            shading: options.fill || DOC_COLORS.amberSoft,
            color: options.textColor || DOC_COLORS.amberText,
            borders: {
              top: DOC_NO_BORDER,
              bottom: DOC_NO_BORDER,
              left: DOC_NO_BORDER,
              right: DOC_NO_BORDER
            },
            size: 20
          })
        ]
      })
    ]
  });

  const createStatsTable = ({ scriptsMarked, averageScore, highest, lowest }) => createDocTable(
    ["Scripts processed", "Average (final scores)", "Highest (final)", "Lowest (final)"],
    [[
      scriptsMarked,
      formatExportPercentage(averageScore),
      highest ? `${highest.totalAwarded}/${highest.totalPossible}` : "",
      lowest ? `${lowest.totalAwarded}/${lowest.totalPossible}` : ""
    ]],
    {
      widths: [25, 25, 25, 25],
      alignments: [AlignmentType.CENTER, AlignmentType.CENTER, AlignmentType.CENTER, AlignmentType.CENTER],
      headerFill: DOC_COLORS.tealSoft,
      alternateRows: false,
      bodySize: 24
    }
  );

  const createKeyValueTable = (rows, options = {}) => new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    borders: {
      top: DOC_BORDER,
      bottom: DOC_BORDER,
      left: DOC_BORDER,
      right: DOC_BORDER,
      insideHorizontal: DOC_BORDER,
      insideVertical: DOC_BORDER
    },
    rows: rows.map(([label, value], index) => new TableRow({
      cantSplit: true,
      children: [
        createDocCell(label, {
          width: 22,
          bold: true,
          color: options.labelColor || DOC_COLORS.teal,
          shading: index % 2 === 0 ? DOC_COLORS.greySoft : undefined
        }),
        createDocCell(value, {
          width: 78,
          shading: index % 2 === 0 ? DOC_COLORS.greySoft : undefined
        })
      ]
    }))
  });

  const isSingleTaskQuickSummary = (submissions) => (
    isSingleTaskMode ||
    submissions.every((submission) => getQuickQuestionResults(submission).length <= 1)
  );

  const downloadQuickMarkTeacherSummary = async () => {
    const paperScope = capturePaperRequest('downloadQuickMarkTeacherSummary', {});
    if (!Array.isArray(fullBatchResults) || fullBatchResults.length === 0) return;

    const rankedSubmissions = getPreparedQuickMarkSubmissions(true);
    const hasBatchQuickMark = rankedSubmissions.some((submission) => submission.batchQuickMark);
    const isBatchSummary = hasBatchQuickMark || rankedSubmissions.length > 1;
    const rawQuizTitle = selectedQuiz?.label || rankedSubmissions[0]?.quizTitle || "Test";
    const quizTitle = /^scanQuiz_/i.test(rawQuizTitle) ? getFriendlyScanQuizTitle() : rawQuizTitle;
    const markedAt = new Date();
    const classPercentages = rankedSubmissions.map((submission) => submission._summary.percentage).filter(value => typeof value === "number" && Number.isFinite(value));
    const averageScore = classPercentages.length
      ? Math.round(classPercentages.reduce((sum, pct) => sum + pct, 0) / classPercentages.length)
      : null;
    const gradedSummaries = rankedSubmissions.filter(s => !s._summary.provisional);
    const highest = gradedSummaries[0]?._summary;
    const lowest = gradedSummaries[gradedSummaries.length - 1]?._summary;

    const singleTaskSummary = isSingleTaskQuickSummary(rankedSubmissions);
    const teacherNote = "Quick Mark is designed for fast classroom marking. Please review unclear or low-confidence answers before using results formally.";

    const children = [
      new Paragraph({
        children: [
          new TextRun({ text: "StudyDen", bold: true, color: DOC_COLORS.teal, size: 28 })
        ],
        spacing: { after: 60 }
      }),
      new Paragraph({
        children: [
          new TextRun({ text: "Batch Marking Summary", bold: true, color: DOC_COLORS.navy, size: 40 })
        ],
        spacing: { after: 160 },
        border: {
          bottom: { color: DOC_COLORS.tealBorder, space: 8, style: BorderStyle.SINGLE, size: 8 }
        }
      }),
      createKeyValueTable([
        ["Assessment", quizTitle],
        ["Marking mode", "Quick Mark"],
        ["Date marked", markedAt.toLocaleString()],
        ["Scripts processed", rankedSubmissions.length],
        ["Final scores", gradedSummaries.length],
        ["Provisional scripts", rankedSubmissions.length - gradedSummaries.length]
      ]),
      createDocParagraph("", { spacing: { after: 120 } }),
      createStatsTable({
        scriptsMarked: rankedSubmissions.length,
        averageScore,
        highest,
        lowest
      }),
      createDocParagraph("", { spacing: { after: 120 } }),
      createCalloutTable(teacherNote),
      createDocHeading("Class Summary")
    ];

    if (isBatchSummary) {
      let lastPercentage = null;
      let lastRank = 0;
      const classRows = rankedSubmissions.map((submission, index) => {
        const summary = submission._summary;
        const rank = summary.percentage == null ? "Pending" : summary.percentage === lastPercentage ? lastRank : index + 1;
        lastPercentage = summary.percentage;
        lastRank = rank;
        return [
          rank,
          submission.studentName || "",
          submission.detectedName || "",
          `${summary.totalAwarded}/${summary.totalPossible}`,
          formatExportPercentage(summary.percentage),
          getQuickMarkBandSummary(summary.percentage)
        ];
      });

      children.push(createDocTable(
        ["Rank", "Student Name", "Detected Name", "Score", "Percentage", "Status/Notes"],
        classRows,
        {
          widths: [8, 24, 22, 12, 12, 22],
          alignments: [
            AlignmentType.CENTER,
            AlignmentType.LEFT,
            AlignmentType.LEFT,
            AlignmentType.CENTER,
            AlignmentType.CENTER,
            AlignmentType.LEFT
          ],
          headerFill: DOC_COLORS.tealSoft,
          alternateRows: true,
          bodySize: 18
        }
      ));
    } else {
      const submission = rankedSubmissions[0];
      const summary = submission._summary;
      children.push(createDocTable(
        ["Student Name", "Score", "Percentage", "Status/Notes"],
        [[
          submission.studentName || "",
          `${summary.totalAwarded}/${summary.totalPossible}`,
          formatExportPercentage(summary.percentage),
          getQuickMarkBandSummary(summary.percentage)
        ]],
        {
          widths: [34, 18, 18, 30],
          alignments: [AlignmentType.LEFT, AlignmentType.CENTER, AlignmentType.CENTER, AlignmentType.LEFT],
          headerFill: DOC_COLORS.tealSoft,
          bodySize: 18
        }
      ));
    }

    children.push(createDocHeading("Student Breakdown"));

    rankedSubmissions.forEach((submission) => {
      const summary = submission._summary;
      const bandSummary = getQuickMarkBandSummary(summary.percentage);
      const questionResults = getQuickQuestionResults(submission);

      children.push(
        createDocParagraph("", { spacing: { before: 160, after: 0 } }),
        new Table({
          width: { size: 100, type: WidthType.PERCENTAGE },
          borders: {
            top: { style: BorderStyle.SINGLE, size: 1, color: DOC_COLORS.tealBorder },
            bottom: { style: BorderStyle.SINGLE, size: 1, color: DOC_COLORS.tealBorder },
            left: { style: BorderStyle.SINGLE, size: 6, color: DOC_COLORS.teal },
            right: { style: BorderStyle.SINGLE, size: 1, color: DOC_COLORS.tealBorder },
            insideHorizontal: DOC_NO_BORDER,
            insideVertical: DOC_NO_BORDER
          },
          rows: [
            new TableRow({
              cantSplit: true,
              children: [
                createDocCell(submission.studentName || "Student", {
                  width: 36,
                  bold: true,
                  shading: DOC_COLORS.tealSoft,
                  color: DOC_COLORS.navy,
                  size: 24,
                  borders: { top: DOC_NO_BORDER, bottom: DOC_NO_BORDER, left: DOC_NO_BORDER, right: DOC_NO_BORDER }
                }),
                createDocCell(`Score: ${summary.totalAwarded}/${summary.totalPossible} | ${formatExportPercentage(summary.percentage)} | ${bandSummary}`, {
                  width: 64,
                  shading: DOC_COLORS.tealSoft,
                  color: DOC_COLORS.teal,
                  alignment: AlignmentType.RIGHT,
                  size: 19,
                  borders: { top: DOC_NO_BORDER, bottom: DOC_NO_BORDER, left: DOC_NO_BORDER, right: DOC_NO_BORDER }
                })
              ]
            })
          ]
        })
      );

      if (singleTaskSummary) {
        const result = questionResults[0] || {};
        children.push(createKeyValueTable([
          ["Mark", paperQuestionMarkText(result)],
          ["Status", paperQuestionStatus(result)],
          ["Answer Preview", truncateForWordPreview(result.visibleAnswerSummary || result.studentAnswerSummary || result.userAnswer || submission.answerText || "")],
          ["Feedback", cleanTeacherFacingText(result.shortReason || result.reason || result.feedback || "")]
        ], { labelColor: DOC_COLORS.teal }));
      } else {
        const questionRows = questionResults.map((result, index) => [
          result.questionLabel || result.questionNumber || `Q${index + 1}`,
          paperQuestionMarkText(result),
          paperQuestionStatus(result),
          truncateForWordPreview(result.visibleAnswerSummary || result.studentAnswerSummary || result.userAnswer || "", 420),
          cleanTeacherFacingText(result.shortReason || result.reason || result.feedback || "")
        ]);

        children.push(createDocTable(
          ["Question", "Mark", "Status", "Answer Preview", "Feedback"],
          questionRows.length ? questionRows : [["", "", "", "", ""]],
          {
            widths: [12, 10, 14, 32, 32],
            alignments: [
              AlignmentType.CENTER,
              AlignmentType.CENTER,
              AlignmentType.CENTER,
              AlignmentType.LEFT,
              AlignmentType.LEFT
            ],
            headerFill: DOC_COLORS.greySoft,
            alternateRows: true,
            bodySize: 17
          }
        ));
      }
    });

    children.push(
      createDocHeading("Suggested Teacher Follow-up"),
      createDocParagraph("Review any low-confidence or unclear responses.", { bullet: { level: 0 } }),
      createDocParagraph("Spot-check results before using them formally.", { bullet: { level: 0 } }),
      createDocParagraph("Use the class summary to identify students who may need support.", { bullet: { level: 0 } })
    );

    if (singleTaskSummary) {
      children.push(
        createDocParagraph("Revisit common gaps in task coverage, detail, and accuracy.", { bullet: { level: 0 } }),
        createDocParagraph("Encourage students to expand comparisons and justify opinions.", { bullet: { level: 0 } }),
        createDocParagraph("Practise key vocabulary or sentence structures linked to the task.", { bullet: { level: 0 } })
      );
    }

    children.push(createDocParagraph(`StudyDen - generated ${markedAt.toLocaleString()}`, {
      alignment: AlignmentType.CENTER,
      spacing: { before: 320 },
      textRun: { color: DOC_COLORS.muted, size: 18 }
    }));

    const doc = new Document({
      sections: [{
        properties: {},
        footers: {
          default: new Footer({
            children: [
              createDocParagraph(`StudyDen - generated ${markedAt.toLocaleDateString()}`, {
                alignment: AlignmentType.CENTER,
                textRun: { color: DOC_COLORS.muted, size: 16 }
              })
            ]
          })
        },
        children
      }]
    });

    const blob = await Packer.toBlob(doc);
      if (!paperActive(paperScope)) return;
    const date = getExportDateStamp();
    const safeQuiz = safeFilenamePart(quizTitle, "test");
    const safeStudent = safeFilenamePart(rankedSubmissions[0]?.studentName || "student", "student");
    const filename = isBatchSummary
      ? `batch-quick-mark-summary-${safeQuiz}-${date}.docx`
      : `quick-mark-summary-${safeStudent}-${safeQuiz}-${date}.docx`;
    saveAs(blob, filename);
  };

  const downloadQuickMarkCsv = () => {
    if (!Array.isArray(fullBatchResults) || fullBatchResults.length === 0) return;

    const preparedSubmissions = getPreparedQuickMarkSubmissions(false);
    const hasBatchQuickMark = preparedSubmissions.some(submission => submission.batchQuickMark);
    const questionLabels = [];
    const seenLabels = new Set();
    preparedSubmissions.forEach((submission) => {
      getQuickQuestionResults(submission).forEach((result, index) => {
        const label = result.questionLabel || result.questionNumber || `Q${index + 1}`;
        if (!seenLabels.has(label)) {
          seenLabels.add(label);
          questionLabels.push(label);
        }
      });
    });

    const headers = [
      "Student Name",
      "Student ID",
      ...(hasBatchQuickMark ? ["Detected Name"] : []),
      "Test Title",
      "Total Awarded",
      "Total Possible",
      "Percentage", "Known Awarded", "Unresolved Questions", "Result Status"
    ];

    questionLabels.forEach((label) => {
      headers.push(`${label} Mark`, `${label} Max`, `${label} Status`, `${label} Answer`, `${label} Reason`);
    });

    const rows = preparedSubmissions.map((submission) => {
      const summary = submission._summary || getSubmissionSummary(submission);
      const row = [
        submission.studentName || "",
        submission.studentId || "",
        ...(hasBatchQuickMark ? [submission.detectedName || ""] : []),
        /^scanQuiz_/i.test(selectedQuiz?.label || submission.quizTitle || "")
          ? getFriendlyScanQuizTitle()
          : selectedQuiz?.label || submission.quizTitle || "",
        summary.provisional ? "" : summary.totalAwarded,
        summary.totalPossible,
        formatExportPercentage(summary.percentage),
        summary.provisional ? summary.knownAwarded : summary.totalAwarded,
        summary.unresolvedCount || 0,
        summary.provisional ? "Provisional" : "Final"
      ];

      const questionResults = getQuickQuestionResults(submission);
      const resultsByLabel = new Map();
      questionResults.forEach((result, index) => {
        const label = result.questionLabel || result.questionNumber || `Q${index + 1}`;
        resultsByLabel.set(label, result);
      });

      questionLabels.forEach((label) => {
        const result = resultsByLabel.get(label) || {};
        row.push(
          result.markAwarded ?? "",
          result.maxMark ?? "",
          paperQuestionStatus(result),
          result.visibleAnswerSummary || result.studentAnswerSummary || result.userAnswer || "",
          result.shortReason || result.feedback || ""
        );
      });
      return row;
    });

    const csv = [headers, ...rows]
      .map((row) => row.map(csvCell).join(","))
      .join("\n");

    const rawQuizTitle = selectedQuiz?.label || preparedSubmissions[0]?.quizTitle || "quiz";
    const exportQuizTitle = /^scanQuiz_/i.test(rawQuizTitle) ? getFriendlyScanQuizTitle() : rawQuizTitle;
    const safeQuiz = safeFilenamePart(exportQuizTitle, "quiz");
    const date = getExportDateStamp();
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${hasBatchQuickMark ? "batch-" : ""}quick-mark-results-${safeQuiz}-${date}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  // ===== BATCH STATUS POLLING =====
  // Uses recursive setTimeout (not setInterval) so each poll decides whether
  // the next delay should be 3 s, 5 s, or 30 s based on elapsed time.
  // This guarantees there is never more than one pending poll at a time.
  //
  // Lifecycle:
  //   • Called once when the backend returns a batchId.
  //   • Schedules its own next invocation via pollTimeoutRef.current.
  //   • Stops when status is completed/partially_completed/failed.
  //   • Stopped on unmount via the cleanup useEffect (pollTimeoutRef cleared).
  //   • isMountedRef.current guards against state updates after unmount.
  const pollBatchStatus = (batchId) => {
    if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
    const epoch = ++pollEpochRef.current;
    const ownerUid = getAuth().currentUser?.uid;
    const pollScope = capturePaperRequest('poll', {batchId});
    const active = () => paperActive(pollScope) && epoch === pollEpochRef.current && getAuth().currentUser?.uid === ownerUid;
    let consecutiveErrors = 0;
    pollStartTimeRef.current = Date.now();

    const doPoll = async () => {
    const paperScope = capturePaperRequest('doPoll', {});
      // Bail out silently if the component has already unmounted
      if (!active()) return;

      try {
        const auth = getAuth();
        const currentUser = auth.currentUser;

        if (!currentUser) {
          if (!active()) return;
          const errorMsg = "Authentication expired. Please refresh and try again.";
          setBatchError(errorMsg);
          setStatusMessage(`❌ Error: ${errorMsg}`);
          setIsProcessing(false);
          return; // Stop polling
        }

        const idToken = await currentUser.getIdToken();
      if (!paperActive(paperScope)) return;
        const elapsedMs = Date.now() - (pollStartTimeRef.current || Date.now());

        void 0;

        const response = await fetch(`${API_BASE_URL}/api/batch-status/${batchId}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${idToken}`,
          },
          cache: "no-store", // Always fetch fresh — never serve a cached response
        });
      if (!paperActive(paperScope)) return;

        if (!active()) return;

        if (!response.ok) {
          if (response.status >= 500 || response.status === 429) throw new Error("The connection was interrupted. Saved work is retained.");
          const errData = await response.json().catch(() => ({}));
      if (!paperActive(paperScope)) return;
          const errorMsg = errData.error || "Failed to fetch batch status";
          void 0;
          setBatchError(errorMsg);
          setStatusMessage(`❌ Error: ${errorMsg}`);
          setIsProcessing(false);
          return; // Stop polling on HTTP error
        }

        const data = await response.json();
      if (!paperActive(paperScope)) return;
        if (!active()) return;
        consecutiveErrors = 0;

        if (!active()) return;

        void 0;
        void 0;

        setProcessingProgress({
          completed: data.completed || 0,
          failed: data.failed || 0,
          processing: data.processing || 0,
          pending: data.pending || 0,
        });
        setSubmissionStatuses(data.submissionStatuses || {});

        // ── Completion detection ──────────────────────────────────────────────
        if (
          data.status === "completed" ||
          data.status === "partially_completed" ||
          data.status === "failed"
        ) {
          void 0;
          setIsProcessing(false);
          setBatchResults(data);
          setStatusMessage("");
          // Fetch the detailed per-submission results (marks, PDF links, etc.)
          await fetchFullBatchResults(batchId);
      if (!paperActive(paperScope)) return;
          return; // Do NOT schedule another poll
        }

        // ── Still processing — pick delay based on how long we've waited ──────
        const FIVE_MIN_MS = 5 * 60 * 1000;
        const TEN_MIN_MS  = 10 * 60 * 1000;

        let nextDelayMs = 3000; // default: 3 seconds
        const expectedCount = batchQuickMarkMode === "batch" ? batchScriptGroups.length : selectedStudents.length;
        let nextMessage = `⏳ Processing: ${data.completed || 0}/${expectedCount} students completed...`;

        if (elapsedMs >= TEN_MIN_MS) {
          nextDelayMs = 30000; // 30 seconds — heavily slow down after 10 min
          nextMessage =
            "⏳ This script is still processing in the background. You can refresh this page or check back shortly.";
          void 0;
        } else if (elapsedMs >= FIVE_MIN_MS) {
          nextDelayMs = 5000; // 5 seconds — moderate slowdown after 5 min
          nextMessage =
            "⏳ This is taking longer than expected. Large scripts may continue processing in the background.";
          void 0;
        }

        if (!active()) return;
        setStatusMessage(nextMessage);

        // Schedule the next poll — single timeout, no overlapping intervals
        pollTimeoutRef.current = setTimeout(doPoll, nextDelayMs);

      } catch (error) {
      if (!paperActive(paperScope)) return;
        void 0;
        if (!active()) return;
        consecutiveErrors++;
        if (consecutiveErrors <= 6) {
          setStatusMessage("Connection interrupted. Reconnecting to your saved batch?");
          pollTimeoutRef.current = setTimeout(doPoll, Math.min(30000, 2000 * 2 ** consecutiveErrors));
          return;
        }
        const errorMsg = error.message || "Failed to check batch status";
        setBatchError(errorMsg);
        setStatusMessage(`❌ Error: ${errorMsg}`);
        setIsProcessing(false);
        // Stop polling — do not schedule another call
      }
    };

    // Kick off the very first poll after a 1-second initial delay
    pollTimeoutRef.current = setTimeout(doPoll, 1000);
  };

  const handleRetry = () => {
    invalidatePaperWork();
    if (user?.uid) { localStorage.removeItem(paperKey(user.uid, 'batch')); localStorage.removeItem(paperKey(user.uid, 'pending')); }
    setFullBatchResults(null);
    setCurrentStep(1);
    setSetupMode("existingQuiz");
    setSelectedQuiz(null);
    setSelectedStudents([]);
    setStudentImages({});
    setCurrentUploadingStudent(null);
    setMarkingMode("standard");
    selectPaperBatch(null);
    setProcessingProgress({ completed: 0, failed: 0, processing: 0, pending: 0 });
    setSubmissionStatuses({});
    setStatusMessage("");
    setBatchResults(null);
    setBatchError(null);
  };

  const handleGoBack = () => {
    invalidatePaperWork();
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  // ===== QR UPLOAD HANDLER =====

  /**
   * Starts a Firestore onSnapshot listener for a QR upload session.
   * When imageUrls change, new URLs are merged into the student's mobileImages
   * array using a Set-based dedup so the same URL is never added twice.
   * The previous listener (if any) is automatically unsubscribed first.
   */
  const startQrListener = (sessionId, studentId) => {
    const qrScope = capturePaperRequest('qr-listener');
    // Tear down any previous listener before starting a new one
    if (qrListenerUnsubRef.current) {
      qrListenerUnsubRef.current();
      qrListenerUnsubRef.current = null;
    }

    const sessionRef = doc(db, "paperUploadSessions", sessionId);
    const unsub = onSnapshot(sessionRef, (snap) => {
      if (!paperActive(qrScope)) return;
      if (!snap.exists()) return;

      const data = snap.data();
      const incomingUrls = (data.images || []).map(image => image.objectPath);
      if (incomingUrls.length === 0) return;

      setStudentImages((prev) => {
        const current = prev[studentId];
        if (!current) return prev; // student was removed — skip

        // Build a Set of URLs already in mobileImages to prevent duplicates
        const existingUrls = new Set(
          (current.mobileImages || []).map((m) => m.url)
        );

        const newEntries = incomingUrls
          .filter((url) => !existingUrls.has(url))
          .map((url, i) => ({
            url, previewUrl: data.images?.find(image => image.objectPath === url)?.previewUrl,
            // Name shown in the image list — includes sequential number across all mobileImages
            name: `📱 Phone page ${(current.mobileImages || []).length + i + 1}`,
          }));

        if (newEntries.length === 0) return prev; // nothing new — no re-render

        return {
          ...prev,
          [studentId]: {
            ...current,
            mobileImages: [...(current.mobileImages || []), ...newEntries],
          },
        };
      });
    }, (err) => {
      void 0;
    });

    qrListenerUnsubRef.current = unsub;
    setActiveQrSession({ sessionId, studentId });
  };

  /** Tears down the active QR listener and clears session tracking state. */
  const stopQrListener = () => {
    if (qrListenerUnsubRef.current) {
      qrListenerUnsubRef.current();
      qrListenerUnsubRef.current = null;
    }
    setActiveQrSession(null);
  };

  const handleOpenQrUpload = async (student, studentIndex) => {
    const paperScope = capturePaperRequest('handleOpenQrUpload', {});
    try {
      setQrModalLoading(true);
      const session = await createPaperUploadSession({
        teacherUid: user.uid,
        quizId: selectedQuiz.value,
        quizTitle: selectedQuiz.label,
        studentId: student.value,
        studentName: student.label,
        studentIndex: studentIndex,
        batchId: null,
      });
      if (!paperActive(paperScope)) return;
      setQrModalSessionId(session.sessionId); setQrCapability(session.token);
      setQrModalStudentName(student.label);
      // Phase 4: start real-time listener so images appear in the upload card automatically
      startQrListener(session.sessionId, student.value);
    } catch (err) {
      if (!paperActive(paperScope)) return;
      void 0;
      alert("Failed to create upload session: " + err.message);
    } finally {
      if (!paperActive(paperScope)) return;
      setQrModalLoading(false);
    }
  };

  const renderQrModal = () => {
    if (!qrModalSessionId) return null;
    const uploadUrl = `${window.location.origin}/mobile-paper-upload/${qrModalSessionId}#${qrCapability}`;

    const handleCloseQrModal = () => {
      const closeScope=capturePaperRequest('qr-close');
      paperRequest('/api/paper-upload-sessions/' + qrModalSessionId + '/revoke', {}).catch(() => {if(paperActive(closeScope))setBatchError('Could not close the phone upload session. It will expire automatically.');});
      setQrCapability('');
      setQrModalSessionId(null);
      // Phase 4: stop the snapshot listener when the modal is closed
      stopQrListener();
    };

    return (
      <div
        className="qr-modal-overlay"
        onClick={handleCloseQrModal}
        role="dialog"
        aria-modal="true"
        aria-label="Phone upload QR code"
      >
        <div className="qr-modal" onClick={(e) => e.stopPropagation()}>
          <button
            className="qr-modal-close"
            onClick={handleCloseQrModal}
            aria-label="Close"
          >
            ✕
          </button>

          <h3 className="qr-modal-title">📱 Upload from phone</h3>
          <p className="qr-modal-subtitle">
            Scan this QR code with your phone to upload images for{" "}
            <strong>{qrModalStudentName}</strong>
          </p>

          <div className="qr-modal-code">
            <QRCodeSVG value={uploadUrl} size={200} level="M" />
          </div>

          <p className="qr-modal-instruction">
            Scan this QR code with your phone to upload images
          </p>

          <div className="qr-modal-url-row">
            <span className="qr-modal-url" title={uploadUrl}>
              {uploadUrl}
            </span>
            <button
              className="qr-modal-copy-btn"
              onClick={() => {
                navigator.clipboard.writeText(uploadUrl).then(() => {
                  alert("Link copied to clipboard!");
                }).catch(() => {
                  alert("Could not copy automatically. URL:\n" + uploadUrl);
                });
              }}
            >
              Copy
            </button>
          </div>

          <p className="qr-modal-expiry-note">
            This session link expires in 30 minutes.
          </p>
        </div>
      </div>
    );
  };

  // ===== RENDER FUNCTIONS =====

  const renderStep1 = () => {
    // Read-only summary of values already held by this page; no new state.
    const summaryTest = setupMode === "existingQuiz"
      ? (selectedQuiz?.label || "")
      : setupMode === "scanExtractedQuiz"
        ? "Built from this paper"
        : "Writing task";
    const summaryUsesScripts = batchQuickMarkMode === "batch";
    const summaryStudentCount = summaryUsesScripts ? batchScriptGroups.length : selectedStudents.length;
    const summaryPageCount = getTotalPageCount();
    const summaryRows = [
      { label: "Assessment", value: getAssessmentSourceLabel(), icon: ClipboardCheck },
      { label: "Test", value: summaryTest, icon: FileText },
      {
        label: "Students",
        value: summaryStudentCount
          ? `${summaryStudentCount} ${summaryUsesScripts ? "script" : "student"}${summaryStudentCount !== 1 ? "s" : ""}`
          : "",
        icon: Users
      },
      { label: "Papers", value: summaryPageCount ? `${summaryPageCount} page${summaryPageCount !== 1 ? "s" : ""}` : "", icon: Layers },
      { label: "Marking depth", value: MARKING_MODES[markingMode].label, icon: Gauge }
    ];
    return (
    <div className="ps-step-layout">
    <div className={`batch-step ${routeAccentClass}`}>
      <div className="step-header ps-section-heading">
        <span className="ps-section-icon" aria-hidden="true"><ClipboardCheck size={20} /></span>
        <div className="ps-section-copy">
          <h2>Set Up Assessment</h2>
          <p>Where should the questions and mark scheme come from? You'll choose Quick Mark, Standard or Full Analysis in step 4.</p>
        </div>
      </div>

      <div className="setup-option-grid">
        <label className={`marking-mode-card setup-option-card assessment-card--existing ${setupMode === "existingQuiz" ? "selected" : ""}`}>
          <input
            type="radio"
            name="setupMode"
            checked={setupMode === "existingQuiz"}
            onChange={() => handleSetupModeChange("existingQuiz")}
          />
          <span className="setup-option-top">
            <span className="setup-option-icon" aria-hidden="true">
              <ClipboardList size={22} />
            </span>
            <span className="setup-option-title">Use my existing test</span>
            <span className="setup-option-radio" aria-hidden="true"></span>
          </span>
          <span className="setup-option-copy">The questions and mark scheme are already in My Tests.</span>
          <span className="setup-option-badge"><CircleCheck size={14} aria-hidden="true" />Most reliable</span>
        </label>
        <label className={`marking-mode-card setup-option-card assessment-card--scanned ${setupMode === "scanExtractedQuiz" ? "selected" : ""}`}>
          <input
            type="radio"
            name="setupMode"
            checked={setupMode === "scanExtractedQuiz"}
            onChange={() => handleSetupModeChange("scanExtractedQuiz")}
          />
          <span className="setup-option-top">
            <span className="setup-option-icon" aria-hidden="true">
              <ScanText size={22} />
            </span>
            <span className="setup-option-title">Build a test from this paper</span>
            <span className="setup-option-radio" aria-hidden="true"></span>
          </span>
          <span className="setup-option-copy">We read the printed questions, labels and marks from your scans.</span>
          <span className="setup-option-badge"><Eye size={14} aria-hidden="true" />Review required</span>
        </label>
        <label className={`marking-mode-card setup-option-card assessment-card--single ${setupMode === "singleTask" ? "selected" : ""}`}>
          <input
            type="radio"
            name="setupMode"
            checked={setupMode === "singleTask"}
            onChange={() => handleSetupModeChange("singleTask")}
          />
          <span className="setup-option-top">
            <span className="setup-option-icon" aria-hidden="true">
              <PenLine size={22} />
            </span>
            <span className="setup-option-title">Mark one writing task</span>
            <span className="setup-option-radio" aria-hidden="true"></span>
          </span>
          <span className="setup-option-copy">One essay, paragraph, reflection or explanation.</span>
          <span className="setup-option-badge"><Zap size={14} aria-hidden="true" />Fastest setup</span>
        </label>
      </div>

      <div className="ps-option-panel">
        <h3 className="ps-option-panel-title">{getAssessmentSourceLabel()}</h3>
        <p className="ps-option-panel-helper">
          {setupMode === "existingQuiz"
            ? "StudyDen will use your existing test/question set and mark scheme. This is the most reliable option when the assessment has already been created."
            : isSingleTaskMode
              ? "StudyDen will create one task/rubric from the printed prompt. Best when students answered one main task rather than a numbered test."
              : "StudyDen will try to create the question structure from the scanned paper. Best for numbered papers such as 1(a), 1(b), 2(c), etc. You will review and edit the questions and marks before marking."}
        </p>
        {setupMode === "scanExtractedQuiz" && (
          <div className="ps-callout ps-callout--amber">
            <Info size={16} aria-hidden="true" />
            <span>If this is just one writing prompt, choose "Mark one writing task" instead.</span>
          </div>
        )}

      {setupMode === "existingQuiz" && (
        <div className="form-group">
          <label htmlFor="quiz">Select test</label>
          <Select
            id="quiz"
            value={selectedQuiz}
            onChange={handleQuizSelect}
            options={quizzes}
            isLoading={loadingQuizzes}
            isSearchable={true}
            placeholder="Search tests..."
            className="custom-react-select"
            classNamePrefix="react-select"
            isClearable
            menuPortalTarget={document.body}
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
          />
        </div>
      )}

      {setupMode === "existingQuiz" && selectedQuiz && (
        <div className="selection-summary">
          <p>
            <strong>Selected test:</strong> {selectedQuiz.label}
          </p>
        </div>
      )}
      </div>

      <div className="step-actions ps-footer">
        <p className="ps-footer-note">Next: choose the class or students who sat this paper</p>
        <div className="ps-footer-actions">
          <button className="secondary-button" onClick={() => navigate("/dashboard")}>
            Cancel
          </button>
          <button
            className="primary-button"
            onClick={proceedToStudentSelection}
            disabled={setupMode === "existingQuiz" && !selectedQuiz}
          >
            {isScanExtractedQuizMode ? "Continue to Students" : "Continue to Students"}
            <ArrowRight size={17} aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>
    <aside className="ps-side" aria-label="Submission summary and help">
      <section className="ps-side-card">
        <PanelHeading icon={Target} title="Submission Summary" />
        <ul className="ps-summary-list">
          {summaryRows.map(({ label, value, icon: RowIcon }) => (
            <li key={label} className="ps-summary-row">
              <span className="ps-summary-icon" aria-hidden="true"><RowIcon size={17} /></span>
              <span className="ps-summary-copy">
                <span className="ps-summary-label">{label}</span>
                <span className={`ps-summary-value${value ? "" : " ps-summary-empty"}`}>{value || "Not set"}</span>
              </span>
            </li>
          ))}
        </ul>
      </section>
      <section className="ps-side-card">
        <PanelHeading icon={CircleHelp} title="Which option fits?" />
        <ul className="ps-fit-list">
          <li><span className="ps-fit-dot ps-fit-dot--blue" aria-hidden="true" />Test already in My Tests? Use my existing test.</li>
          <li><span className="ps-fit-dot ps-fit-dot--orange" aria-hidden="true" />Numbered questions, no test yet? Build a test from this paper.</li>
          <li><span className="ps-fit-dot ps-fit-dot--purple" aria-hidden="true" />One essay or written answer? Mark one writing task.</li>
        </ul>
      </section>
    </aside>
    </div>
    );
  };

  const renderStep2 = () => {
    const { maxStudents, maxPages, label: limitLabel } = getPaperSubmissionLimits(userRole, teacherStatus, fullAnalysisEnabled);
    const atLimit = selectedStudents.length >= maxStudents;
    const canContinueToUpload = selectedStudents.length > 0 || useDetectedNamesFromPdf;
    const limitCopy = `Batch limits: up to ${maxStudents} students ${fullAnalysisEnabled ? "with Full Analysis" : "without Full Analysis"} and ${maxPages} pages per batch.`;
    return (
    <div className={`batch-step ${routeAccentClass}`}>
      <div className="step-header ps-section-heading">
        <span className="ps-section-icon" aria-hidden="true"><Users size={20} /></span>
        <div className="ps-section-copy">
        <h2>Step 2: Review Students</h2>
        <p>
          StudyDen will try to read student names from the uploaded scripts. You can edit names or assign them to students from your class list before marking.
        </p>
        </div>
      </div>

      {/* Batch limit info banner */}
      <div className="student-limit-banner" style={{
        backgroundColor: "#f0f9ff",
        border: "1px solid #bae6fd",
        borderRadius: "8px",
        padding: "0.75rem 1rem",
        marginBottom: "1.25rem",
        fontSize: "0.9rem",
        color: "#0369a1"
      }}>
        <span className="student-limit-icon" aria-hidden="true">i</span>
        <span>{limitCopy}</span>
        {false && (
          <>
        ℹ️ {limitLabel}
          </>
        )}
      </div>

      <label
        className={`student-detection-card ${useDetectedNamesFromPdf ? "selected" : ""}`}
      >
        <input
          type="checkbox"
          checked={useDetectedNamesFromPdf}
          onChange={(e) => activateDetectedNamesFromPdf(e.target.checked)}
        />
        <span className="student-detection-top">
          <span className="student-detection-icon" aria-hidden="true">
            <Users size={21} />
          </span>
          <span className="student-detection-title">Read names from uploaded scripts</span>
          <span className="student-detection-radio" aria-hidden="true"></span>
        </span>
        <span className="student-detection-copy">
          Recommended for combined PDFs. StudyDen will detect cover/name pages and fill in student names automatically.
        </span>
        {useDetectedNamesFromPdf && (
          <span className="student-detection-note">
            You can review and edit detected names before marking.
          </span>
        )}
      </label>

      {useDetectedNamesFromPdf ? (
        <div className="student-selected-summary">
          <span className="student-selected-icon" aria-hidden="true">
            <CircleCheck size={19} />
          </span>
          <div>
            <h3 style={{ marginTop: 0 }}>Next: upload your combined PDF</h3>
            <p>
              StudyDen will detect scripts, names, and page ranges, then show a review screen before marking.
            </p>
          </div>
        </div>
      ) : (
        <>
      {/* Empty state when no students are linked to the teacher */}
      {!loadingStudents && students.length === 0 && (
        <div style={{
          backgroundColor: "#f0f4ff",
          border: "1px solid #c7d2fe",
          borderRadius: "8px",
          padding: "2rem",
          marginBottom: "2rem",
          textAlign: "center"
        }}>
          <h3 style={{ color: "#4f46e5", marginTop: 0 }}>No students are linked to your teacher account yet</h3>
          <p style={{ color: "#6b7280", marginBottom: "0.5rem" }}>
            Students need to join your teacher account or one of your classes before they can be selected for paper submissions.
          </p>
          <p style={{ color: "#9ca3af", fontSize: "0.9rem", marginBottom: 0 }}>
            You can still manually enter student names below, or have students join using your teacher code.
          </p>
        </div>
      )}

      <div className="form-group">
        <label htmlFor="student-select">Assign students from class list</label>
        {students.length === 0 && !loadingStudents ? (
          <div style={{
            padding: "1rem",
            backgroundColor: "#f9fafb",
            border: "1px solid #e5e7eb",
            borderRadius: "4px",
            color: "#6b7280",
            textAlign: "center"
          }}>
            No linked students available. Use the form below to add student names manually.
          </div>
        ) : (
          <Select
            id="student-select"
            options={students.filter(
              (s) => !selectedStudents.find((sel) => sel.value === s.value)
            )}
            onChange={handleAddStudent}
            isLoading={loadingStudents}
            isSearchable={true}
            placeholder="Search students..."
            className="custom-react-select"
            classNamePrefix="react-select"
            isClearable
            menuPortalTarget={document.body}
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
          />
        )}
      </div>

      <div className="form-group">
        <label htmlFor="manual-name">Or manually enter names</label>
        <div className="manual-entry-row">
          <input
            id="manual-name"
            type="text"
            value={manualStudentName}
            onChange={(e) => setManualStudentName(e.target.value)}
            placeholder="Type name..."
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleAddManualStudent();
              }
            }}
          />
          <button className="secondary-button" onClick={handleAddManualStudent}>
            Add
          </button>
        </div>
      </div>

      <div className="student-list-section">
        <h3>
          Selected Students ({selectedStudents.length}/{maxStudents})
          {atLimit && (
            <span className="limit-warning">Maximum reached</span>
          )}
        </h3>
        {selectedStudents.length === 0 ? (
          <p className="empty-state">
            {useDetectedNamesFromPdf
              ? "No students selected. Detected names from the combined PDF will be used automatically."
              : "No students selected yet. Add students above to begin."}
          </p>
        ) : (
          <div className="student-chips">
            {selectedStudents.map((student) => (
              <div key={student.value} className="student-chip">
                <span>{student.label}</span>
                <button
                  className="remove-chip"
                  onClick={() => handleRemoveStudent(student.value)}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
        </>
      )}

      <div className="step-actions">
        <button
          className="primary-button"
          onClick={proceedToImageUpload}
          disabled={!canContinueToUpload}
        >
          Continue to Upload Papers
        </button>
        <button className="secondary-button" onClick={handleGoBack}>
          Back
        </button>
      </div>

      {/* Inline error for student limit */}
      {studentLimitError && (
        <div style={{
          marginTop: "0.75rem",
          backgroundColor: "#fef2f2",
          border: "1px solid #fca5a5",
          borderRadius: "6px",
          padding: "0.6rem 1rem",
          color: "#b91c1c",
          fontSize: "0.9rem"
        }}>
          ⚠️ {studentLimitError}
        </div>
      )}
    </div>
  );
  };

  const renderExtractedQuestionReview = () => {
    if (!isScanExtractedQuizMode) return null;
    const totalMarks = getExtractedTotalMarks();
    const sourcePages = questionExtractionSource?.sourcePageNumbers || extractionDiagnostics?.sourcePageNumbers || [];
    const detectedQuestionPatternCount = getDetectedQuestionPatternCount();
    const diagnosticExtractedQuestionCount = Number(extractionDiagnostics?.extractedQuestionCount);
    const summaryQuestionCount = Number.isFinite(diagnosticExtractedQuestionCount) && diagnosticExtractedQuestionCount >= 0
      ? diagnosticExtractedQuestionCount
      : extractedQuestions.length;
    const diagnosticExtractedTotalMarks = Number(extractionDiagnostics?.extractedTotalMarks);
    const summaryTotalMarks = Number.isFinite(diagnosticExtractedTotalMarks) && diagnosticExtractedTotalMarks >= 0
      ? diagnosticExtractedTotalMarks
      : totalMarks;
    const incompleteExtraction = extractionDiagnostics?.incompleteExtraction === true;
    const looksStructured = detectedQuestionPatternCount >= 2 || extractedQuestions.length >= 2;
    const incompleteWarningText = "StudyDen may have missed some questions. Please review the extracted structure before marking.";
    const diagnosticWarnings = getUniqueWarnings(extractionDiagnostics?.warnings || [])
      .filter(warning => String(warning || "").trim() !== incompleteWarningText);
    const holisticNeedsReview = holisticTaskSuggestion && (
      (Number(holisticTaskSuggestion.confidence) || 0) < 0.65 ||
      holisticTaskSuggestion.likelyStudentResponseAsPrompt ||
      !Array.isArray(holisticTaskSuggestion.numberedPrompts) ||
      holisticTaskSuggestion.numberedPrompts.filter(prompt => String(prompt || "").trim()).length === 0 ||
      extractionDiagnostics?.rejectedGenericPrompts
    );

    return (
      <div className="student-list-summary review-panel extracted-review-panel">
        <h3>{isSingleTaskMode ? "Review Task Prompt" : "Review Assessment Structure"}</h3>
        <p className="credit-note">
          {isSingleTaskMode
            ? "Check that this is the printed task prompt, not a student's response. Edit the task and rubric before marking starts."
            : "Check the labels, wording, marks, and marking notes before marking starts."}
        </p>
        {sourcePages.length > 0 && (
          <p className="credit-note">Question structure source: PDF pages {sourcePages.join(", ")}</p>
        )}
        <div className="selection-summary extraction-summary">
          <p><strong>Source pages used:</strong> {sourcePages.length > 0 ? sourcePages.join(", ") : "Not available"}</p>
          <p><strong>Extracted questions:</strong> {summaryQuestionCount}</p>
          <p><strong>Total extracted marks:</strong> {summaryTotalMarks}</p>
          {detectedQuestionPatternCount > 0 && (
            <p><strong>Visible/detected question patterns:</strong> {detectedQuestionPatternCount}</p>
          )}
        </div>
        {questionExtracting && (
          <div className="question-extraction-loading" role="status" aria-live="polite">
            <span className="paper-loading-spinner" aria-hidden="true"></span>
            <span className="question-extraction-loading-copy">
              <span className="question-extraction-loading-title">
                Extracting question structure from the first detected script...
              </span>
              <span className="question-extraction-loading-subtext">
                This usually takes a few seconds.
              </span>
            </span>
          </div>
        )}
        {questionExtractionError && <div className="mode-warning">{questionExtractionError}</div>}
        {incompleteExtraction && (
          <div className="mode-warning">
            {incompleteWarningText}
          </div>
        )}
        {diagnosticWarnings.map((warning) => (
          <div key={warning} className="mode-warning">{warning}</div>
        ))}
        {looksStructured && markingMode === "quick" && !isSingleTaskMode && (
          <div className="mode-warning">
            This looks like a structured exam paper. Standard Marking is recommended unless you specifically want Quick Mark.
          </div>
        )}
        {(holisticTaskSuggestion?.likelyStudentResponseAsPrompt || extractionDiagnostics?.likelyStudentResponseAsPrompt) && (
          <div className="mode-warning">
            StudyDen may have detected a student response instead of the printed task. Please enter or correct the task prompt before marking.
          </div>
        )}
        {holisticNeedsReview && (
            <div className="mode-warning">
              StudyDen could not confidently read the printed task. Please check the task text before marking.
            </div>
        )}

        {!questionExtracting && extractedQuestions.length === 0 && (
          <div className="step-actions review-actions">
            <div className="mode-selected-summary holistic-empty-card">
              <div>
                <h3 style={{ marginTop: 0 }}>{isSingleTaskMode ? "Create one writing task" : "No separate markable questions were detected"}</h3>
                <p>
                  {isSingleTaskMode || markingMode === "quick"
                    ? "StudyDen can create one editable task/rubric for this script."
                    : "StudyDen could not extract separate questions automatically. Add the questions manually, or switch to Single task / writing response if this is one holistic task."}
                </p>
                {(isSingleTaskMode || markingMode === "quick") && (
                  <div className="writing-task-fallback-actions">
                    <button type="button" className="primary-button" onClick={createHolisticWritingTask}>
                      Create Writing Task
                    </button>
                  </div>
                )}
              </div>
            </div>
            <button type="button" className="secondary-button" onClick={addExtractedQuestion}>
              Add Question Manually
            </button>
          </div>
        )}

        {extractedQuestions.length > 0 && (
          <>
            <div className="form-group review-form-field">
              <label htmlFor="scan-quiz-title">Assessment title</label>
              <input
                id="scan-quiz-title"
                type="text"
                value={extractedQuiz?.quizTitle || getEffectiveQuizTitle()}
                onChange={(e) => {
                  const quizTitle = e.target.value;
                  setExtractedQuiz((prev) => ({ ...(prev || {}), quizTitle }));
                  setExtractedQuestionsConfirmed(false);
                }}
                className="manual-student-input"
              />
            </div>
            <div className="summary-list extracted-question-list">
              {extractedQuestions.map((question, index) => (
                <div key={`${question.questionLabel || "question"}_${index}`} className="extracted-question-card">
                  <div className="extracted-question-header">
                    <div>
                      <h4>{question.questionLabel || `Question ${index + 1}`}</h4>
                      <p>Confidence: {Math.round((Number(question.confidence) || 0) * 100)}%</p>
                    </div>
                    <span className="marks-pill">{question.maxMark || 1} marks</span>
                  </div>
                  <div className="extracted-question-fields">
                    <div className="review-field-row">
                      <label className="review-form-field">
                        <span>Task label</span>
                        <input
                          type="text"
                          value={question.questionLabel || ""}
                          onChange={(e) => updateExtractedQuestion(index, { questionLabel: e.target.value })}
                          placeholder="Label"
                          className="manual-student-input"
                        />
                      </label>
                      <label className="review-form-field marks-field">
                        <span>Marks</span>
                        <input
                          type="number"
                          min="1"
                          value={question.maxMark || 1}
                          onChange={(e) => updateExtractedQuestion(index, { maxMark: e.target.value })}
                          placeholder="Marks"
                          className="manual-student-input"
                        />
                      </label>
                    </div>
                    <label className="review-form-field">
                        <span>{isSingleTaskMode ? "Task prompt" : "Question text"}</span>
                      <textarea
                        value={question.questionText || ""}
                        onChange={(e) => updateExtractedQuestion(index, { questionText: e.target.value })}
                          placeholder={isSingleTaskMode ? "Task prompt" : "Question text"}
                        className="manual-student-input review-textarea"
                        rows={4}
                      />
                    </label>
                    <label className="review-form-field">
                      <span>Rubric / marking notes</span>
                      <textarea
                        value={question.markSchemeAnswer || ""}
                        onChange={(e) => updateExtractedQuestion(index, { markSchemeAnswer: e.target.value })}
                        placeholder="Rubric / marking notes"
                        className="manual-student-input review-textarea rubric-textarea"
                        rows={6}
                      />
                    </label>
                  </div>
                  <div className="script-card-actions">
                    <button className="remove-button compact-remove-button" onClick={() => removeExtractedQuestion(index)}>
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <div className="credit-breakdown review-total-row">
              <div className="credit-row total">
                <span>Total marks</span>
                <span className="credit-cost-total">{totalMarks}</span>
              </div>
            </div>
            <div className="step-actions review-actions">
              <button type="button" className="secondary-button" onClick={addExtractedQuestion}>
                {isSingleTaskMode ? "Add another task" : "Add missing question"}
              </button>
              <button type="button" className="primary-button" onClick={confirmExtractedQuestions}>
                {extractedQuestionsConfirmed ? "Assessment Structure Confirmed" : "Confirm Assessment Structure"}
              </button>
            </div>
            {extractedQuestionsConfirmed && (
              <p className="credit-note compact-info-note">Assessment structure confirmed. You can continue to marking depth selection.</p>
            )}
          </>
        )}
      </div>
    );
  };
  const renderStep3 = () => {
    const detectedNamesOnly = useDetectedNamesFromPdf;
    const currentStudent = selectedStudents.find(
      (s) => s.value === currentUploadingStudent
    );
    const currentIndex = selectedStudents.findIndex(
      (s) => s.value === currentUploadingStudent
    );
    const studentImages_ = studentImages[currentUploadingStudent] || { images: [] };
    const rangeDiagnostics = getBatchRangeDiagnostics();
    const batchPdfBusy = batchDetecting || questionExtracting;
    const batchPdfPreparing = batchPdfBusy && batchPdfPages.length === 0;
    const batchPdfStatusText = batchPdfName
      ? `PDF: ${batchPdfName} \u00b7 ${batchPdfPages.length > 0 ? `${batchPdfPages.length} pages` : "preparing pages..."}`
      : "";
    const batchDetectionButtonText = batchPdfPreparing
      ? "Preparing PDF pages..."
      : (batchPdfBusy
          ? "Detecting scripts..."
          : (batchPdfPages.length > 0 && batchScriptGroups.length > 0 ? "Re-detect Scripts" : "Detect Scripts"));
    const handleBatchDetectionAction = () => {
      if (batchPdfBusy) return;
      if (batchPdfPages.length > 0) {
        rerunBatchSplitDetection();
        return;
      }
      document.getElementById("batch-quick-pdf-input").click();
    };

    return (
      <div className={`batch-step ${routeAccentClass}`}>
        <div className="step-header ps-section-heading">
          <span className="ps-section-icon" aria-hidden="true"><Upload size={20} /></span>
          <div className="ps-section-copy">
          <h2>Step 3: Upload Papers</h2>
          <p>
            {detectedNamesOnly
              ? "Upload one combined PDF. StudyDen will detect the scripts, names, and page ranges for you to review."
              : "Upload page images for each student. Maximum 25 pages per student."}
          </p>
          </div>
        </div>

        <div className="mode-selected-summary" style={{ alignItems: "stretch" }}>
          <div>
            <h3 style={{ marginTop: 0 }}>{detectedNamesOnly ? "Combined PDF" : "Upload method"}</h3>
            <p className="upload-method-intro">
              {detectedNamesOnly
                ? "StudyDen will detect these scripts. Please check the names and page ranges after upload."
                : "Use combined PDF when one file contains several student scripts with cover/name pages."}
            </p>
            {!detectedNamesOnly && (
            <div className="marking-mode-options upload-method-options">
              <label
                className={`marking-mode-card upload-method-card ${batchQuickMarkMode === "single" ? "selected" : ""}`}
              >
                <input
                  type="radio"
                  name="batchQuickMarkMode"
                  checked={batchQuickMarkMode === "single"}
                  onChange={() => setBatchQuickMarkMode("single")}
                />
                <span className="marking-mode-title">Single student script</span>
                <span className="marking-mode-copy">Keep the current per-student upload workflow.</span>
              </label>
              <label className={`marking-mode-card upload-method-card ${batchQuickMarkMode === "batch" ? "selected" : ""}`}>
                <input
                  type="radio"
                  name="batchQuickMarkMode"
                  checked={batchQuickMarkMode === "batch"}
                  onChange={() => {
                    setBatchQuickMarkMode("batch");
                  }}
                />
                <span className="marking-mode-title">Combined PDF with multiple scripts</span>
                <span className="marking-mode-copy">Upload one combined PDF, detect cover/name pages, then review groups.</span>
              </label>
            </div>
            )}

            {batchQuickMarkMode === "batch" && (
              <div style={{ marginTop: "1rem" }}>
                {detectedNamesOnly && (
                  <div className="batch-upload-actions">
                    <button
                      type="button"
                      className="secondary-button"
                      onClick={() => {
                        if (showAdvancedBatchSplitOptions) {
                          setBatchSplitMethod("coverNamePages");
                          setBatchPagesPerStudent("");
                        }
                        setShowAdvancedBatchSplitOptions((prev) => !prev);
                      }}
                    >
                    {showAdvancedBatchSplitOptions ? "Hide advanced options" : "Advanced options"}
                    </button>
                  </div>
                )}
                {(!detectedNamesOnly || showAdvancedBatchSplitOptions) && (
                <div className="split-method-panel">
                  <h4>Advanced script splitting</h4>
                  <label className={`split-option ${batchSplitMethod === "coverNamePages" ? "selected" : ""}`}>
                    <span className="split-option-radio">
                      <input
                        type="radio"
                        name="batchSplitMethod"
                        checked={batchSplitMethod === "coverNamePages"}
                        onChange={() => setBatchSplitMethod("coverNamePages")}
                      />
                    </span>
                    <span className="split-option-copy">
                      <strong>Auto-detect scripts and names</strong>
                      <span>Best when each student has a cover page or name on the first page. StudyDen will detect names and estimate where each script starts and ends.</span>
                    </span>
                  </label>
                  <label className={`split-option ${batchSplitMethod === "fixedPages" ? "selected" : ""}`}>
                    <span className="split-option-radio">
                      <input
                        type="radio"
                        name="batchSplitMethod"
                        checked={batchSplitMethod === "fixedPages"}
                        onChange={() => setBatchSplitMethod("fixedPages")}
                      />
                    </span>
                    <span className="split-option-copy">
                      <strong>Fixed pages per student</strong>
                      <span>Use this when every student script is exactly the same length.</span>
                    </span>
                  </label>
                  {batchSplitMethod === "fixedPages" && (
                    <div className="fixed-pages-config">
                      <label>
                        <span>Pages per student</span>
                        <input
                          type="number"
                          min="1"
                          value={batchPagesPerStudent}
                          onChange={(e) => setBatchPagesPerStudent(e.target.value)}
                          placeholder="2"
                        />
                      </label>
                      <p>Example: enter 2 if every student has exactly 2 pages.</p>
                    </div>
                  )}
                </div>
                )}

                <input
                  type="file"
                  id="batch-quick-pdf-input"
                  accept=".pdf"
                  onChange={handleBatchPdfUpload}
                  className="file-input"
                />
                <div className="batch-upload-actions">
                  <button
                    type="button"
                    className="primary-button"
                    onClick={handleBatchDetectionAction}
                    disabled={batchPdfBusy || (batchSplitMethod === "fixedPages" && !batchPagesPerStudent)}
                  >
                    {batchPdfBusy && <span className="button-spinner" aria-hidden="true"></span>}
                    {batchDetectionButtonText}
                  </button>
                  {batchPdfName && <span className="pdf-file-pill">{batchPdfStatusText}</span>}
                </div>
                {batchPdfBusy && (
                  <p className="upload-helper-text">
                    StudyDen is checking the PDF for scripts and names. This may take a moment for larger files.
                  </p>
                )}
                {batchDetectionError && <div className="mode-warning">{batchDetectionError}</div>}

                {batchScriptGroups.length > 0 && (
                  <div className="student-list-summary review-panel script-review-panel">
                    <h3>Review Detected Scripts</h3>
                    <p className="credit-note">
                      StudyDen detected these scripts. Please check the names and page ranges.
                    </p>
                    {rangeDiagnostics.overlapWarnings.map((warning) => (
                      <div key={warning} className="script-warning script-warning-danger">{warning}</div>
                    ))}
                    {rangeDiagnostics.unassignedPages.length > 0 && (
                      <div className="script-warning script-warning-info">
                        Unassigned page(s): {rangeDiagnostics.unassignedPages.join(", ")}. This is fine for blank or ignored pages.
                      </div>
                    )}
                    <div className="summary-list script-card-list">
                      {batchScriptGroups.map((group, index) => {
                        const likelyBlankPages = getLikelyBlankPagesForGroup(group);
                        const visibleWarnings = getVisibleScriptWarnings(group);
                        return (
                        <div key={group.groupId} className="script-card">
                          <div className="script-card-header">
                            <div className="script-card-title-block">
                              <strong className="script-card-title">Script {index + 1}: {getBatchGroupStudentName(group) || "Name not detected"}</strong>
                              <span className="script-card-meta">
                                Pages {group.pageStart + 1}-{group.pageEnd + 1} - {(group.pageIndexes || []).length} page(s) - {group.confidence} confidence
                              </span>
                            </div>
                            <div className="script-card-actions">
                              <button className="remove-button compact-remove-button" onClick={() => removeBatchScriptGroup(group.groupId)}>
                                Remove
                              </button>
                            </div>
                          </div>
                          <div className="script-card-controls">
                            <label className="review-form-field script-name-field">
                              <span>Detected name</span>
                              <input
                                type="text"
                                value={getBatchGroupStudentName(group)}
                                onChange={(e) => updateBatchScriptGroup(group.groupId, {
                                  detectedName: e.target.value,
                                  assignedStudentName: e.target.value
                                })}
                                placeholder="Student name"
                                className="manual-student-input"
                              />
                            </label>
                            <label className="review-form-field script-assign-field">
                              <span>Assign student</span>
                              <Select
                                options={selectedStudents}
                                value={selectedStudents.find(student => student.value === group.assignedStudentId) || null}
                                onChange={(option) => updateBatchScriptGroup(group.groupId, {
                                  assignedStudentId: option?.value || "",
                                  assignedStudentName: option?.label || group.detectedName || ""
                                })}
                                placeholder="Optional..."
                                isClearable
                                className="custom-react-select compact-select"
                                classNamePrefix="react-select"
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                styles={{ menuPortal: (base) => ({ ...base, zIndex: 10000 }) }}
                              />
                            </label>
                            <div className="page-range-group">
                              <span className="page-range-label">Page range</span>
                              <div className="page-range-inputs">
                                <label>
                                  <span>Start</span>
                                  <input
                                    type="number"
                                    min="1"
                                    max={batchPdfPages.length || 1}
                                    value={(Number.isInteger(group.pageStart) ? group.pageStart : 0) + 1}
                                    onChange={(e) => updateBatchScriptRange(group.groupId, "start", e.target.value)}
                                    className="manual-student-input page-range-input"
                                  />
                                </label>
                                <label>
                                  <span>End</span>
                                  <input
                                    type="number"
                                    min="1"
                                    max={batchPdfPages.length || 1}
                                    value={(Number.isInteger(group.pageEnd) ? group.pageEnd : group.pageStart || 0) + 1}
                                    onChange={(e) => updateBatchScriptRange(group.groupId, "end", e.target.value)}
                                    className="manual-student-input page-range-input"
                                  />
                                </label>
                              </div>
                            </div>
                          </div>
                          <div className="script-warning-row">
                            {likelyBlankPages.length > 0 && (
                              <div className="script-warning script-warning-info">
                                Possible blank page detected: {likelyBlankPages.map(pageIndex => pageIndex + 1).join(", ")}
                                <div className="script-warning-actions">
                                  <button
                                    type="button"
                                    className="secondary-button compact-secondary-button"
                                    onClick={() => removeLikelyBlankPagesFromScript(group.groupId)}
                                  >
                                    Remove possible blank pages
                                  </button>
                                </div>
                              </div>
                            )}
                            {visibleWarnings.map((warning) => (
                              <div key={warning} className="script-warning script-warning-muted">{warning}</div>
                            ))}
                          </div>
                        </div>
                      );})}
                    </div>
                    {isScanExtractedQuizMode && renderExtractedQuestionReview()}
                    <div className="step-actions review-actions final-script-actions">
                      <details className="advanced-inline-details">
                        <summary>Advanced options</summary>
                        <button
                          type="button"
                          className="secondary-button"
                          onClick={addBatchScriptManually}
                        >
                          Add script manually
                        </button>
                      </details>
                      <button
                        className="primary-button"
                        onClick={proceedToMarkingMode}
                        disabled={
                          batchScriptGroups.length < 2 ||
                          batchScriptGroups.some(group => !getBatchGroupStudentName(group)) ||
                          (isScanExtractedQuizMode && (!extractedQuestionsConfirmed || questionExtracting))
                        }
                      >
                        Continue to Marking Depth
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {batchQuickMarkMode !== "batch" && (
          <>
        <div className="student-progress-bar">
          <p>
            <strong>
              Student {currentIndex + 1} of {selectedStudents.length}
            </strong>
          </p>
          <div className="progress-fill" style={{ width: `${((currentIndex + 1) / selectedStudents.length) * 100}%` }}></div>
        </div>

        <div className="upload-card">
          <div className="upload-card-header">
            <h3>{currentStudent.label}</h3>
            <span className="page-count">
              {/* Phase 4: show combined local + mobile page count */}
              {(studentImages_.images.length + (studentImages_.mobileImages?.length || 0))} page{(studentImages_.images.length + (studentImages_.mobileImages?.length || 0)) !== 1 ? "s" : ""}
              {studentImages_.images.length > 12 && (
                <span className="warning"> ⚠️ Large script — may take longer to process</span>
              )}
              {(studentImages_.mobileImages?.length || 0) > 0 && (
                <span style={{ marginLeft: 6, fontSize: "0.78em", color: "#4f46e5" }}>
                  ({studentImages_.mobileImages.length} 📱)
                </span>
              )}
            </span>
          </div>

          <div className="upload-zone">
            <input
              type="file"
              id={`file-input-${currentUploadingStudent}`}
              multiple
              accept=".jpg,.jpeg,.png"
              onChange={(e) => handleImageUpload(e, currentUploadingStudent)}
              disabled={studentImages_.images.length >= 25}
              className="file-input"
            />
            <label
              htmlFor={`file-input-${currentUploadingStudent}`}
              className="upload-label"
            >
              <div className="upload-icon">📷</div>
              <div className="upload-text">
                {studentImages_.images.length >= 25
                  ? "Maximum 25 pages reached"
                  : "Drag and drop images or click to browse"}
              </div>
              {studentImages_.images.length < 25 && (
                <button
                  type="button"
                  className="choose-button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    document.getElementById(`file-input-${currentUploadingStudent}`).click();
                  }}
                >
                  Choose images
                </button>
              )}
            </label>
          </div>

          {/* ── PDF upload option ──
               Teacher selects a PDF (e.g. a multi-page scanned script).
               The server converts each page to a PNG image and returns them
               as base64 data URLs.  The frontend then creates File objects
               from those data URLs and adds them to studentImages.images so
               they flow through the exact same Firebase Storage upload, OCR,
               marking, and moderation pipeline as manually chosen images. */}
          <div className="phone-upload-option">
            <input
              type="file"
              id={`pdf-input-${currentUploadingStudent}`}
              accept=".pdf"
              onChange={(e) => handlePdfUpload(e, currentUploadingStudent)}
              disabled={
                (studentImages_.images.length + (studentImages_.mobileImages?.length || 0)) >= 25 ||
                !!pdfConverting[currentUploadingStudent]
              }
              className="file-input"
            />
            <button
              type="button"
              className="phone-upload-button"
              onClick={() =>
                document.getElementById(`pdf-input-${currentUploadingStudent}`).click()
              }
              disabled={
                (studentImages_.images.length + (studentImages_.mobileImages?.length || 0)) >= 25 ||
                !!pdfConverting[currentUploadingStudent]
              }
            >
              {pdfConverting[currentUploadingStudent]
                ? "⏳ Converting PDF pages…"
                : "📄 Upload PDF"}
            </button>
            {pdfConverting[currentUploadingStudent] && (
              <p style={{ fontSize: "0.82em", color: "#555", marginTop: "6px", textAlign: "center" }}>
                Converting PDF pages to images on the server, please wait…
              </p>
            )}
          </div>

          <div className="phone-upload-option">
            <button
              type="button"
              className="phone-upload-button"
              onClick={() => handleOpenQrUpload(currentStudent, currentIndex)}
              disabled={qrModalLoading}
            >
              {qrModalLoading ? "Creating session…" : "Upload from phone 📱"}
            </button>
          </div>

          {studentImages_.images.length > 0 && (
            <div className="images-list">
              <div className="images-list-header">
                <h4>Uploaded Images</h4>
                <button
                  type="button"
                  className="sort-button"
                  onClick={() => handleSortImagesByFilename(currentUploadingStudent)}
                  title="Sort images by filename (natural numeric order)"
                >
                  🔤 Sort by Filename
                </button>
              </div>
              {studentImages_.images.map((image, idx) => (
                <div key={idx} className="image-item">
                  <div className="image-info">
                    <span className="page-number">Page {idx + 1}</span>
                    <span className="image-name">{image.name}</span>
                  </div>
                  <div className="image-actions">
                    {idx > 0 && (
                      <button
                        className="icon-button"
                        onClick={() => moveImageUp(currentUploadingStudent, idx)}
                        title="Move up"
                      >
                        ▲
                      </button>
                    )}
                    {idx < studentImages_.images.length - 1 && (
                      <button
                        className="icon-button"
                        onClick={() => moveImageDown(currentUploadingStudent, idx)}
                        title="Move down"
                      >
                        ▼
                      </button>
                    )}
                    <button
                      className="remove-button"
                      onClick={() => handleRemoveImage(currentUploadingStudent, idx)}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Mobile-uploaded images from QR session — included in batch processing (Phase 5) */}
          {(studentImages_.mobileImages?.length || 0) > 0 && (
            <div className="images-list" style={{ marginTop: "12px" }}>
              <h4 style={{ color: "#4f46e5" }}>📱 From Phone</h4>
              {studentImages_.mobileImages.map((img, idx) => (
                <div key={img.url} className="image-item" style={{ borderColor: "#c7d2fe", background: "#f5f3ff" }}>
                  <div className="image-info">
                    <span className="page-number" style={{ color: "#4f46e5" }}>
                      Phone {idx + 1}
                    </span>
                    <span className="image-name">{img.name}</span>
                  </div>
                  <div className="image-actions">
                    <a
                      href={img.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="icon-button"
                      title="View image"
                    >
                      👁️
                    </a>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="step-actions">
          <button
            className="secondary-button"
            onClick={goToPreviousStudent}
            disabled={currentIndex === 0}
          >
            ← Previous Student
          </button>
          {currentIndex < selectedStudents.length - 1 ? (
            <button className="secondary-button" onClick={goToNextStudent}>
              Next Student →
            </button>
          ) : (
            <button className="primary-button" onClick={proceedToMarkingMode}>
              Choose Marking Depth
            </button>
          )}
          <button className="secondary-button" onClick={handleGoBack}>
            Back to Students
          </button>
        </div>
          </>
        )}

        {renderQrModal()}
      </div>
    );
  };

  const renderLegacyReviewStep = () => {
    const creditCost = calculateCreditCost();
    const { baseStudentCost, pageCost, fullAnalysisCost, totalCost } = creditCost;
    const totalPages = getTotalPageCount();

    return (
      <div className={`batch-step ${routeAccentClass}`}>
        <div className="step-header">
          <h2>Step 4: Batch Summary</h2>
          <p>Review your batch configuration and credit cost before processing.</p>
        </div>

        <div className="summary-grid">
          <div className="summary-card">
            <div className="summary-label">Test</div>
            <div className="summary-value">{getEffectiveQuizTitle()}</div>
          </div>

          <div className="summary-card">
            <div className="summary-label">Students</div>
            <div className="summary-value">{selectedStudents.length}</div>
          </div>

          <div className="summary-card">
            <div className="summary-label">Total Pages</div>
            <div className="summary-value">{totalPages}</div>
          </div>

          <div className="summary-card">
            <div className="summary-label">Full Analysis</div>
            <div className="summary-value">{fullAnalysisEnabled ? "Enabled" : "Disabled"}</div>
          </div>
        </div>

        <div className="credit-breakdown-section">
          <h3>Credit Cost Breakdown</h3>
          <div className="credit-breakdown">
            <div className="credit-row">
              <span>Student scripts ({selectedStudents.length} × 1 credit)</span>
              <span className="credit-cost">{baseStudentCost} credit{baseStudentCost !== 1 ? "s" : ""}</span>
            </div>
            <div className="credit-row">
              <span>Page processing ({totalPages} page{totalPages !== 1 ? "s" : ""} ÷ 10, rounded up)</span>
              <span className="credit-cost">{pageCost} credit{pageCost !== 1 ? "s" : ""}</span>
            </div>
            {fullAnalysisEnabled ? (
              <div className="credit-row">
                <span>Full Analysis ({selectedStudents.length} student{selectedStudents.length !== 1 ? "s" : ""} × 2 credits)</span>
                <span className="credit-cost">{fullAnalysisCost} credit{fullAnalysisCost !== 1 ? "s" : ""}</span>
              </div>
            ) : (
              <div className="credit-row">
                <span>Full Analysis</span>
                <span className="credit-cost">Off</span>
              </div>
            )}
            <div className="credit-row total">
              <span>Total Cost</span>
              <span className="credit-cost-total">{totalCost} credit{totalCost !== 1 ? "s" : ""}</span>
            </div>
          </div>
          <p className="credit-note">
            Credits will be deducted once processing begins. Processing large batches may take several minutes.
          </p>
        </div>

        <div className="full-analysis-section">
          <h3>Full Analysis (Premium Feature)</h3>
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={fullAnalysisEnabled}
              onChange={(e) => setFullAnalysisEnabled(e.target.checked)}
            />
            <span>
              Enable Full Analysis for deeper written feedback, strengths, missing points, targets, and richer insights. This adds 2 credits per student.
            </span>
          </label>
        </div>

        <div className="student-list-summary">
          <h3>Students in Batch</h3>
          <div className="summary-list">
            {selectedStudents.map((student) => {
              const entry = studentImages[student.value];
              const totalPages = (entry?.images?.length || 0) + (entry?.mobileImages?.length || 0);
              return (
                <div key={student.value} className="summary-item">
                  <span className="student-name">{student.label}</span>
                  <span className="page-badge">
                    {totalPages} page{totalPages !== 1 ? "s" : ""}
                    {(entry?.mobileImages?.length || 0) > 0 && (
                      <span style={{ marginLeft: 4, fontSize: "0.85em" }}>
                        ({entry.mobileImages.length} 📱)
                      </span>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Batch error from limit validation */}
        {batchError && (
          <div style={{
            marginTop: "1rem",
            backgroundColor: "#fef2f2",
            border: "1px solid #fca5a5",
            borderRadius: "8px",
            padding: "0.75rem 1rem",
            color: "#b91c1c",
            fontSize: "0.9rem"
          }}>
            ⚠️ {batchError}
          </div>
        )}

        {/* Page limit hint */}
        {(() => {
          const { maxPages } = getPaperSubmissionLimits(userRole, teacherStatus, fullAnalysisEnabled);
          const pagesOver = totalPages > maxPages;
          return (
            <div style={{
              marginTop: "0.75rem",
              padding: "0.6rem 1rem",
              borderRadius: "6px",
              fontSize: "0.85rem",
              backgroundColor: pagesOver ? "#fef2f2" : "#f0fdf4",
              border: `1px solid ${pagesOver ? "#fca5a5" : "#86efac"}`,
              color: pagesOver ? "#b91c1c" : "#166534"
            }}>
              📄 Current batch: {totalPages} / {maxPages} total pages allowed
              {pagesOver && ` — exceeds limit by ${totalPages - maxPages} page${totalPages - maxPages !== 1 ? "s" : ""}`}
            </div>
          );
        })()}

        <div className="step-actions">
          <button
            className="primary-button"
            onClick={() => { setBatchError(null); proceedToProcessing(); }}
            disabled={isPaperSubmissionDisabled || isStartingBatch}
            title={isPaperSubmissionDisabled ? "Trial expired - paper submission unavailable" : ""}
          >
            Confirm & Process Batch
          </button>
          <button className="secondary-button" onClick={handleGoBack}>
            Back
          </button>
        </div>
      </div>
    );
  };

  const renderStep4 = () => {
    const selectedMode = MARKING_MODES[markingMode];
    const detectedQuestionPatternCount = getDetectedQuestionPatternCount();
    const looksStructuredScan = isScanExtractedQuizMode && (detectedQuestionPatternCount >= 2 || extractedQuestions.length >= 2);

    return (
      <div className={`batch-step ${routeAccentClass}`}>
        <div className="step-header ps-section-heading">
          <span className="ps-section-icon" aria-hidden="true"><Target size={20} /></span>
          <div className="ps-section-copy">
          <h2>Step 4: Choose Marking Depth</h2>
          <p>Now choose how much detail you want in the marking. Quick Mark, Standard Marking, and Full Analysis use the assessment source you selected in Step 1.</p>
          </div>
        </div>

        <div className="marking-mode-options" role="radiogroup" aria-label="Marking mode">
          {Object.entries(MARKING_MODES).map(([modeKey, mode]) => {
            const ModeIcon = MARKING_MODE_ICONS[modeKey];
            const isSelected = markingMode === modeKey;
            const isUnavailable = !isMarkingModeAvailable(modeKey);
            const modeCopy = isSingleWritingTaskAssessment && modeKey === "quick"
              ? "Available. Best for this single writing task."
              : isUnavailable && modeKey === "standard"
                ? "Requires a structured question set."
                : isUnavailable && modeKey === "fullAnalysis"
                  ? "Requires structured questions before analysis can run."
                  : mode.copy;
            const modeOutput = isUnavailable
              ? "Not available for single-task fallback yet."
              : mode.expectedOutput;

            return (
              <label
                key={modeKey}
                className={`marking-mode-card ${isSelected ? "selected" : ""} ${isUnavailable ? "disabled" : ""}`}
              >
                <input
                  type="radio"
                  name="markingMode"
                  value={modeKey}
                  checked={isSelected}
                  disabled={isUnavailable}
                  onChange={() => handleMarkingModeChange(modeKey)}
                />
                <span className="marking-mode-card-top">
                  <span className="marking-mode-icon" aria-hidden="true">
                    <ModeIcon size={20} strokeWidth={2.2} />
                  </span>
                  <span className="marking-mode-state">
                    {isUnavailable ? "Not available" : isSelected ? "Selected" : "Option"}
                  </span>
                </span>
                <span className="marking-mode-title">{mode.label}</span>
                <span className="marking-mode-copy">{modeCopy}</span>
                <span className="marking-mode-output">{modeOutput}</span>
              </label>
            );
          })}
        </div>

        {markingModeNotice && (
          <div className="mode-selected-summary">
            {markingModeNotice}
          </div>
        )}
        {isSingleWritingTaskAssessment && (
          <div className="mode-warning">
            {singleWritingTaskUnavailableMessage}
          </div>
        )}
        {markingMode === "quick" && !selectedQuiz && !isScanExtractedQuizMode && (
          <div className="mode-warning">
            Quick Mark needs an assessment structure. Select an existing test or review the scanned-paper structure first.
          </div>
        )}
        {isScanExtractedQuizMode && markingMode === "quick" && (
          <div className="mode-warning">
            Quick Mark will use the reviewed assessment structure from Step 1 and give shorter feedback.
          </div>
        )}
        {looksStructuredScan && markingMode === "quick" && (
          <div className="mode-warning">
            This looks like a structured paper. Question-by-question marking may be more suitable.
          </div>
        )}
        {isScanExtractedQuizMode && markingMode !== "quick" && (
          <div className="mode-selected-summary">
            Standard Marking or Full Analysis will use the reviewed assessment structure from Step 1 across each detected script.
          </div>
        )}
        {isScanExtractedQuizMode && !extractedQuestionsConfirmed && renderExtractedQuestionReview()}

        <div className="mode-selected-summary">
          <div className="mode-selected-icon" aria-hidden="true">
            <Settings2 size={20} strokeWidth={2.2} />
          </div>
          <div>
            <p><strong>Selected mode:</strong> {selectedMode.label}</p>
            <p>{selectedMode.expectedOutput}</p>
          </div>
        </div>

        <div className="step-actions">
          <button
            className="primary-button"
            onClick={proceedToReview}
            disabled={
              (markingMode === "quick" && !selectedQuiz && !isScanExtractedQuizMode) ||
              (isScanExtractedQuizMode && (!extractedQuestionsConfirmed || questionExtracting)) ||
              !isMarkingModeAvailable(markingMode)
            }
          >
            Continue to Review
          </button>
          <button className="secondary-button" onClick={handleGoBack}>
            Back
          </button>
        </div>
      </div>
    );
  };

  const renderReviewStep = () => {
    const creditCost = calculateCreditCost();
    const { baseStudentCost, pageCost, fullAnalysisCost, quickMarkDiscount, totalCost } = creditCost;
    const totalPages = getTotalPageCount();
    const selectedMode = MARKING_MODES[markingMode];
    const reviewStudentCount = batchQuickMarkMode === "batch" ? batchScriptGroups.length : selectedStudents.length;
    const selectedModeUnavailable = !isMarkingModeAvailable(markingMode);
    const startButtonLabel =
      selectedModeUnavailable
        ? "Use Quick Mark"
        : markingMode === "quick"
          ? "Start Quick Mark"
          : markingMode === "fullAnalysis"
            ? "Start Full Analysis"
            : "Start Standard Marking";

    return (
      <div className={`batch-step ${routeAccentClass}`}>
        <div className="step-header ps-section-heading">
          <span className="ps-section-icon" aria-hidden="true"><FileCheck size={20} /></span>
          <div className="ps-section-copy">
          <h2>Step 5: Review and Mark</h2>
          <p>Check the assessment, scripts, marking depth, and credit cost before processing.</p>
          </div>
        </div>

        <div className="summary-grid review-summary-grid">
          <div className="summary-card">
            <FileText className="summary-icon" size={20} strokeWidth={2.1} aria-hidden="true" />
            <div className="summary-label">Assessment Source</div>
            <div className="summary-value">{getAssessmentSourceLabel()}</div>
          </div>
          <div className="summary-card">
            <ClipboardCheck className="summary-icon" size={20} strokeWidth={2.1} aria-hidden="true" />
            <div className="summary-label">Assessment Title</div>
            <div className="summary-value">{getEffectiveQuizTitle()}</div>
          </div>
          <div className="summary-card">
            <Users className="summary-icon" size={20} strokeWidth={2.1} aria-hidden="true" />
            <div className="summary-label">{batchQuickMarkMode === "batch" ? "Scripts" : "Students"}</div>
            <div className="summary-value">{reviewStudentCount}</div>
          </div>
          <div className="summary-card">
            <CloudUpload className="summary-icon" size={20} strokeWidth={2.1} aria-hidden="true" />
            <div className="summary-label">Total Pages</div>
            <div className="summary-value">{totalPages}</div>
          </div>
          <div className="summary-card">
            <Settings2 className="summary-icon" size={20} strokeWidth={2.1} aria-hidden="true" />
            <div className="summary-label">Marking Depth</div>
            <div className="summary-value">{selectedMode.label}</div>
          </div>
          {!selectedModeUnavailable && (
            <div className="summary-card">
              <CircleCheck className="summary-icon" size={20} strokeWidth={2.1} aria-hidden="true" />
              <div className="summary-label">Estimated Credits</div>
              <div className="summary-value">{totalCost}</div>
            </div>
          )}
          <div className="summary-card">
            <Search className="summary-icon" size={20} strokeWidth={2.1} aria-hidden="true" />
            <div className="summary-label">Expected Output</div>
            <div className="summary-value compact">{selectedMode.expectedOutput}</div>
          </div>
        </div>

        {isSingleWritingTaskAssessment && (
          <div className="mode-warning">
            {singleWritingTaskUnavailableMessage}
          </div>
        )}

        <div className="mode-review-note">
          <strong>Expected output:</strong> {selectedMode.expectedOutput}
          {markingMode === "quick" && (
            <p>
              Quick Mark gives fast marks and short reasons. It does not provide full analysis or detailed mark scheme breakdowns.
            </p>
          )}
        </div>

        {!selectedModeUnavailable && (
          <div className="credit-breakdown-section">
            <h3>Credit Cost Breakdown</h3>
            <div className="credit-breakdown">
              <div className="credit-row">
                <span>
                  {markingMode === "quick"
                    ? `Quick Mark scripts (${reviewStudentCount} student${reviewStudentCount !== 1 ? "s" : ""})`
                    : `Student scripts (${reviewStudentCount} x 1 credit)`}
                </span>
                <span className="credit-cost">{baseStudentCost} credit{baseStudentCost !== 1 ? "s" : ""}</span>
              </div>
              {markingMode === "quick" && quickMarkDiscount > 0 && (
                <div className="credit-row">
                  <span>Quick Mark lightweight processing saving</span>
                  <span className="credit-cost">-{quickMarkDiscount} credit{quickMarkDiscount !== 1 ? "s" : ""}</span>
                </div>
              )}
              <div className="credit-row">
                <span>Page processing ({totalPages} page{totalPages !== 1 ? "s" : ""} / 10, rounded up)</span>
                <span className="credit-cost">{pageCost} credit{pageCost !== 1 ? "s" : ""}</span>
              </div>
              {fullAnalysisEnabled ? (
                <div className="credit-row">
                  <span>Full Analysis ({reviewStudentCount} student{reviewStudentCount !== 1 ? "s" : ""} x 2 credits)</span>
                  <span className="credit-cost">{fullAnalysisCost} credit{fullAnalysisCost !== 1 ? "s" : ""}</span>
                </div>
              ) : (
                <div className="credit-row">
                  <span>Full Analysis</span>
                  <span className="credit-cost">Off</span>
                </div>
              )}
              <div className="credit-row total">
                <span>Total Cost</span>
                <span className="credit-cost-total">{totalCost} credit{totalCost !== 1 ? "s" : ""}</span>
              </div>
            </div>
            <p className="credit-note">
              Credits will be deducted once processing begins. Processing large batches may take several minutes.
            </p>
          </div>
        )}

        <div className="student-list-summary">
          <h3>{batchQuickMarkMode === "batch" ? "Scripts to Mark" : "Students to Mark"}</h3>
          <div className="summary-list">
            {(batchQuickMarkMode === "batch" ? batchScriptGroups : selectedStudents).map((student) => {
              if (batchQuickMarkMode === "batch") {
                return (
                  <div key={student.groupId} className="summary-item">
                    <span className="student-name">{student.assignedStudentName || student.detectedName}</span>
                    <span className="page-badge">Pages {student.pageStart + 1}-{student.pageEnd + 1}</span>
                  </div>
                );
              }
              const entry = studentImages[student.value];
              const totalPagesForStudent = (entry?.images?.length || 0) + (entry?.mobileImages?.length || 0);
              return (
                <div key={student.value} className="summary-item">
                  <span className="student-name">{student.label}</span>
                  <span className="page-badge">
                    {totalPagesForStudent} page{totalPagesForStudent !== 1 ? "s" : ""}
                    {(entry?.mobileImages?.length || 0) > 0 && (
                      <span style={{ marginLeft: 4, fontSize: "0.85em" }}>
                        ({entry.mobileImages.length} phone)
                      </span>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {batchError && (
          <div className="mode-warning" role="alert">
            {batchError}
            {batchErrorAction?.message === batchError && (
              <button
                type="button"
                className="secondary-button"
                style={{ marginLeft: "0.75rem" }}
                onClick={() => { setBatchError(null); setBatchErrorAction(null); setCurrentStep(batchErrorAction.step); }}
              >
                Go to Step {batchErrorAction.step}
              </button>
            )}
          </div>
        )}
        {isStartingBatch && !batchError && (
          <div className="mode-selected-summary" role="status">{statusMessage || "Starting marking..."}</div>
        )}

        {(() => {
          const { maxPages } = getPaperSubmissionLimits(userRole, teacherStatus, fullAnalysisEnabled);
          const pagesOver = totalPages > maxPages;
          return (
            <div className={`page-limit-note ${pagesOver ? "over" : ""}`}>
              Current batch: {totalPages} / {maxPages} total pages allowed
              {pagesOver && ` - exceeds limit by ${totalPages - maxPages} page${totalPages - maxPages !== 1 ? "s" : ""}`}
            </div>
          );
        })()}

        <div className="step-actions">
          <button
            className="primary-button"
            onClick={() => { setBatchError(null); proceedToProcessing(); }}
            disabled={isPaperSubmissionDisabled || selectedModeUnavailable || isStartingBatch}
            aria-busy={isStartingBatch}
            title={isPaperSubmissionDisabled ? "Trial expired - paper submission unavailable" : ""}
          >
            {isStartingBatch ? "Starting..." : startButtonLabel}
          </button>
          <button className="secondary-button" onClick={handleGoBack} disabled={isStartingBatch}>
            Back
          </button>
        </div>
      </div>
    );
  };

  const renderBreakdownModal = (submission) => {
    if (!submission) return null;

    const handleCloseModal = () => setSelectedBreakdownStudent(null);

    const cleanFeedback = (feedback) => {
      if (!feedback) return "";
      let text = feedback.trim();
      text = text.replace(/^feedback\s*:\s*/i, "");
      text = text.replace(/\n?\s*score\s*[:=]?\s*\d+(\.\d+)?\s*$/i, "").trim();
      return text;
    };

    return (
      <div className="breakdown-modal-overlay" onClick={handleCloseModal}>
        <div className="breakdown-modal" onClick={(e) => e.stopPropagation()}>
          <button className="close-button" onClick={handleCloseModal}>✕</button>
          
          <div className="breakdown-header">
            <h2>{submission.studentName}</h2>
            <div className="marks-summary">
              <span className="total-mark">{paperSummaryText(submission.markingSummary)}</span>
              <span className="percentage-badge">{submission.markingSummary?.percentage == null ? "Score pending" : `${Number(submission.markingSummary.percentage).toFixed(1)}%`}</span>
            </div>
          </div>

          <div className="breakdown-content">
            {submission.usedFallback && submission.status === "completed" && (
              <div className="fallback-warning">
                <p><strong>⚠️ Fallback Mode Used</strong></p>
                <p>This submission was marked using fallback mode because no clearly structured answers were detected. The full OCR text was treated as the answer to the first available question.</p>
              </div>
            )}
            
            {submission.status === "failed" ? (
              <div className="failure-info">
                <h3>❌ Submission Failed</h3>
                <p><strong>Stage:</strong> {submission.errorStage}</p>
                <p><strong>Error:</strong> {submission.errorMessage}</p>
              </div>
            ) : (
              <>
                {submission.markedResults && submission.markedResults.length > 0 && (
                  <div className="questions-breakdown">
                    <h3>Question Breakdown</h3>
                    {submission.markedResults.map((result, idx) => (
                      <div key={idx} className="question-item">
                        <div className="question-header">
                          <div className="question-label-and-marks">
                            <span className="question-label">{result.questionLabel}</span>
                            <span className="marks-badge">
                              {result.markAwarded == null ? `Pending review (${result.markingState || "unresolved"})` : `${result.markAwarded}/${result.maxMark}`}
                            </span>
                          </div>
                        </div>
                        
                        {result.questionText && (
                          <div className="question-text">
                            <strong>Question:</strong> {result.questionText}
                          </div>
                        )}
                        
                        {result.userAnswer && (
                          <div className="student-answer">
                            <strong>Your Answer:</strong>
                            <p>{result.userAnswer.substring(0, 300)}{result.userAnswer.length > 300 ? "..." : ""}</p>
                          </div>
                        )}
                        
                        {result.feedback && (
                          <div className="feedback-section">
                            <strong>Feedback:</strong>
                            <p>{cleanFeedback(result.feedback)}</p>
                          </div>
                        )}

                        {result.extractionNote && (
                          <div className="feedback-section transcription-note">
                            <strong>Check transcription:</strong>
                            <p>{result.extractionNote.replace(/^Check transcription:\s*/i, "")}</p>
                          </div>
                        )}

                        {result.status && (
                          <div className="feedback-section quick-status-section">
                            <strong>Status:</strong>
                            <p>{result.status}</p>
                          </div>
                        )}

                        {result.shortReason && (
                          <div className="feedback-section">
                            <strong>Short Reason:</strong>
                            <p>{result.shortReason}</p>
                          </div>
                        )}

                        {result.fullAnalysis && (typeof result.fullAnalysis === "object") && (
                          <div className="full-analysis-section">
                            <h4>📊 Full Analysis</h4>
                            
                            {result.fullAnalysis.strengths && result.fullAnalysis.strengths.length > 0 && (
                              <div className="analysis-block strengths">
                                <strong>✅ Strengths:</strong>
                                <ul>
                                  {result.fullAnalysis.strengths.map((s, i) => (
                                    <li key={i}>{s}</li>
                                  ))}
                                </ul>
                              </div>
                            )}

                            {result.fullAnalysis.missingPoints && result.fullAnalysis.missingPoints.length > 0 && (
                              <div className="analysis-block missing">
                                <strong>❌ Missing Points:</strong>
                                <ul>
                                  {result.fullAnalysis.missingPoints.map((m, i) => (
                                    <li key={i}>{m}</li>
                                  ))}
                                </ul>
                              </div>
                            )}

                            {result.fullAnalysis.targets && result.fullAnalysis.targets.length > 0 && (
                              <div className="analysis-block targets">
                                <strong>🎯 Targets for Improvement:</strong>
                                <ul>
                                  {result.fullAnalysis.targets.map((t, i) => (
                                    <li key={i}>
                                      <strong>{t.target}</strong>
                                      {t.example && <p>Example: {t.example}</p>}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}

                            {result.fullAnalysis.commonErrors && result.fullAnalysis.commonErrors.length > 0 && (
                              <div className="analysis-block errors">
                                <strong>⚠️ Common Errors:</strong>
                                <ul>
                                  {result.fullAnalysis.commonErrors.map((e, i) => (
                                    <li key={i}>
                                      <strong>{e.type}:</strong> {e.example && `Your: "${e.example}"`} {e.correction && `→ Correct: "${e.correction}"`}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          <div className="breakdown-actions">
            <button className="primary-button" onClick={handleCloseModal}>Close</button>
          </div>
        </div>
      </div>
    );
  };

  const showingPaperResults = currentStep === 5 && restoredOwnerRef.current === user?.uid && !!(batchResults && fullBatchResults);

  const renderStep5 = () => {
    // STATE 4: ERROR - Show error card with retry/back actions
    if (batchError && (isProcessing || batchId || batchResults)) {
      return (
        <div className={`batch-step ${routeAccentClass}`}>
          <div style={{
            display: "flex",
            alignItems: "flex-start",
            gap: "1rem",
            marginBottom: "2rem"
          }}>
            <span style={{ fontSize: "2.5rem" }}>❌</span>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <h2 style={{ margin: "0 0 0.5rem 0" }}>Processing Failed</h2>
              <p style={{ margin: 0, color: "#555" }}>There was an issue with your batch.</p>
            </div>
          </div>

          <div style={{
            backgroundColor: "#fee",
            border: "1px solid #c33",
            borderRadius: "8px",
            padding: "20px",
            marginBottom: "20px",
            textAlign: "center"
          }}>
            <h3 style={{ color: "#c33", marginTop: 0 }}>Error</h3>
            <p style={{ color: "#333", lineHeight: "1.6", margin: "10px 0" }}>
              {batchError}
            </p>
            <p style={{ color: "#666", fontSize: "0.95em", marginBottom: 0 }}>
              Please try again or contact support if the problem persists.
            </p>
          </div>

          <div className="step-actions">
            <button 
              className="primary-button" 
              onClick={() => {
                setBatchError(null);
                setCurrentStep(5);
              }}
            >
              ← Go Back to Review
            </button>
            {!showingPaperResults && <button className="secondary-button" onClick={() => navigate("/dashboard")}>
              Back to Dashboard
            </button>}
          </div>
        </div>
      );
    }

    // STATE 3: COMPLETED - Show results dashboard with student cards
    if (batchResults && fullBatchResults) {
      const allFailed = fullBatchResults.length > 0 && fullBatchResults.every(s=>s.status==='failed');
      // Counts describe the displayed scripts, including unresolved work and
      // reports still awaiting recovery. They do not change stored results.
      const failedCount = fullBatchResults.filter(s => s.status === 'failed').length;
      const reviewCount = fullBatchResults.filter(s => s.status !== 'failed' && (s.status === 'needs_review' || s.markingSummary?.provisional || s.analysisStatus === 'failed')).length;
      const completedCount = fullBatchResults.filter(s => s.status === 'completed' && !s.markingSummary?.provisional && s.analysisStatus !== 'failed').length;
      const pendingCount = fullBatchResults.length - failedCount - reviewCount - completedCount;
      const reportCount = fullBatchResults.filter(s => s.status === 'completed' && s.markingMode !== 'quick' && (s.pdfStatus === 'failed' || s.pdfStatus === 'pending' || s.pdfStatus === 'generating')).length;
      const needsReview = failedCount > 0 || reviewCount > 0;
      const workInProgress = isProcessing || pendingCount > 0;
      const summaryTitle = workInProgress ? 'Batch processing' : allFailed ? 'Batch failed' : needsReview ? 'Batch needs review' : reportCount ? 'Reports need attention' : fullBatchResults.length ? 'Batch complete' : 'Results unavailable';
      const SummaryIcon = workInProgress ? Clock3 : needsReview || reportCount || !fullBatchResults.length ? CircleAlert : CircleCheck;
      const summaryTone = workInProgress ? 'info' : allFailed ? 'danger' : needsReview || reportCount || !fullBatchResults.length ? 'warning' : 'success';
      return (
        <div className="paper-results-content">
          <section className={`paper-results-summary paper-results-summary--${summaryTone}`} aria-labelledby="paper-results-summary-title">
            <SummaryIcon className="paper-results-summary-icon" size={24} aria-hidden="true" />
            <div className="paper-results-summary-copy">
              <h2 id="paper-results-summary-title">{summaryTitle}</h2>
              <p>{pendingCount ? `${pendingCount} script${pendingCount === 1 ? ' is' : 's are'} still processing. Results are not final.` : isProcessing ? 'Unresolved work is being retried. Saved results remain available.' : allFailed ? 'No scripts completed. Saved work is retained; retry unresolved work below.' : needsReview ? 'Some scripts need attention. Saved marks are retained; review or retry unresolved work below.' : reportCount ? `${completedCount} script${completedCount === 1 ? ' has' : 's have'} saved marks. Reports are pending or need recovery.` : fullBatchResults.length ? `${completedCount} script${completedCount === 1 ? '' : 's'} completed successfully.` : 'No script results are available yet.'}</p>
            </div>
            <div className="paper-results-counts" aria-label="Batch counts">
              <span className="paper-result-badge paper-result-badge--success">{completedCount} completed</span>
              <span className={`paper-result-badge${failedCount ? ' paper-result-badge--danger' : ''}`}>{failedCount} failed</span>
              {reviewCount > 0 && <span className="paper-result-badge paper-result-badge--warning">{reviewCount} need review</span>}
              {pendingCount > 0 && <span className="paper-result-badge paper-result-badge--info">{pendingCount} processing</span>}
              {reportCount > 0 && <span className="paper-result-badge paper-result-badge--warning">{reportCount} report{reportCount === 1 ? '' : 's'} pending</span>}
            </div>
          </section>

          {markingMode === "quick" && (
            <div className="quick-mark-actions download-results-panel">
              <div>
                <h3>Download Results</h3>
                <p>Export the Quick Mark results as data or a teacher-friendly summary document.</p>
              </div>
              <div className="download-results-buttons">
                <button className="primary-button" onClick={downloadQuickMarkCsv}>
                  Download CSV
                </button>
                <button className="secondary-button" onClick={downloadQuickMarkTeacherSummary}>
                  Download Teacher Summary
                </button>
              </div>
              <p className="download-results-note">
                Some answers may be marked as unclear if the scan could not be read confidently.
              </p>
            </div>
          )}

          <div className="paper-results-panel">
            <table className="paper-results-table" role="table">
              <caption className="paper-results-sr-only">Student results, in batch order</caption>
              <thead><tr role="row">
                {['Student', 'Score', 'Percentage', 'Outcome', 'Status', 'Actions'].map(label => <th key={label} scope="col" role="columnheader">{label}</th>)}
              </tr></thead>
              <tbody>
            {fullBatchResults.map((submission) => {
              const pdfStatus = submission.pdfStatus || "";
              const hasPdfReport = submission.pdfReportRef && submission.pdfReportRef.trim() !== "";
              const isCompleted = ["completed","needs_review"].includes(submission.status);
              const isProvisional = submission.markingSummary?.provisional;
              const isFailed = submission.status === "failed";
              const isQuickMarkResult = submission.markingMode === "quick";
              const pdfGenerating = !isQuickMarkResult && pdfStatus === "generating";
              const pdfFailed = !isQuickMarkResult && pdfStatus === "failed";
              const pdfReady = !isQuickMarkResult && pdfStatus === "ready" && hasPdfReport;
              const resultPercentage = Number(submission.markingSummary?.percentage) || 0;
              const performance = submission.markingSummary?.provisional ? { label: "Provisional", className: "warning" } : submission.markingSummary?.percentage == null ? {label: 'Score pending', className: 'neutral'} :
                resultPercentage >= 85
                  ? { label: "Strong", className: "success" }
                  : resultPercentage >= 65
                    ? { label: "Secure", className: "info" }
                    : resultPercentage >= 40
                      ? { label: "Developing", className: "warning" }
                      : { label: "Needs review", className: "warning" };
              const errorMessage = String(submission.errorMessage || "This script could not be processed.").trim();
              const shortErrorMessage = errorMessage.length > 130
                ? `${errorMessage.slice(0, 127).trim()}...`
                : errorMessage;

              return (
                <tr key={submission.submissionId} role="row">
                  <th scope="row" role="rowheader" className="paper-result-student">
                    {submission.studentName || 'Name unavailable'}
                  </th>
                  <td role="cell" data-label="Score" className="paper-result-score">
                    {isFailed ? '—' : paperSummaryText(submission.markingSummary)}
                  </td>
                  <td role="cell" data-label="Percentage" className="paper-result-percentage">
                    {isFailed ? '—' : submission.markingSummary?.percentage == null ? 'Score pending' : `${Number(submission.markingSummary.percentage).toFixed(1)}%`}
                  </td>
                  <td role="cell" data-label="Outcome">
                    {isFailed ? '—' : <span className={`paper-result-badge paper-result-badge--${performance.className}`}>{performance.label}</span>}
                  </td>
                  <td role="cell" data-label="Status" className="paper-result-status">
                    <div className="paper-result-status-badges">
                      {isFailed && <span className="paper-result-badge paper-result-badge--danger"><CircleAlert size={14} aria-hidden="true" />Failed</span>}
                      {isCompleted && (
                        <>
                          <span className={`paper-result-badge paper-result-badge--${isProvisional || submission.status === 'needs_review' ? 'warning' : 'success'}`}>{isProvisional || submission.status === 'needs_review' ? <CircleAlert size={14} aria-hidden="true" /> : <CircleCheck size={14} aria-hidden="true" />}{isProvisional ? "Provisional · needs review" : submission.status === 'needs_review' ? 'Needs review' : "Completed"}</span>
                          {submission.usedFallback && <span className="paper-result-badge">OCR Fallback</span>}
                          {pdfGenerating && <span className="paper-result-badge paper-result-badge--info"><Clock3 size={14} aria-hidden="true" />PDF generating</span>}
                          {!isQuickMarkResult && pdfStatus === 'pending' && <span className="paper-result-badge paper-result-badge--info">PDF pending</span>}
                          {pdfFailed && (
                            <span 
                              className="paper-result-badge paper-result-badge--warning"
                              title={submission.pdfError || "PDF generation failed"}
                            >
                              PDF unavailable
                            </span>
                          )}
                          {submission.analysisStatus === 'failed' && <span className="paper-result-badge paper-result-badge--warning">Analysis unavailable</span>}
                        </>
                      )}
                      {!isFailed && !isCompleted && <span className="paper-result-badge paper-result-badge--info"><Clock3 size={14} aria-hidden="true" />{submission.status === 'processing' ? 'Processing' : 'Pending'}</span>}
                    </div>
                    {isFailed ? (
                      <div className="paper-result-message">
                        {submission.errorStage && <p><strong>Stage:</strong> {submission.errorStage}</p>}
                        <p>{shortErrorMessage}</p>
                      </div>
                    ) : (
                      ((isProvisional && submission.extractionMessage) || submission.analysisStatus === 'failed') && <div className="paper-result-message">
                        {submission.errorStage && <p><strong>Stage:</strong> {submission.errorStage}</p>}
                        <p>{isProvisional && submission.extractionMessage ? submission.extractionMessage : shortErrorMessage}</p>
                      </div>
                    )}
                  </td>
                  <td role="cell" data-label="Actions">
                    <div className="paper-result-actions">
                    {(submission.status === 'needs_review' || isFailed || submission.analysisStatus === 'failed') && <button className="paper-result-button" onClick={() => retrySavedPaper(submission.submissionId)}>Retry unresolved work</button>}
                    {pdfFailed && <button className="paper-result-button" onClick={() => retrySavedPaper(submission.submissionId, true)}>Retry PDF</button>}
                    {isCompleted && (
                      <>
                        <button
                          className="paper-result-button"
                          onClick={() => setSelectedBreakdownStudent(submission)}
                        >
                          View breakdown
                        </button>
                        {pdfReady && (
                          <button
                            className="paper-result-button paper-result-button--primary"
                            onClick={async () => {
                              const downloadScope=capturePaperRequest('report-download', {batchId});
                              try {
                                // Generate a meaningful filename from student name and quiz title
                                const safeName = (submission.studentName || "Student").replace(/[^a-zA-Z0-9_\- ]/g, "").replace(/\s+/g, "_").replace(/_+/g, "_").substring(0, 40);
                                const safeQuiz = (selectedQuiz?.label || "Test").replace(/[^a-zA-Z0-9_\- ]/g, "").replace(/\s+/g, "_").replace(/_+/g, "_").substring(0, 60);
                                const downloadFilename = `${safeName}_ExamScript_${safeQuiz}.pdf`;

                                // Fetch the PDF as a blob and trigger a proper download
                                const response = await paperDownload(batchId, submission.submissionId);
                                if(!paperActive(downloadScope))return;
                                if (!response.ok) throw new Error("Report download failed.");
                                const blob = await response.blob();
                                if(!paperActive(downloadScope))return;
                                const url = window.URL.createObjectURL(blob);
                                const a = document.createElement("a");
                                a.href = url;
                                a.download = downloadFilename;
                                document.body.appendChild(a);
                                a.click();
                                document.body.removeChild(a);
                                window.URL.revokeObjectURL(url);
                              } catch (err) {
                                if(!paperActive(downloadScope))return;
                                // Fallback: open the signed URL directly
                                void 0;
                                setBatchError("The report could not be downloaded. Try again to request a fresh link.");
                              }
                            }}
                          >
                            Download PDF
                          </button>
                        )}
                      </>
                    )}
                    </div>
                  </td>
                </tr>
              );
            })}
              </tbody>
            </table>
          </div>

          {selectedBreakdownStudent && renderBreakdownModal(selectedBreakdownStudent)}

        </div>
      );
    }

    // STATE 2: ACTIVE PROCESSING - Show progress
    if (isProcessing) {
      const processingStudents = getProcessingStudents();
      const total = processingStudents.length;
      const completed = processingProgress.completed;
      const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;

      return (
        <div className={`batch-step ${routeAccentClass}`}>
          <div className="step-header">
            <h2>⏳ Processing Batch</h2>
            <p>{statusMessage || "Your batch is being processed. Please wait..."}</p>
          </div>

          <div className="processing-progress">
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{ width: `${percentage}%` }}
              ></div>
            </div>
            <p className="progress-text">
              {completed} of {total} students completed ({percentage}%)
            </p>
          </div>

          <div className="progress-counts">
            <div className="count-item completed">
              <span className="count-label">Completed</span>
              <span className="count-value">{processingProgress.completed}</span>
            </div>
            <div className="count-item failed">
              <span className="count-label">Failed</span>
              <span className="count-value">{processingProgress.failed}</span>
            </div>
            <div className="count-item processing">
              <span className="count-label">Processing</span>
              <span className="count-value">{processingProgress.processing}</span>
            </div>
            <div className="count-item pending">
              <span className="count-label">Pending</span>
              <span className="count-value">{processingProgress.pending}</span>
            </div>
          </div>

          <div className="submission-list">
            <h3>Student Status</h3>
            {processingStudents.map((student) => {
              const status = submissionStatuses[student.value] || { status: "pending", error: null };
              return (
                <div key={student.value} className={`submission-item ${status.status}`}>
                  <div className="submission-info">
                    <span className="submission-name">{student.label}</span>
                    <span className={`submission-status ${status.status}`}>
                      {status.status === "pending" && "⏳ Pending"}
                      {status.status === "processing" && (
                        <span className="processing-status-inline">
                          <span className="processing-spinner"></span>
                          Processing
                        </span>
                      )}
                      {status.status === "completed" && "✅ Completed"}
                      {status.status === "failed" && "❌ Failed"}
                    </span>
                  </div>
                  {status.status === "processing" && (
                    <div className="processing-heartbeat">
                      <p className="processing-heartbeat-text">
                        Still processing — we're working through this script now.
                      </p>
                      <p className="processing-heartbeat-subtext">
                        Larger scripts can take a few minutes.
                      </p>
                    </div>
                  )}
                  {status.error && (
                    <div className="submission-error">{status.error}</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      );
    }

    return renderReviewStep();
  };

  // ===== MAIN RENDER =====
  return (
    <div className={`sd-dashboard sd-role-${shellRole} sd-brand-theme ps-shell${darkMode ? " sd-dark" : ""}`}>
      <DashboardSidebar
        role={shellRole}
        username={shellUsername}
        creditsRemaining={user?.creditsRemaining ?? null}
        teacherCode={user?.teacherCode || ""}
        onCopyCode={handleCopyTeacherCode}
        copied={copiedTeacherCode}
        onLogout={handleShellLogout}
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        activePath="/paper-submission"
        officialDashboardBrand
      />
      <div className="sd-workspace ps-workspace">
        <DashboardHeader
          username={shellUsername}
          role={shellRole}
          darkMode={darkMode}
          onToggleTheme={() => setDarkMode((value) => !value)}
          onOpenMenu={() => setSidebarOpen(true)}
          search=""
          onSearch={() => {}}
        />
        <div className="sd-main ps-main">
    <div className={`paper-submission-container${showingPaperResults ? ' paper-results-screen' : ''}`}>
      <header className="paper-submission-header ps-hero">
        <div className="paper-submission-title">
          <p className="ps-eyebrow">Paper marking</p>
          <h1>{showingPaperResults ? 'Paper results' : 'Batch Paper Submission'}</h1>
          <p>{showingPaperResults ? 'Review marks, feedback and reports for this batch.' : 'Upload scanned student papers and choose how you want them marked.'}</p>
        </div>
        {showingPaperResults && <div className="paper-results-heading-actions">
          <button className="paper-result-button paper-result-button--primary" onClick={handleRetry}>Process another batch</button>
          <button className="paper-result-button" onClick={() => navigate('/dashboard')}>Back to dashboard</button>
        </div>}
        {!isProcessing && currentStep < 5 && (
          <button
            className="back-button ps-button-secondary"
            onClick={() => navigate("/dashboard")}
          >
            <X size={16} aria-hidden="true" />
            Cancel
          </button>
        )}
      </header>

      <main className="paper-submission-main">
        <div className="step-indicator" aria-label="Paper submission progress">
          {WIZARD_STEPS.map((step) => {
            const isCompleted = currentStep > step.number;
            const isCurrent = currentStep === step.number;
            const canNavigateBack = step.number < currentStep && !isProcessing;

            return (
            <button
              key={step.number}
              type="button"
              className={`step-dot ${isCompleted ? "completed" : ""} ${
                currentStep >= step.number ? "active" : ""
              } ${isCurrent ? "current" : ""}`}
              aria-current={isCurrent ? "step" : undefined}
              aria-label={`${step.label}${isCompleted ? ", completed" : isCurrent ? ", current step" : ""}`}
              disabled={!canNavigateBack}
              onClick={() => {
                if (canNavigateBack) {
                  invalidatePaperWork();
                  setCurrentStep(step.number);
                }
              }}
            >
              <span className="step-number ps-step-tile" aria-hidden="true">
                {isCompleted ? <CircleCheck size={20} strokeWidth={2.2} /> : <step.icon size={20} strokeWidth={2} />}
              </span>
              <span className="ps-step-text">
                <span className="ps-step-kicker">Step {step.number}</span>
                <span className="step-label">{step.shortLabel}</span>
              </span>
            </button>
            );
          })}
        </div>

        {restoredOwnerRef.current === user?.uid && currentStep === 1 && renderStep1()}
        {restoredOwnerRef.current === user?.uid && currentStep === 2 && renderStep2()}
        {restoredOwnerRef.current === user?.uid && currentStep === 3 && renderStep3()}
        {restoredOwnerRef.current === user?.uid && currentStep === 4 && renderStep4()}
        {restoredOwnerRef.current === user?.uid && currentStep === 5 && renderStep5()}
      </main>
    </div>
        </div>
      </div>
    </div>
  );
};

export default PaperSubmission;
